/**
 * Workstream Z.1 — voiceStore
 *
 * Click-to-toggle model: one utterance per session.
 *   startRecording() opens WS to /api/voice/session, getUserMedia, MediaRecorder.start,
 *                    arms the silence-watchdog (5s of sub-threshold RMS auto-stops)
 *   stopRecording()  stops recorder + watchdog; sends {type: stop}; awaits transcript
 *
 * Activation: click toggles. Click idle → recording; click recording → stop.
 * (Hold-to-talk was the v0.1.0 default; switched to click-toggle on 2026-05-04
 * after live UX feedback. VAD pre-empts hung sessions where the user clicks
 * record then walks away.)
 *
 * Status transitions:
 *   idle        → recording      (after mic permission granted + WS open + config sent)
 *   recording   → transcribing   (user clicked stop OR 5s silence auto-stop)
 *   transcribing→ idle           (server sent {type: transcript})
 *   any         → error          (mic denied, WS error, server error frame)
 *
 * Error frames from the server (Rule #19, see backend routes/voice.py):
 *   {type: error, code: stt_failed | stt_timeout | stt_unreachable | no_audio | protocol_error}
 *
 * Auth: WS inherits the same-origin ForwardAuth cookie automatically (browser
 * sends cookies on the upgrade per same-origin Pivot C model).
 */
import { create } from 'zustand';
import { api, getAuthHeaders, getStoredToken, voiceAuthFrame, ApiError } from '@/services/api';
import { useChatPrefsStore } from '@/stores/chatPrefsStore';
import { getSpaContext } from '@/stores/spaContextStore';
import { usePaneStore } from '@/stores/paneStore';
import { useUIStore, type ViewMode } from '@/stores/uiStore';
// 2026-06-27 — voice IDE verbs. The voice agent's spa_action gains preview_file
// / build / deploy / undeploy by calling the SAME store actions the SPA buttons
// + Pi tools use (D1, no parallel path). deploy is STAGED (confirm-gate) so voice
// cannot publish publicly on its own — see deployStore.requestDeploy.
import { useProjectStore } from '@/stores/projectStore';
import { useBuildStore } from '@/stores/buildStore';
import { useDeployStore } from '@/stores/deployStore';
// 2026-07-04 — focus_panel 'hermes-chat' opens/activates the Hermes chat tab,
// which needs the tenant + the 'pi' harness + dock-open + a Hermes tab.
import { useTenantStore } from '@/stores/tenantStore';
import { useChatHarnessStore } from '@/stores/chatHarnessStore';
import { usePiDockStore } from '@/stores/piDockStore';
import { useChatTabsStore } from '@/stores/chatTabsStore';
// δ″ F4 — static imports (replace `await import()` cold-start round-trips).
import { VoiceAudioQueue } from '../services/voiceAudioQueue';
import { VoiceTurnSocket } from '../services/voiceTurnSocket';

// W (2026-05-16) — `connecting` state added between idle and recording for the
// WS-handshake window. Pre-fix, the SPA flipped directly to 'recording' as soon
// as getUserMedia resolved (2026-05-07 fix), but the WS handshake on the VPS
// reverse-tunnel path was observed taking up to 15 seconds in production. During
// that window the mic was captured but recorder.start() had not yet fired (lives
// inside ws.onopen per the 2026-05-06 race-fix), so any user speech was LOST and
// the user — seeing the "recording" indicator with no feedback — would often
// click again, which immediately stopped the recording after the first 250ms
// timeslice. Yellow 'connecting' orb tells the user "do not speak yet"; click is
// a no-op during connecting (handleClick), preventing the double-click hazard.
/**
 * 2026-06-27 — apply a voice spa_action verb. Single dispatcher shared by BOTH
 * the WebSocket end-of-turn path and the HTTP path (the two were byte-identical
 * inline blocks). Calls store actions directly — same pattern switch_view uses
 * for setViewMode — so there is no parallel path (D1).
 *
 * Verbs:
 *   switch_view  {view}   — dashboard|designer|terminals|knowledge|modules|deploys
 *   focus_panel  {panel}  — knowledge-ask|knowledge-ingest|voice|pi-chat|hermes-chat
 *   preview_file {path}   — point the preview pane at a source file (no build)
 *   build                 — run the SPA build (mounts the preview iframe)
 *   deploy       {slug?}  — STAGE a public deploy (confirm-gate; voice cannot
 *                           publish on its own — the human confirms the banner)
 *   undeploy              — take the public deploy down (reversible/safe)
 */
export function applyVoiceSpaAction(
  innerAction: string | undefined,
  params: Record<string, unknown>,
): void {
  const activeProjectId = (): string | undefined =>
    useProjectStore.getState().currentProject?.id;
  if (innerAction === 'switch_view') {
    const view = params.view as string | undefined;
    if (
      // Dashboard tab P1 (2026-07-28): 'dashboard' is the new leftmost tab.
      view === 'dashboard' ||
      view === 'designer' ||
      view === 'terminals' ||
      view === 'knowledge' ||
      view === 'modules' ||
      view === 'deploys'
    ) {
      useUIStore.getState().setViewMode(view as ViewMode);
    }
  } else if (innerAction === 'focus_panel') {
    const panel = params.panel as string | undefined;
    if (panel === 'hermes-chat') {
      // 2026-07-04 — open/activate the Hermes chat tab. Done at the STORE level
      // (not via the vibe:focus-panel bus) because ChatPanelPi — which owns the
      // tab strip — only mounts in the 'pi' harness. Setting harness to 'pi'
      // MOUNTS it; then ensure the dock is open + a Hermes tab is active
      // (created if none, capped at one per tenant by newTab).
      const tenantId = useTenantStore.getState().tenantId;
      if (tenantId) {
        useChatHarnessStore.getState().setHarness(tenantId, 'pi');
        usePiDockStore.getState().setOpen(true);
        const tabsState = useChatTabsStore.getState();
        const target = tabsState.tabs.find((t) => t.kind === 'hermes') ?? tabsState.newTab(tenantId, 'hermes');
        if (target) useChatTabsStore.getState().setActiveTab(tenantId, target.id);
      }
    } else if (
      panel === 'knowledge-ask' ||
      panel === 'knowledge-ingest' ||
      panel === 'voice' ||
      panel === 'pi-chat'
    ) {
      window.dispatchEvent(new CustomEvent('vibe:focus-panel', { detail: { panel } }));
    }
  } else if (innerAction === 'preview_file') {
    const path = params.path as string | undefined;
    const pid = activeProjectId();
    if (path && pid) {
      useBuildStore.getState().previewFile(pid, path.replace(/^\/+/, ''));
    }
  } else if (innerAction === 'build') {
    const pid = activeProjectId();
    if (pid) {
      void useBuildStore
        .getState()
        .startBuild(pid)
        .then(() => useBuildStore.getState().pollBuildStatus(pid));
    }
  } else if (innerAction === 'deploy') {
    // STAGED only — the confirm banner (TopBar) is the only path that publishes.
    const pid = activeProjectId();
    if (pid) {
      const slug = typeof params.slug === 'string' ? params.slug : undefined;
      useDeployStore.getState().requestDeploy(pid, slug);
    }
  } else if (innerAction === 'undeploy') {
    const pid = activeProjectId();
    if (pid) void useDeployStore.getState().undeployProject(pid);
  } else if (innerAction === 'module_action') {
    // 2026-07-01 — INTERACT with the active module on command (change tab/theme,
    // refresh, open external). Same window-event bus focus_panel uses; the
    // active module (e.g. GHL App) listens for 'vibe:module-action' and applies
    // verbs it supports, ignoring the rest. Benign local UI — no server writes.
    // P3 tiling: an optional module_id targets a specific tile (untargeted
    // verbs go to the FOCUSED tile via the shouldHandleModuleAction guard).
    const moduleVerb = typeof params.action === 'string' ? params.action : undefined;
    if (moduleVerb) {
      const detailParams =
        params.params && typeof params.params === 'object'
          ? (params.params as Record<string, unknown>)
          : {};
      const moduleId =
        typeof params.module_id === 'string' && params.module_id
          ? params.module_id
          : typeof detailParams.module_id === 'string' && detailParams.module_id
            ? (detailParams.module_id as string)
            : undefined;
      window.dispatchEvent(
        new CustomEvent('vibe:module-action', {
          detail: { action: moduleVerb, params: detailParams, module_id: moduleId },
        }),
      );
    }
  }
}

export type VoiceStatus = 'idle' | 'connecting' | 'recording' | 'transcribing' | 'speaking' | 'error';

/** F1 (was Z.1.5) — qwen3-tts preset voices fallback list. Live source is
 * GET /api/voice/voices (proxies qwen3-tts /v1/models). Hardcoded list is
 * the offline/network-error fallback so the SPA still works when qwen3-tts
 * is down. F1 fetches the live list on first mount + replaces this. */
export const VOICE_PRESETS = [
  { id: 'ryan',     gender: 'male',   description: 'Professional male (English)' },
  { id: 'serena',   gender: 'female', description: 'Clear female (English)' },
  { id: 'vivian',   gender: 'female', description: 'Warm female (English)' },
  { id: 'aiden',    gender: 'male',   description: 'Youthful male' },
  { id: 'dylan',    gender: 'male',   description: 'Deep male' },
  { id: 'eric',     gender: 'male',   description: 'Energetic male' },
  { id: 'ono_anna', gender: 'female', description: 'Japanese female' },
  { id: 'sohee',    gender: 'female', description: 'Korean female' },
  { id: 'uncle_fu', gender: 'male',   description: 'Chinese male' },
] as const;
export type VoicePresetId = string;
export interface VoicePreset {
  id: string;
  gender?: string;
  description?: string;
}

const VOICE_PREF_KEY = 'vibe_voice_preset';
// 2026-05-07: was 'ryan' (qwen3-tts preset). Switched TTS backend to Kokoro
// which uses different voice IDs ('af_heart', 'am_adam', etc.). Stored
// 'ryan' values from old localStorage are caught by loadVoices() validation
// below — when settings.voice isn't in the live voices list, we reset to
// this default. af_heart is the canonical "warm female English" voice
// per the Kokoro model card.
const DEFAULT_VOICE: VoicePresetId = 'af_heart';

export function getStoredVoice(): VoicePresetId {
  try {
    const stored = localStorage.getItem(VOICE_PREF_KEY) as VoicePresetId | null;
    if (stored && VOICE_PRESETS.some((v) => v.id === stored)) return stored;
  } catch { /* localStorage disabled */ }
  return DEFAULT_VOICE;
}

export function setStoredVoice(voice: VoicePresetId): void {
  try {
    localStorage.setItem(VOICE_PREF_KEY, voice);
  } catch { /* localStorage disabled — non-fatal */ }
}

/** 2026-05-07 evening — multi-engine TTS metadata (per
 * docs/20260507/20260507_PLAN_MULTI_TTS_ENGINE_ROUTER_FOR_VIBE_DESIGNER.md).
 * Backend `/api/voice/engines` returns the registered engines; SPA stores
 * the user's selection in settings.engine; voice list reloads on change. */
export interface EngineSpec {
  id: string;          // currently 'kokoro' | 'qwen-fast' (vibevoice disabled in backend registry 2026-05-07)
  label: string;       // human-readable name shown in dropdown
  default_voice: string;
}
export const DEFAULT_ENGINE_ID = 'kokoro';

/** 2026-06-28 — one selectable LLM model for the voice model picker. Sourced
 * from the tenant LLM router via /api/chat-proxy/v1/models (the same endpoint
 * Pi Chat's selector uses). `id` is the router model id (e.g.
 * "qwopus36-27b-abliterated-vision-nvfp4@node2"); `label` is the display name. */
export interface VoiceModelOption {
  id: string;
  label: string;
}

/** Z.4 — additional voice settings persisted alongside the voice preset.
 * Speed clamped to 0.5–2.0 range (Kokoro accepts; other engines may ignore).
 * Language is the whisperx STT autodetect override. Activation: push-to-talk
 * vs armed VAD (worldview §3.1). 2026-05-07: engine ID added per multi-TTS
 * plan; defaults to 'kokoro'. */
export interface VoiceSettings {
  engine: string;
  voice: VoicePresetId;
  speed: number;
  language: string;
  activation: 'click-toggle' | 'push-to-talk';
  /** δ′.2 (2026-05-17) — voice mode dispatch:
   *   'classic'   = legacy 3-hop (startRecording / WS /api/voice/session)
   *   'streaming' = new pipeline (startTurn / WS /api/voice/turn)
   * Default 'classic'. Streaming is opt-in via voice settings panel.
   * Existing users with no `voiceMode` in localStorage default to 'classic'
   * via the per-field guard in getStoredSettings(). */
  voiceMode: 'classic' | 'streaming';
  /** δ″.2 (2026-05-17) — streaming-mode auto-stop silence threshold in seconds.
   * After this many seconds of sub-RMS-threshold audio, the streaming turn
   * auto-stops (calls stopTurn()) so conversational flow doesn't require a
   * second click. Set to 0 to DISABLE auto-stop (manual click-to-stop only).
   * Range: 0..10. Default 1.5s (classic-mode uses 5s; streaming defaults
   * tighter for conversational latency). Classic mode ignores this — it
   * has its own hardcoded SILENCE_TIMEOUT_MS. */
  streamingSilenceSec: number;
  /** 1.160.0 — optional initial greeting on page load. */
  initialGreetingEnabled: boolean;
  initialGreetingTiming: 'before-interaction' | 'after-interaction';
  initialGreetingText: string;
  initialGreetingAutoArmMic: boolean;
  /** 1.161.0 — conversation memory cap. Number of prior turns to include in
   * the LLM context window on each dispatch. 0 = stateless (legacy). Capped
   * upper bound by HISTORY_MAX_TURNS=50. Default 20 (≈ 10 user/bridge pairs). */
  dispatcherHistoryTurns: number;
  /** 1.161.0 — customizable voice-agent identity. Threaded into the system
   * prompt ("You are {name}, a voice agent for the vibe-designer IDE.") AND
   * used as the chat-log label (all-caps mirror of existing "USER"/"BRIDGE"). */
  voiceAgentName: string;
  /** 1.162.2 — voice→Pi chat direct-route prefix. When a transcript starts
   * with this phrase + separator ([,:.!?\s]), the SPA forwards the
   * stripped remainder directly to the active Pi chat tab via the
   * vibe:send-to-pi-chat CustomEvent, bypassing the LLM entirely.
   * Empty string disables the feature. Default "pi chat" (2-word phrase
   * chosen for low false-positive surface). */
  piPrefixPhrase: string;
  /** 1.162.2 — how many recent Pi chat user/assistant messages to inject
   * as context into the voice LLM's system prompt. 0 disables. Streaming
   * mode does NOT yet support this (deferred per v2.3 Δ12); HTTP/classic
   * path only. Default 10. */
  piChatHistoryTurns: number;
  /** 1.162.3 — voice trigger phrase to forward already-said content to
   * Pi chat. When transcript contains this phrase (case-insensitive), the
   * SPA forwards the configured `piForwardScope` to the active Pi tab
   * and SKIPS the LLM dispatch for that turn. Empty disables. Default
   * "send that to pi". Different from `piPrefixPhrase` which prefixes a
   * NEW delegation; this one forwards EXISTING transcript content. */
  piForwardTriggerPhrase: string;
  /** 1.162.3 — what gets forwarded when piForwardTriggerPhrase matches
   * OR when the user clicks the "→ Pi" button next to a BRIDGE turn.
   * 'latest-bridge' = just the most recent bridge reply (default).
   * 'latest-pair'    = the most recent user turn + bridge reply.
   * 'whole-transcript' = the entire current voice conversation. */
  piForwardScope: 'latest-bridge' | 'latest-pair' | 'whole-transcript';
  /** 1.164.0 (WS-A.5) — per-tab "behavior when this tab loses focus" toggles.
   * Default false matches user directive: tabs are independent audio surfaces;
   * switching away preserves each tab's audio/recorder/status by default. */
  pauseAudioOnLoseFocus: boolean;
  stopRecorderOnLoseFocus: boolean;
  resetStatusOnLoseFocus: boolean;
  /** W3 (2026-05-25) — D-W3-4 LOCKED. In-app voice volume independent of OS
   * system volume. Range [0, 1.5] (0=mute, 1.0=native default, 1.5=+50%
   * boost ceiling). Applied via GainNode in VoiceAudioQueue. Real-time
   * effect on the in-flight turn via activeTurnAudioQueue.setVolume(). */
  audioVolume: number;
  /** 2026-06-28 — per-voice-tab LLM model picker (mirrors Pi Chat's model
   * selector). The id of a tenant-router model (e.g.
   * "qwopus36-27b-abliterated-vision-nvfp4@node2"), threaded into the
   * /api/voice/dispatch body + WS turn config so the backend dispatcher uses
   * it verbatim. Sentinel 'auto' (the default) = "let the router/dispatcher
   * decide" → backend falls back to _resolve_active_model(), i.e. behavior is
   * identical to pre-picker. Persists per-tab like every other VoiceSetting. */
  model: string;
  /** 1.421.0 — unrestricted-conversation toggle (operator request 2026-07-03).
   * Threads as `unrestricted_content` into the /voice/dispatch body + WS turn
   * config; when true the backend appends the OPEN CONVERSATION block to the
   * dispatcher system prompt (no restricted topics — for abliterated models).
   * Deliberately NO model detection and NO filter. Default false. */
  unrestrictedContent: boolean;
}

const SETTINGS_KEY = 'vibe_voice_settings';
export const DEFAULT_SETTINGS: VoiceSettings = {
  engine: DEFAULT_ENGINE_ID,
  voice: DEFAULT_VOICE,
  speed: 1.0,
  language: 'auto',
  activation: 'click-toggle',
  voiceMode: 'classic',
  // δ″.3 — bumped 1.5 → 2.5 after live-test feedback that 1.5s was too aggressive
  // mid-sentence pauses kept tripping auto-stop before the user finished speaking.
  streamingSilenceSec: 2.5,
  // 1.160.0 — initial greeting defaults. after-interaction is iOS-safe; the
  // before-interaction option fires before any user gesture and silently fails
  // on iOS Safari (autoplay policy), so the UI labels it "desktop only".
  initialGreetingEnabled: true,
  initialGreetingTiming: 'after-interaction',
  initialGreetingText: "Hi — I'm ready. What are we working on today?",
  initialGreetingAutoArmMic: false,
  // 1.161.0 — conversation memory + customizable agent name.
  dispatcherHistoryTurns: 20,
  voiceAgentName: 'Bridge',
  // 1.162.2 — voice→Pi chat prefix route + Pi history injection.
  piPrefixPhrase: 'pi chat',
  piChatHistoryTurns: 10,
  // 1.162.3 — voice trigger phrase to forward existing content + scope.
  piForwardTriggerPhrase: 'send that to pi',
  piForwardScope: 'latest-bridge',
  // 1.164.0 (WS-A.5) — per-tab teardown discipline, all default false.
  pauseAudioOnLoseFocus: false,
  stopRecorderOnLoseFocus: false,
  resetStatusOnLoseFocus: false,
  // W3 (2026-05-25) — D-W3-4 LOCKED. Native default; user can drag to mute
  // (0) or boost (up to 1.5) via VoiceSettingsModal slider.
  audioVolume: 1.0,
  // 2026-06-28 — voice model picker default. 'auto' = unchanged behavior
  // (backend _resolve_active_model()).
  model: 'auto',
  // 1.421.0 — unrestricted conversation OFF by default; strictly opt-in.
  unrestrictedContent: false,
};

export function getStoredSettings(): VoiceSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS, voice: getStoredVoice() };
    const parsed = JSON.parse(raw) as Partial<VoiceSettings>;
    return {
      // 2026-05-07: engine field added. Persisted values from older clients
      // (no engine field) fall back to DEFAULT_ENGINE_ID = 'kokoro'.
      engine: typeof parsed.engine === 'string' && parsed.engine.length > 0
        ? parsed.engine
        : DEFAULT_SETTINGS.engine,
      voice: VOICE_PRESETS.some((v) => v.id === parsed.voice)
        ? (parsed.voice as VoicePresetId)
        : DEFAULT_SETTINGS.voice,
      speed: typeof parsed.speed === 'number' && parsed.speed >= 0.5 && parsed.speed <= 2.0
        ? parsed.speed
        : DEFAULT_SETTINGS.speed,
      language: typeof parsed.language === 'string' ? parsed.language : DEFAULT_SETTINGS.language,
      activation: parsed.activation === 'push-to-talk' || parsed.activation === 'click-toggle'
        ? parsed.activation
        : DEFAULT_SETTINGS.activation,
      // δ′.2 — voiceMode missing in older localStorage payloads → default 'classic'.
      voiceMode: parsed.voiceMode === 'streaming' || parsed.voiceMode === 'classic'
        ? parsed.voiceMode
        : DEFAULT_SETTINGS.voiceMode,
      // δ″.2 — streamingSilenceSec migration-safe per-field guard.
      // Range clamp [0, 10]; older payloads / non-numeric → default 1.5s.
      streamingSilenceSec: typeof parsed.streamingSilenceSec === 'number'
        && parsed.streamingSilenceSec >= 0
        && parsed.streamingSilenceSec <= 10
        ? parsed.streamingSilenceSec
        : DEFAULT_SETTINGS.streamingSilenceSec,
      // 1.160.0 — initial-greeting per-field migration guards.
      initialGreetingEnabled: typeof parsed.initialGreetingEnabled === 'boolean'
        ? parsed.initialGreetingEnabled
        : DEFAULT_SETTINGS.initialGreetingEnabled,
      initialGreetingTiming: parsed.initialGreetingTiming === 'before-interaction'
        || parsed.initialGreetingTiming === 'after-interaction'
        ? parsed.initialGreetingTiming
        : DEFAULT_SETTINGS.initialGreetingTiming,
      initialGreetingText: typeof parsed.initialGreetingText === 'string'
        ? parsed.initialGreetingText
        : DEFAULT_SETTINGS.initialGreetingText,
      initialGreetingAutoArmMic: typeof parsed.initialGreetingAutoArmMic === 'boolean'
        ? parsed.initialGreetingAutoArmMic
        : DEFAULT_SETTINGS.initialGreetingAutoArmMic,
      // 1.161.0 — dispatcherHistoryTurns migration: range [0, 50]; non-numeric
      // / out-of-range → default 20.
      dispatcherHistoryTurns: typeof parsed.dispatcherHistoryTurns === 'number'
        && parsed.dispatcherHistoryTurns >= 0
        && parsed.dispatcherHistoryTurns <= 50
        ? parsed.dispatcherHistoryTurns
        : DEFAULT_SETTINGS.dispatcherHistoryTurns,
      // 1.161.0 — voiceAgentName migration: non-string → default "Bridge".
      voiceAgentName: typeof parsed.voiceAgentName === 'string'
        && parsed.voiceAgentName.length > 0
        ? parsed.voiceAgentName
        : DEFAULT_SETTINGS.voiceAgentName,
      // 1.162.2 — piPrefixPhrase migration: empty string allowed (disables);
      // non-string → default "pi chat". Per R2 Δ13 — do NOT require length>0.
      piPrefixPhrase: typeof parsed.piPrefixPhrase === 'string'
        ? parsed.piPrefixPhrase
        : DEFAULT_SETTINGS.piPrefixPhrase,
      // 1.162.2 — piChatHistoryTurns migration: range [0, 20]; non-numeric /
      // out-of-range → default 10.
      piChatHistoryTurns: typeof parsed.piChatHistoryTurns === 'number'
        && parsed.piChatHistoryTurns >= 0
        && parsed.piChatHistoryTurns <= 20
        ? parsed.piChatHistoryTurns
        : DEFAULT_SETTINGS.piChatHistoryTurns,
      // 1.162.3 — piForwardTriggerPhrase migration: empty string allowed.
      piForwardTriggerPhrase: typeof parsed.piForwardTriggerPhrase === 'string'
        ? parsed.piForwardTriggerPhrase
        : DEFAULT_SETTINGS.piForwardTriggerPhrase,
      // 1.162.3 — piForwardScope migration: only 3 valid values.
      piForwardScope: parsed.piForwardScope === 'latest-bridge'
        || parsed.piForwardScope === 'latest-pair'
        || parsed.piForwardScope === 'whole-transcript'
        ? parsed.piForwardScope
        : DEFAULT_SETTINGS.piForwardScope,
      // 1.164.0 (WS-A.5) — per-tab lose-focus toggle migration. Missing keys
      // in pre-1.164.0 blobs fall back to defaults (all false).
      pauseAudioOnLoseFocus: typeof parsed.pauseAudioOnLoseFocus === 'boolean'
        ? parsed.pauseAudioOnLoseFocus
        : DEFAULT_SETTINGS.pauseAudioOnLoseFocus,
      stopRecorderOnLoseFocus: typeof parsed.stopRecorderOnLoseFocus === 'boolean'
        ? parsed.stopRecorderOnLoseFocus
        : DEFAULT_SETTINGS.stopRecorderOnLoseFocus,
      resetStatusOnLoseFocus: typeof parsed.resetStatusOnLoseFocus === 'boolean'
        ? parsed.resetStatusOnLoseFocus
        : DEFAULT_SETTINGS.resetStatusOnLoseFocus,
      // W3 (2026-05-25) — D-W3-7 LOCKED. Per-field guard for audioVolume.
      // Pre-W3 payloads (no field) → 1.0. Out-of-range numeric values
      // clamped to [0, 1.5]. Non-number / NaN → 1.0.
      audioVolume: typeof parsed.audioVolume === 'number'
        && Number.isFinite(parsed.audioVolume)
        ? Math.max(0, Math.min(1.5, parsed.audioVolume))
        : DEFAULT_SETTINGS.audioVolume,
      // 2026-06-28 — model picker migration: pre-picker payloads (no field) /
      // non-string → 'auto' (the unchanged default).
      model: typeof parsed.model === 'string' && parsed.model.length > 0
        ? parsed.model
        : DEFAULT_SETTINGS.model,
      // 1.421.0 — pre-toggle payloads (no field) → false (unchanged default).
      unrestrictedContent: typeof parsed.unrestrictedContent === 'boolean'
        ? parsed.unrestrictedContent
        : DEFAULT_SETTINGS.unrestrictedContent,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function setStoredSettings(settings: VoiceSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    // Keep VOICE_PREF_KEY in sync for backwards-compat with Z.1.5.
    setStoredVoice(settings.voice);
  } catch { /* non-fatal */ }
}

/** WS-A Φ2 (2026-05-18) — per-tenant per-tab schema v2 storage.
 * Key shape: `vibe_voice_settings_v2_${tenantId}` → JSON `{[tabId]: VoiceSettings, ...}`.
 * Legacy `SETTINGS_KEY` is migrated into the first tab id on first hydrate
 * (D-A2). Legacy key is NOT deleted on hydrate — only deleted on the FIRST
 * v2 write per tenant (`_legacyDeletePending[tenantId]` flag). */
const SETTINGS_KEY_V2_PREFIX = 'vibe_voice_settings_v2_';

/** Per-tenant flag: TRUE iff the legacy SETTINGS_KEY needs deletion on the
 * NEXT successful setStoredSettingsV2 call for this tenant. Set by hydrate
 * (when a legacy payload was migrated); cleared by setStoredSettingsV2. */
const _legacyDeletePending: Record<string, boolean> = {};

/** WS-A Φ2 (reviewer C-3 fix) — module-level current tenantId, set by
 * `hydrate(tenantId)` and read by `updateSettings()` so per-tab settings
 * persist to v2 storage on every write — not just on the first post-migration
 * write. Null pre-hydrate (legacy fast path uses `setStoredSettings` only). */
let _activeTenantId: string | null = null;

function storageKeyV2For(tenantId: string): string {
  return `${SETTINGS_KEY_V2_PREFIX}${tenantId}`;
}

/** Read the per-tenant v2 payload. Returns null when key absent or unparseable.
 * Returned object is `{[tabId]: VoiceSettings}` shape per D-A2. */
export function getStoredSettingsV2(tenantId: string): Record<TabId, VoiceSettings> | null {
  try {
    const raw = localStorage.getItem(storageKeyV2For(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    // Coerce each entry through the per-field migration guards.
    const out: Record<TabId, VoiceSettings> = {};
    for (const [tabId, val] of Object.entries(parsed as Record<string, unknown>)) {
      out[tabId] = coerceSettings(val);
    }
    return out;
  } catch {
    return null;
  }
}

/** Write the per-tenant v2 payload + delete legacy key if marked pending.
 * Also mirrors the active tab's voice to VOICE_PREF_KEY per D-A9. */
export function setStoredSettingsV2(
  tenantId: string,
  byTabIdSettings: Record<TabId, VoiceSettings>,
  activeTabId: TabId | null,
): void {
  try {
    localStorage.setItem(storageKeyV2For(tenantId), JSON.stringify(byTabIdSettings));
    if (_legacyDeletePending[tenantId]) {
      try { localStorage.removeItem(SETTINGS_KEY); } catch { /* non-fatal */ }
      _legacyDeletePending[tenantId] = false;
    }
    // D-A9 — mirror active tab's voice for back-compat with anything reading
    // VOICE_PREF_KEY directly.
    if (activeTabId && byTabIdSettings[activeTabId]) {
      setStoredVoice(byTabIdSettings[activeTabId].voice);
    }
  } catch { /* non-fatal */ }
}

/** 1.167.0 — persistent per-tab voice chat history.
 *
 * Key shape: `vibe_voice_history_v2_${tenantId}` → JSON `{[tabId]: VoiceTurn[]}`.
 * Capped per tab to HISTORY_MAX_TURNS on write (matches in-memory cap).
 * Pre-1.167.0, D-A5 had voice transcripts in-memory only; user directive
 * 2026-05-20 reversed that. Privacy posture: plaintext on disk, same as
 * settings; per-tab clear button + tab-close + new-tab provide user escape
 * hatches. No new UI surface.
 */
const HISTORY_KEY_V2_PREFIX = 'vibe_voice_history_v2_';

function historyKeyV2For(tenantId: string): string {
  return `${HISTORY_KEY_V2_PREFIX}${tenantId}`;
}

export function getStoredHistoryV2(tenantId: string): Record<TabId, VoiceTurn[]> {
  try {
    const raw = localStorage.getItem(historyKeyV2For(tenantId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<TabId, VoiceTurn[]> = {};
    for (const [tabId, turns] of Object.entries(parsed as Record<string, unknown>)) {
      if (!Array.isArray(turns)) continue;
      const coerced: VoiceTurn[] = [];
      for (const t of turns) {
        if (!t || typeof t !== 'object') continue;
        const o = t as Partial<VoiceTurn>;
        if (typeof o.text !== 'string' || typeof o.timestamp !== 'number') continue;
        if (o.role === 'user' || o.role === 'bridge') {
          coerced.push({ role: o.role, text: o.text, timestamp: o.timestamp });
          continue;
        }
        // Phase 9.8 (R2 C-1 fix): hydrate role:'tool' rows from localStorage.
        // Pre-fix the deserializer dropped all tool rows silently — chips
        // would disappear on page reload despite the write path persisting
        // them. Per D-9.8-1 LOCKED, schema is additive + optional; tool field
        // hydrates as long as it carries `name` (the only required sub-field).
        if (o.role === 'tool' && o.tool && typeof o.tool === 'object'
            && typeof (o.tool as { name?: unknown }).name === 'string') {
          const tp = o.tool as {
            name: string;
            args_summary?: unknown;
            result_summary?: unknown;
            error?: unknown;
            duration_ms?: unknown;
            step?: unknown;
          };
          coerced.push({
            role: 'tool',
            text: o.text,
            timestamp: o.timestamp,
            tool: {
              name: tp.name,
              args_summary: typeof tp.args_summary === 'string' ? tp.args_summary : undefined,
              result_summary: typeof tp.result_summary === 'string' ? tp.result_summary : undefined,
              error: typeof tp.error === 'string' ? tp.error : null,
              duration_ms: typeof tp.duration_ms === 'number' ? tp.duration_ms : null,
              step: typeof tp.step === 'number' ? tp.step : undefined,
            },
          });
        }
      }
      if (coerced.length > 0) out[tabId] = coerced.slice(-HISTORY_MAX_TURNS);
    }
    return out;
  } catch {
    return {};
  }
}

export function setStoredHistoryV2(
  tenantId: string,
  byTabIdHistory: Record<TabId, VoiceTurn[]>,
): void {
  try {
    // Build cap-trimmed payload. Drop tabs with empty history so the
    // persisted map stays compact and orphan cleanup has a stable target.
    const payload: Record<TabId, VoiceTurn[]> = {};
    for (const [tabId, turns] of Object.entries(byTabIdHistory)) {
      if (!Array.isArray(turns) || turns.length === 0) continue;
      payload[tabId] = turns.slice(-HISTORY_MAX_TURNS);
    }
    const key = historyKeyV2For(tenantId);
    if (Object.keys(payload).length === 0) {
      // Don't leave an empty-object stub on disk.
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(payload));
  } catch { /* non-fatal */ }
}

/** Coerce arbitrary parsed value through the per-field migration guards.
 * Mirrors getStoredSettings()'s shape but on an arbitrary input. */
function coerceSettings(parsed: unknown): VoiceSettings {
  const p = (parsed && typeof parsed === 'object' ? parsed : {}) as Partial<VoiceSettings>;
  return {
    engine: typeof p.engine === 'string' && p.engine.length > 0
      ? p.engine
      : DEFAULT_SETTINGS.engine,
    voice: typeof p.voice === 'string' && p.voice.length > 0
      ? p.voice
      : DEFAULT_SETTINGS.voice,
    speed: typeof p.speed === 'number' && p.speed >= 0.5 && p.speed <= 2.0
      ? p.speed
      : DEFAULT_SETTINGS.speed,
    language: typeof p.language === 'string' ? p.language : DEFAULT_SETTINGS.language,
    activation: p.activation === 'push-to-talk' || p.activation === 'click-toggle'
      ? p.activation
      : DEFAULT_SETTINGS.activation,
    voiceMode: p.voiceMode === 'streaming' || p.voiceMode === 'classic'
      ? p.voiceMode
      : DEFAULT_SETTINGS.voiceMode,
    streamingSilenceSec: typeof p.streamingSilenceSec === 'number'
      && p.streamingSilenceSec >= 0 && p.streamingSilenceSec <= 10
      ? p.streamingSilenceSec
      : DEFAULT_SETTINGS.streamingSilenceSec,
    initialGreetingEnabled: typeof p.initialGreetingEnabled === 'boolean'
      ? p.initialGreetingEnabled
      : DEFAULT_SETTINGS.initialGreetingEnabled,
    initialGreetingTiming: p.initialGreetingTiming === 'before-interaction'
      || p.initialGreetingTiming === 'after-interaction'
      ? p.initialGreetingTiming
      : DEFAULT_SETTINGS.initialGreetingTiming,
    initialGreetingText: typeof p.initialGreetingText === 'string'
      ? p.initialGreetingText
      : DEFAULT_SETTINGS.initialGreetingText,
    initialGreetingAutoArmMic: typeof p.initialGreetingAutoArmMic === 'boolean'
      ? p.initialGreetingAutoArmMic
      : DEFAULT_SETTINGS.initialGreetingAutoArmMic,
    dispatcherHistoryTurns: typeof p.dispatcherHistoryTurns === 'number'
      && p.dispatcherHistoryTurns >= 0 && p.dispatcherHistoryTurns <= 50
      ? p.dispatcherHistoryTurns
      : DEFAULT_SETTINGS.dispatcherHistoryTurns,
    voiceAgentName: typeof p.voiceAgentName === 'string' && p.voiceAgentName.length > 0
      ? p.voiceAgentName
      : DEFAULT_SETTINGS.voiceAgentName,
    piPrefixPhrase: typeof p.piPrefixPhrase === 'string'
      ? p.piPrefixPhrase
      : DEFAULT_SETTINGS.piPrefixPhrase,
    piChatHistoryTurns: typeof p.piChatHistoryTurns === 'number'
      && p.piChatHistoryTurns >= 0 && p.piChatHistoryTurns <= 20
      ? p.piChatHistoryTurns
      : DEFAULT_SETTINGS.piChatHistoryTurns,
    piForwardTriggerPhrase: typeof p.piForwardTriggerPhrase === 'string'
      ? p.piForwardTriggerPhrase
      : DEFAULT_SETTINGS.piForwardTriggerPhrase,
    piForwardScope: p.piForwardScope === 'latest-bridge'
      || p.piForwardScope === 'latest-pair'
      || p.piForwardScope === 'whole-transcript'
      ? p.piForwardScope
      : DEFAULT_SETTINGS.piForwardScope,
    // WS-A.5 — per-tab lose-focus toggles. Missing keys in pre-1.164.0 blobs
    // fall back to defaults (all false) via the typeof boolean guard.
    pauseAudioOnLoseFocus: typeof p.pauseAudioOnLoseFocus === 'boolean'
      ? p.pauseAudioOnLoseFocus
      : DEFAULT_SETTINGS.pauseAudioOnLoseFocus,
    stopRecorderOnLoseFocus: typeof p.stopRecorderOnLoseFocus === 'boolean'
      ? p.stopRecorderOnLoseFocus
      : DEFAULT_SETTINGS.stopRecorderOnLoseFocus,
    resetStatusOnLoseFocus: typeof p.resetStatusOnLoseFocus === 'boolean'
      ? p.resetStatusOnLoseFocus
      : DEFAULT_SETTINGS.resetStatusOnLoseFocus,
    // W3 (2026-05-25) — D-W3-7 LOCKED. Mirrors getStoredSettings's per-field
    // guard for per-tab persist hydration path.
    audioVolume: typeof p.audioVolume === 'number' && Number.isFinite(p.audioVolume)
      ? Math.max(0, Math.min(1.5, p.audioVolume))
      : DEFAULT_SETTINGS.audioVolume,
    // 2026-06-28 — model picker per-tab hydration guard (mirrors getStoredSettings).
    model: typeof p.model === 'string' && p.model.length > 0
      ? p.model
      : DEFAULT_SETTINGS.model,
    // 1.421.0 — unrestricted-conversation per-tab hydration guard.
    unrestrictedContent: typeof p.unrestrictedContent === 'boolean'
      ? p.unrestrictedContent
      : DEFAULT_SETTINGS.unrestrictedContent,
  };
}

/** WS-A Φ2 — fresh tab state with provided settings + reset state machine. */
function freshTabState(settings: VoiceSettings = { ...DEFAULT_SETTINGS }): VoiceTabState {
  return {
    settings,
    history: [],
    transcript: '',
    status: 'idle',
    error: null,
    lastDispatch: null,
    currentTurnId: null,
    currentTurnStart: null,
    lastTurnTiming: null,
    currentToolProgress: null,  // Phase 9.3
    streamingReplyText: '',     // Phase 9.6 D-9.6-3 transient overlay
  };
}

// 1.164.2 — pre-populate greeting text in each tab's history at hydrate time
// so every tab is "hot-loaded": the user sees the bridge greeting in the
// transcript area the instant the page loads, with NO need to click the mic
// first. The greeting AUDIO still fires only on first activation per tab
// (handled by fireInitialGreetingIfReady's _greetingFiredByTab Set), so audio
// respects the "speak only when activated" rule.
//
// Architecture: this splits text-vs-audio concerns that were conflated in
// fireInitialGreetingIfReady. Text is deterministic + idempotent — fits
// hydrate. Audio is stateful + gated — stays in fireInitialGreetingIfReady.
//
// Idempotent: if the tab's history already STARTS with the configured greeting
// (text + role === 'bridge'), skip. Repeated hydrate calls (e.g., the
// cold-start double-hydrate path at VoicePanel.tsx:597 + :612) do not
// double-append. Append (not prepend) matches _appendTabHistory at line ~505.
//
// Limitation (accepted per plan §9): if a user changes initialGreetingText
// mid-session AND the page is later force-re-hydrated, the idempotency gate
// (text-match against history[0].text) misses the old greeting and appends a
// second new-text greeting. Mitigation: re-hydrate is rare; cold-start window
// is millisecond-scale before the user can change settings.
function populateGreetingHistory(
  byTabId: Record<TabId, VoiceTabState>,
): Record<TabId, VoiceTabState> {
  const out: Record<TabId, VoiceTabState> = {};
  for (const [tabId, tab] of Object.entries(byTabId)) {
    const greetingEnabled = tab.settings.initialGreetingEnabled;
    const greetingText = (tab.settings.initialGreetingText || '').trim();
    if (!greetingEnabled || greetingText.length === 0) {
      out[tabId] = tab;
      continue;
    }
    // 1.167.0 — widened skip: any non-empty history skips greeting append.
    // Pre-1.167.0 only skipped when history[0] was the greeting (idempotent
    // re-hydrate). With persistent history, a returning user's first item
    // may be a user turn, and the old guard would have appended the greeting
    // to the END of an existing conversation (L509 appends, not prepends),
    // which is the wrong UX. New rule: greeting only seeds genuinely empty
    // tabs. Cold-start (empty history) → greeting seeded. Returning user
    // (history restored from localStorage) → no greeting reseed.
    if (tab.history.length > 0) {
      out[tabId] = tab;
      continue;
    }
    const greetingTurn: VoiceTurn = {
      role: 'bridge',
      text: greetingText,
      timestamp: Date.now(),
    };
    out[tabId] = { ...tab, history: [...tab.history, greetingTurn] };
  }
  return out;
}

/** WS-A Φ2 — apply partial VoiceTabState update to a specific tab via setState.
 * When tabId === activeTabId, ALSO mirrors the patch to the top-level shim
 * fields (status/transcript/error/lastDispatch/history/settings/currentTurnId/
 * currentTurnStart/lastTurnTiming) per hybrid-mirror design. New consumers
 * read byTabId; legacy consumers (VoicePanel, SettingsMenu, 5 test suites)
 * read top level. */
function _setTabFields(tabId: TabId, patch: Partial<VoiceTabState>): void {
  useVoiceStore.setState((s) => {
    const tab = s.byTabId[tabId];
    if (!tab) return s;
    const nextTab = { ...tab, ...patch };
    const next: Partial<VoiceState> = {
      byTabId: { ...s.byTabId, [tabId]: nextTab },
    };
    if (tabId === s.activeTabId) {
      // Mirror per-tab fields to top level so legacy consumers see updates.
      Object.assign(next, patch);
    }
    return next as Partial<VoiceState>;
  });
}

/** WS-A Φ2 — convenience: apply update to active tab; no-op when no active tab.
 * Mirrors to top level via _setTabFields. */
function _setActiveTabFields(patch: Partial<VoiceTabState>): void {
  const tabId = useVoiceStore.getState().activeTabId;
  if (!tabId) {
    // Pre-hydrate: no active tab. Mirror to top level only (legacy fast path
    // for tests / components that never call hydrate). Avoids dropping writes
    // on the floor when byTabId is empty.
    useVoiceStore.setState(patch as Partial<VoiceState>);
    return;
  }
  _setTabFields(tabId, patch);
}

/** WS-A Φ2 — append history turn to a specific tab with cap. Mirrors to top
 * level when tabId === activeTabId. */
function _appendTabHistory(tabId: TabId, turn: VoiceTurn): void {
  useVoiceStore.setState((s) => {
    const tab = s.byTabId[tabId];
    if (!tab) return s;
    const nextHistory = [...tab.history, turn].slice(-HISTORY_MAX_TURNS);
    const next: Partial<VoiceState> = {
      byTabId: { ...s.byTabId, [tabId]: { ...tab, history: nextHistory } },
    };
    if (tabId === s.activeTabId) {
      next.history = nextHistory;
    }
    return next as Partial<VoiceState>;
  });
}

/** Z.4 — transcript-log entry.
 *
 * Phase 9.8 (2026-05-24) — D-9.8-1 LOCKED: schema extends ADDITIVELY for
 * cross-turn tool memory. `role` gains `'tool'`; new optional `tool` field
 * carries the structured tool-call summary. Pre-9.8 localStorage payloads
 * (no `tool` field, role ∈ {user,bridge}) hydrate cleanly — `tool` is
 * optional, existing render paths fall through the role check.
 */
export interface VoiceTurn {
  role: 'user' | 'bridge' | 'tool';
  /** For role:'user'|'bridge': the spoken/typed text.
   *  For role:'tool': human-readable chip label e.g. "navigate → ok view=designer". */
  text: string;
  timestamp: number;
  /** Phase 9.8 — present only when role === 'tool'. */
  tool?: {
    name: string;
    args_summary?: string;
    result_summary?: string;
    error?: string | null;
    duration_ms?: number | null;
    step?: number;
  };
}

export interface VoiceTranscriptEvent {
  type: 'transcript';
  text: string;
  language: string;
  duration: number;
}

export interface VoiceErrorEvent {
  type: 'error';
  code: string;
  message?: string;
}

export interface VoiceReadyEvent {
  type: 'ready';
}

type VoiceServerEvent = VoiceTranscriptEvent | VoiceErrorEvent | VoiceReadyEvent;

/** Z.3 — structured action result returned from /api/voice/dispatch. */
export interface DispatcherActionTaken {
  tool: string;
  args: Record<string, unknown>;
  result?: { spawned_names?: string[]; truncated?: boolean };
  error?: string;
}

export interface DispatcherResponse {
  action_taken: DispatcherActionTaken | null;
  reply_text: string;
  /** Phase α — backend timing meta from /api/voice/dispatch.
   * Populated on success; null on error paths. Shape mirrors
   * `services/voice_dispatcher.py::DispatchResult.timing_meta`. */
  timing_meta?: {
    model_id: string;
    llm_call_ms: number;
    model_resolve_ms: number;
    prompt_tokens: number | null;
    completion_tokens: number | null;
    reasoning_present: boolean;
  } | null;
}

/** Phase α — client-side per-turn timing summary. All timestamps are
 * `performance.now()` (high-resolution monotonic, ms since page load).
 * Emitted via `console.info('[voice.turn.timing.client]', JSON.stringify(...))`
 * from the `audio.onended` handler when a turn completes successfully.
 * The DESIGN §11 α specifies 12 capture points (R1.C2 ensures `t_config_sent`
 * is present). `ttfa_ms` = OLD-path time-to-first-audio. */
export interface ClientTurnTiming {
  turn_id: string;
  t_mic_click: number;
  /** φ (2026-05-16) — captured immediately after `await getUserMedia({audio:true})`
   * resolves, BEFORE the WS constructor runs. Lets the analysis split the
   * Phase α `t_ws_open - t_mic_click` interval into its real sub-stages:
   * (a) getUserMedia cost (mic permission + system audio cold-start) = this − t_mic_click
   * (b) WS construct + handshake cost = t_ws_open − this. The N investigation
   * agent (docs/20260516/20260516_N_INVESTIGATION_MACBOOK_TO_VULTR_LEG.md)
   * found that Phase α's `t_ws_open - t_mic_click` had been mistakenly blamed
   * on the WS handshake alone, when the real spend could be either stage. */
  t_get_user_media_resolved: number;
  /** φ (2026-05-16) — captured immediately after `new WebSocket(url)` returns,
   * before the WS handshake begins. Lets the analysis further isolate the
   * pure-handshake cost: t_ws_open − this = TCP/TLS/Upgrade-round-trip. */
  t_ws_constructor_called: number;
  /** PWS (2026-05-17) — WS lifecycle state at the moment of mic click.
   * 'OPEN' means the persistent WS was already open (warm path; no handshake
   * cost — `t_ws_open - t_ws_constructor_called` will be 0ms because both
   * are set to t_mic_click). 'CONNECTING' means the WS was in flight (handshake
   * already started in background). 'CLOSED' means cold path (legacy
   * `new WebSocket()` in startRecording — handshake cost still paid). The
   * analyst filters by this field to compute warm-vs-cold ratio. */
  t_ws_state_at_click: 'OPEN' | 'CONNECTING' | 'CLOSED';
  t_ws_open: number;
  t_config_sent: number;
  t_first_audio_chunk_sent: number;
  t_stop_sent: number;
  t_transcript_rcvd: number;
  t_dispatch_post_start: number;
  t_dispatch_rcvd: number;
  t_tts_post_start: number;
  t_tts_rcvd: number;
  t_audio_play_called: number;
  t_audio_ended: number;
  ttfa_ms: number;
  total_ms: number;
  user_agent: string;
}

/** WS-A Φ2 (2026-05-18) — per-tab voice state. One per voice tab in the strip.
 * Owns everything that can differ between tabs: voice settings, transcript log,
 * status state-machine, last dispatch result, per-turn timing meta. Page-global
 * fields (audioLevel/voices/engines) stay TOP-LEVEL on VoiceState per worldview
 * D-A1 + R1.A C-3. */
/** Phase 9.3 (2026-05-24) — entry in the per-tab tool_progress strip.
 * One frame from the backend WS pipeline = one entry. Frontend renders the
 * strip via `humanizeTool(tool, status)` in VoicePanel. Bounded to 10 most
 * recent entries (slice(-10)); cleared on `end_of_turn` via
 * `writeIdleToOriginTab` (CONC-9.3-3).
 *
 * `tool` is NOT enum-typed — schema is EXTENSIBLE per CONC-9.3-1; backend
 * may add new tools without a frontend ship. `humanizeTool` falls back to
 * `${tool}: ${status}` for unknown tools. */
export interface ToolProgressEntry {
  tool: string;
  step: number;
  status: 'starting' | 'completed' | 'failed';
  error?: string | null;
  timestamp: number;
}

export interface VoiceTabState {
  settings: VoiceSettings;
  history: VoiceTurn[];
  transcript: string;
  status: VoiceStatus;
  error: string | null;
  lastDispatch: DispatcherResponse | null;
  currentTurnId: string | null;
  currentTurnStart: number | null;
  lastTurnTiming: ClientTurnTiming | null;
  /** Phase 9.3 — bounded per-tab tool_progress strip; null when no turn
   * is in flight or after end_of_turn clear. */
  currentToolProgress: ToolProgressEntry[] | null;
  /** Phase 9.6 (2026-05-24) — transient streaming overlay text for the
   * in-flight bridge reply. Updated per `delta` WS frame (after tool-call-XML
   * filter per D-9.6-2). Reset to '' on every `tool_progress` `status='starting'`
   * (D-9.6-3 load-bearing piece). Cleared on `end_of_turn` via
   * `teardownStreamingTurnResources` → `resetStreamingTurnState`. NOT persisted
   * to localStorage. Render in VoicePanel ONLY while `status === 'transcribing'`. */
  streamingReplyText: string;
}

/** WS-A Φ2 — tab id alias. UUIDs in production; arbitrary strings in tests. */
export type TabId = string;

interface VoiceState {
  /** WS-A Φ2 — per-tab map. CANONICAL source of truth for per-tab fields
   * (settings, history, transcript, status, error, lastDispatch, currentTurnId,
   * currentTurnStart, lastTurnTiming). The top-level fields below are MIRRORS
   * of `byTabId[activeTabId]` — every per-tab write also updates the mirror
   * for back-compat with the 60+ existing consumers across VoicePanel,
   * SettingsMenu, and 5 test suites. setActiveTab swaps the mirror by copying
   * the new active tab's values. New byTabId consumers (voice tab strip)
   * read from byTabId[activeTabId] directly. Plan-v2 §2.3 step 2 deviation
   * documented in commit body. */
  byTabId: Record<TabId, VoiceTabState>;
  activeTabId: TabId | null;

  status: VoiceStatus;
  transcript: string;
  error: string | null;
  lastDispatch: DispatcherResponse | null;
  history: VoiceTurn[];
  /** 2026-05-07 — RMS audio level [0, 1] sampled by the VAD analyser
   * while recording. Drives the live level-meter UI so the user can
   * confirm the mic is hot. Always 0 when not recording. Page-global per
   * R1.A C-3 (NOT per-tab). */
  audioLevel: number;
  settings: VoiceSettings;
  /** F1 — live voice list from the SELECTED engine. Page-global per R1.A C-3. */
  voices: VoicePreset[];
  /** 2026-05-07 — registered TTS engines. Page-global per R1.A C-3. */
  engines: EngineSpec[];
  /** 2026-06-28 — tenant-router models available to the voice model picker.
   * Populated by loadModels(); empty until fetched (UI shows just "Auto"). */
  models: VoiceModelOption[];
  lastTurnTiming: ClientTurnTiming | null;
  currentTurnId: string | null;
  currentTurnStart: number | null;
  /** Phase 9.3 — top-level mirror of `byTabId[activeTabId].currentToolProgress`
   * for the SPA panel consumers (VoicePanel reads from useVoiceStore()
   * destructure). _setTabFields auto-mirrors per-tab writes to top-level
   * when tabId === activeTabId. */
  currentToolProgress: ToolProgressEntry[] | null;
  /** Phase 9.6 (2026-05-24) — top-level mirror of
   * `byTabId[activeTabId].streamingReplyText`. Same auto-mirror pattern as
   * `currentToolProgress`. VoicePanel renders the overlay reading from this. */
  streamingReplyText: string;

  /**
   * F2 — startRecording accepts disableVAD to skip the silence-watchdog
   * (push-to-talk mode: user explicitly holds the button; we should not
   * auto-stop on silence). Default false = click-toggle mode (Z.1 behavior).
   */
  startRecording: (options?: { disableVAD?: boolean }) => Promise<void>;
  stopRecording: () => void;
  /** δ.8.1 — voice-streaming-pipeline turn. Parallels startRecording but
   * routes through the new /api/voice/turn WS endpoint with server-driven
   * streaming MP3 + Web Audio API playback. Gated by USE_VOICE_TURN_WS.
   * On `end_of_turn` or error, calls teardownTurn() internally. */
  startTurn: (options?: { disableVAD?: boolean }) => Promise<void>;
  /** δ.8.1 — signal the server to flush + transcribe + synthesize the
   * current utterance. Mirrors stopRecording for the new path. */
  stopTurn: () => void;
  /** Phase 9.7 (2026-05-24) — D-9.7 mid-turn cancellation. Sends
   * `cancel_turn` frame for the in-flight turn_id. WS stays open per
   * D-9.7-4. D-9.7-6 idempotent: second call for same turn_id is a no-op.
   * Called by VoicePanel's stop button onClick. */
  cancelTurn: () => void;
  /** 2026-06-07 — stop the currently-playing TTS audio for the active
   * voice tab. Pauses the audio element, rewinds, removes it from
   * _playingAudioByTab, and transitions the tab status to 'idle'. Mirrors
   * the pauseAudioOnLoseFocus path (L2879-2887) but invoked explicitly by
   * the user via a Stop button while status === 'speaking'. Idempotent:
   * if no audio is registered for the active tab, no-op. */
  stopSpeaking: () => void;
  /** δ⁗ — explicit teardown of the persistent streaming WS + AudioContext +
   * AudioQueue. Called from VoicePanel's mode-switch onChange handler so the
   * server-side coroutine doesn't sit on an idle persistent WS. Idempotent. */
  teardownStreamingSession: () => void;
  /** Z.2 — synthesize text via the selected TTS engine and play via Web Audio.
   * WS-A Φ2 v2 — `engine?` added per D-A10 + R1.B C-3 so the dispatch chain
   * can lock the engine at dispatch initiation (not at microtask consumption). */
  speak: (text: string, voice?: string, speed?: number, engine?: string) => Promise<void>;
  /** Z.3 — dispatch a transcript through the LLM dispatcher; returns
   * action_taken + reply_text. Errors set status='error'. */
  dispatch: (transcript: string) => Promise<DispatcherResponse | null>;
  /** Z.4 — settings update; persists to localStorage. WS-A Φ2 — writes to
   * active tab via byTabId[activeTabId].settings. */
  updateSettings: (patch: Partial<VoiceSettings>) => void;
  /** Z.4 — wipe the in-memory transcript log. WS-A Φ2 — active tab only. */
  clearHistory: () => void;
  /** 1.420.0 — replace the transcript log wholesale (voice compaction:
   * summary bridge turn replaces the raw log). Same active-tab semantics
   * as clearHistory. */
  replaceHistory: (history: VoiceTurn[]) => void;
  /** F1 / 2026-05-07: load voices for the CURRENTLY SELECTED engine
   * (settings.engine). Validates settings.voice against the live list and
   * resets to engine's default_voice if stale. */
  loadVoices: () => Promise<void>;
  /** 2026-05-07 — fetch the registered engines from /api/voice/engines.
   * Called once on mount; stores into `engines` state. */
  loadEngines: () => Promise<void>;
  /** 2026-06-28 — fetch tenant-router models for the voice model picker from
   * /api/chat-proxy/v1/models (same source as Pi Chat). Best-effort: on
   * failure leaves `models` unchanged (UI degrades to just the "Auto" option). */
  loadModels: () => Promise<void>;
  /** 2026-05-07 — switch active TTS engine. Persists to localStorage,
   * resets settings.voice to the new engine's default_voice, then reloads
   * the voice list for the new engine. */
  setEngine: (engineId: string) => Promise<void>;

  /** WS-A Φ2 — hydrate per-tab settings from localStorage. Reads v2 storage
   * (`vibe_voice_settings_v2_${tenantId}`); on miss, MIGRATES from legacy
   * SETTINGS_KEY into the FIRST tab id supplied via tabsForTenant. Legacy
   * key is NOT deleted on hydrate — only on the first subsequent v2 write
   * via updateSettings (D-A2). Tests can pass synthetic tab ids; production
   * hydrate is wired from voiceTabsStore.hydrate. */
  hydrate: (
    tenantId: string,
    tabsForTenant?: TabId[],
    activeTabIdHint?: TabId | null,
  ) => Promise<void>;

  /** WS-A Φ2 — swap active voice tab. Tears down per-turn resources
   * (D-A3) while preserving persistent WS handles. Triggers greeting
   * fire-once-per-tab (D-A6). No-op on unknown tabId or same-tab call. */
  setActiveTab: (tenantId: string, tabId: TabId) => void;

  /** 1.167.0 — remove a tab's byTabId entry. Called from
   * voiceTabsStore.closeTab so persistent history doesn't outlive the tab.
   * No-op if tabId is unknown. If the dropped tab was active, callers
   * must update activeTabId separately (voiceTabsStore.closeTab does). */
  dropTab: (tabId: TabId) => void;

  /** WS-A Φ2 test-only — synthetic injection of a classic-WS frame. NOT for
   * production callers. Test fixture only (see VS.21 in voiceStore.byTabId.test.ts). */
  __test_classic_onmessage_handle?: (payload: VoiceServerEvent) => void;
  /** WS-A Φ2 test-only — synthetic injection of a streaming frame. NOT for
   * production callers. Test fixture only (see VS.22 in voiceStore.byTabId.test.ts). */
  __test_streaming_frame_handle?: (frame: Record<string, unknown>) => void;
}

const HISTORY_MAX_TURNS = 50;

// 1.162.2 — voice→Pi prefix-route helper. Returns null on no-match;
// returns {routed:true, strippedText, originalText} on match. Mirrors
// backend _check_pi_prefix in routes/voice.py. Empty phrase disables.
// Per R3 Δ23: caller MUST NOT push user turn (handlePerTurnWsMessage
// already pushes it unconditionally at the transcript handler).
export function checkAndRoutePiPrefix(
  transcript: string,
  phrase: string,
): { routed: boolean; strippedText: string; originalText: string } {
  const trimmed = (phrase || '').trim();
  if (!trimmed) {
    return { routed: false, strippedText: transcript, originalText: transcript };
  }
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^\\s*${escaped}\\s*[,:.!?\\s]+(.*)$`, 'is');
  const m = transcript.match(re);
  if (!m) {
    return { routed: false, strippedText: transcript, originalText: transcript };
  }
  return { routed: true, strippedText: m[1].trim(), originalText: transcript };
}

// 1.162.2 — extract plain-text content from a pi-agent Message. Handles
// UserMessage (content: string | (TextContent|ImageContent)[]) +
// AssistantMessage (content: (TextContent|ThinkingContent|ToolCall)[]).
// ToolResultMessage is dropped upstream by the role whitelist.
// Reviewer trail R2 Δ2+Δ3: verified shapes from @earendil-works/pi-ai types.d.ts.
type PiContentBlock = { type?: string; text?: string };
export function extractPiChatText(msg: {
  role: string;
  content: string | PiContentBlock[];
}): string {
  if (msg.role === 'user') {
    const c = msg.content;
    if (typeof c === 'string') return c;
    if (Array.isArray(c)) {
      return c
        .filter((b): b is PiContentBlock => !!b && (b as PiContentBlock).type === 'text')
        .map((b) => String((b as PiContentBlock).text || ''))
        .join(' ');
    }
    return '';
  }
  if (msg.role === 'assistant') {
    const c = msg.content;
    if (!Array.isArray(c)) return '';
    return c
      .filter((b): b is PiContentBlock => !!b && (b as PiContentBlock).type === 'text')
      .map((b) => String((b as PiContentBlock).text || ''))
      .join(' ');
  }
  return '';
}

// 1.162.3 — voice forward-trigger detection. Returns true if the transcript
// CONTAINS the trigger phrase (case-insensitive, word-boundary-tolerant).
// Different from checkAndRoutePiPrefix which requires anchored-at-start.
// This one matches anywhere because the user often says the trigger AFTER
// describing what they want forwarded.
export function checkPiForwardTrigger(transcript: string, phrase: string): boolean {
  const trimmed = (phrase || '').trim();
  if (!trimmed) return false;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Match anywhere; case-insensitive. Don't require word boundaries —
  // phrase will likely contain spaces; the substring match is what matters.
  const re = new RegExp(escaped, 'i');
  return re.test(transcript);
}

// 1.162.3 — build the forwarded text per the configured scope. Reads from
// the current voice history (CALLED BEFORE pushing the trigger user turn,
// so history[-1] is whatever BRIDGE last said).
export function buildForwardPayload(
  scope: 'latest-bridge' | 'latest-pair' | 'whole-transcript',
  history: Array<{ role: 'user' | 'bridge'; text: string }>,
): string {
  if (history.length === 0) return '';
  if (scope === 'latest-bridge') {
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].role === 'bridge') return history[i].text;
    }
    return '';
  }
  if (scope === 'latest-pair') {
    // Find the latest bridge turn + the user turn immediately before it.
    let bridgeIdx = -1;
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].role === 'bridge') { bridgeIdx = i; break; }
    }
    if (bridgeIdx === -1) return '';
    let userIdx = -1;
    for (let i = bridgeIdx - 1; i >= 0; i--) {
      if (history[i].role === 'user') { userIdx = i; break; }
    }
    const parts: string[] = [];
    if (userIdx >= 0) parts.push(`USER: ${history[userIdx].text}`);
    parts.push(`BRIDGE: ${history[bridgeIdx].text}`);
    return parts.join('\n');
  }
  // 'whole-transcript'
  return history
    .map((t) => `${t.role === 'user' ? 'USER' : 'BRIDGE'}: ${t.text}`)
    .join('\n');
}

// 1.162.2 — collect Pi chat history for injection into voice LLM context.
// Reads from window.__piChatBridge if mounted; else empty.
// Returns the last `maxTurns` user/assistant messages with text extracted,
// each capped at 500 chars + ellipsis. Empty results filtered.
export function collectPiChatHistory(maxTurns: number): Array<{ role: string; text: string }> {
  if (maxTurns <= 0) return [];
  const bridge = (window as unknown as {
    __piChatBridge?: { getActiveMessages?: () => unknown[] };
  }).__piChatBridge;
  if (!bridge || typeof bridge.getActiveMessages !== 'function') return [];
  let messages: unknown[] = [];
  try {
    messages = bridge.getActiveMessages() || [];
  } catch {
    return [];
  }
  if (!Array.isArray(messages)) return [];
  const filtered: Array<{ role: string; text: string }> = [];
  for (const m of messages) {
    if (!m || typeof m !== 'object') continue;
    const obj = m as { role?: string; content?: string | PiContentBlock[] };
    if (obj.role !== 'user' && obj.role !== 'assistant') continue;
    const text = extractPiChatText({
      role: obj.role,
      content: obj.content ?? '',
    }).trim();
    if (text.length === 0) continue;
    const capped = text.length > 500 ? text.slice(0, 497) + '...' : text;
    filtered.push({ role: obj.role, text: capped });
  }
  return filtered.slice(-maxTurns);
}

// Module-level handles for the active session. NOT in store — these are
// non-serializable browser objects that don't belong in zustand state.
let activeWS: WebSocket | null = null;
let activeRecorder: MediaRecorder | null = null;
let activeStream: MediaStream | null = null;
// VAD silence-watchdog handles (Z.1 click-to-toggle UX).
let activeAudioContext: AudioContext | null = null;
let activeAnalyser: AnalyserNode | null = null;
let silencePollHandle: ReturnType<typeof setInterval> | null = null;

// PWS (2026-05-17) — persistent-WS lifecycle handles.
// activeWS is OPENED ONCE at module init and reused across every voice turn.
// Keepalive interval sends {type:"keepalive"} every 30s; backend echoes ack.
// Auto-reconnect schedules with exponential backoff on unexpected close while
// status === 'idle'; mid-recording close surfaces as 'error' via existing
// ws.onclose escape hatch.
const PWS_KEEPALIVE_INTERVAL_MS = 30_000;
const PWS_RECONNECT_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
/** WS close code for auth-frame rejection (RFC 6455 policy violation). The
 * backend (routes/voice.py) closes the voice WS with this code when
 * consume_ws_auth_frame fails. Mirrors TerminalTile's D1-5 fail-closed const. */
const WS_CLOSE_POLICY_VIOLATION = 1008;

/**
 * 2026-06-29 — decide whether a streaming-WS 1008 (policy-violation / auth-frame
 * rejection) should reconnect or fail closed.
 *
 * The persistent /api/voice/turn WS auto-opens at module-init, which can beat
 * the OIDC token into localStorage. Its onOpen then sends no auth frame
 * (voiceAuthFrame() is null) and the backend closes 1008. The original
 * fail-closed-on-every-1008 rule (2026-06-25) assumed 1008 ⟹ dead token ⟹ the
 * auth layer redirects to /login. That holds for a genuinely-expired/absent
 * token, but NOT for the boot race: the token is valid a moment later, so
 * AuthProvider never redirects and the streaming socket stays dead all session
 * (the classic WS recovers because it reconnects unconditionally).
 *
 * Distinguish the two by the only thing that differs: is there a token NOW?
 *   token present → transient race → reconnect (the reopen sends the auth frame).
 *   no token      → genuine logout/expiry → fail closed; the auth layer owns it.
 */
export function streamingPolicyCloseShouldReconnect(): boolean {
  return getStoredToken() !== null;
}
let pwsKeepaliveHandle: ReturnType<typeof setInterval> | null = null;
let pwsReconnectHandle: ReturnType<typeof setTimeout> | null = null;
let pwsReconnectAttempt = 0;
// PWS — true after the FIRST successful openPersistentWS() call. Used by
// startRecording to decide whether to await an in-flight handshake or to
// fall back to the legacy per-turn `new WebSocket()` path.
let pwsInitialized = false;

// ----- δ.8 — voice-streaming-pipeline state + feature flag -----
//
// Per plan §4 δ.8 + DESIGN §5.2 + R3.2 naming discipline:
//   `activeTurnAudioContext` is the per-turn PLAYBACK AudioContext for
//   the new Web Audio API streaming-MP3 queue. It is DISTINCT from
//   `activeAudioContext` (line 326) which wraps the VAD mic-input
//   AnalyserNode in the legacy 3-hop path. Reusing the same variable
//   would either prematurely close VAD or leak it across turns (R3.2).
//
// Per worldview D6: feature flag default OFF. Component (VoicePanel.tsx)
// reads this flag and dispatches to startTurn() vs the legacy
// startRecording() at click time. Flag flip to ON for dallas canary
// happens via a separate δ.10.1 commit + redeploy.

// δ′.3 (2026-05-17) — `USE_VOICE_TURN_WS` REMOVED. The dispatch decision
// moved to `VoiceSettings.voiceMode` (a user-facing setting in the voice
// settings panel) — see VoicePanel.tsx::handleClick. Default voiceMode
// is 'classic' so existing users see no change; streaming is opt-in.
//
// Why the change: the prior boolean was build-time-fixed and required
// a redeploy to flip. The δ.8.1 ship (1.147.0, flag forced ON) broke
// dallas because every chunk's decodeAudioData failed (DESIGN §22 records
// the root cause + correction). δ′ ships the corrected per-sentence
// architecture but defaults to 'classic' so the legacy 3-hop path stays
// active for any user who doesn't explicitly opt in via settings.

let activeTurnAudioContext: AudioContext | null = null;
// δ″ — static imports at top-of-file replaced lazy `await import()` so these
// can carry their real types (Plan R1 Concern C1).
let activeTurnSocket: VoiceTurnSocket | null = null;
let activeTurnAudioQueue: VoiceAudioQueue | null = null;
// δ″ F2 — streaming-mode mic-level meter poll handle (distinct from
// classic-mode's silencePollHandle per R3.2 separation).
let turnLevelPollHandle: ReturnType<typeof setInterval> | null = null;

// δ⁗ (2026-05-17) — streaming PWS module-level state. Mirrors classic PWS at
// lines 377-382 but tracks the streaming WS lifecycle separately.
let streamingKeepaliveHandle: ReturnType<typeof setInterval> | null = null;
let streamingReconnectHandle: ReturnType<typeof setTimeout> | null = null;
let streamingReconnectAttempt = 0;
// δ⁗ post-G6 fix — Promise that resolves on WS open. Replaces the
// pendingConfigSend pattern which had a race: if onOpen fired during
// startTurn's awaits BEFORE pendingConfigSend was assigned, the drain
// in onOpen saw null and never re-triggered. Promise-based gate is
// immune to ordering (resolved Promise still resolves on later await).
//
// δ⁗ post-G7 fix — resolve/reject are captured in LOCAL closure scope
// inside openPersistentStreamingWS, NOT at module level. Module level
// only carries the Promise itself (for startTurn to await). Reason:
// when a new socket is constructed (after teardown), module-level
// resolve/reject would otherwise be overwritten — the OLD socket's
// onclose could then erroneously reject the NEW socket's Promise.
let streamingWSOpenPromise: Promise<void> | null = null;

// 1.164.2 — exported so vitest can pin the budget without racing a real timer.
// voiceStore.ts:37-43 documents a 15-second observed VPS reverse-tunnel WS
// handshake latency. The pre-1.164.2 budget of 8s was shorter than that and
// fired false-positive timeouts on cold loads through the tunnel — symptom
// surfaced as `"streaming ws: streaming ws open timeout 8s"` in the SPA's
// voice transcript area when bundle-hash bumps forced a fresh SPA load.
// 20s = documented 15s + 5s headroom. Used at the Promise.race in startTurn.
export const STREAMING_WS_OPEN_TIMEOUT_MS = 20000;
export const STREAMING_WS_OPEN_TIMEOUT_ERROR = 'streaming ws open timeout 20s';

// δ⁗ — per-turn streaming state lifted out of startTurn closure (D4).
// MUST reset at the moment a new `config` frame is sent (R1 Concern #3).
const currentStreamingTurnState: {
  lastSeq: number | null;
  finalizeStarted: boolean;
  accumulatedReply: string;
  // Phase 9.6 (2026-05-24) — D-9.6-3 transient delta-accumulator (SEPARATE
  // from accumulatedReply which keeps sourcing from chunk_meta per D-9.6-4).
  streamingDeltaText: string;
  // Phase 9.6 — D-9.6-2 tool-call-XML filter state machine. R1.0 C-2 INVARIANT:
  // BOTH exit-tool-mode paths in `_applyToolCallFilter` MUST clear
  // toolCallBuffer + inToolCallMode. Buffer-not-cleared causes spurious
  // re-entry on next delta. T4b regression-guards this.
  inToolCallMode: boolean;
  toolCallBuffer: string;
  // Phase 9.7 (2026-05-24) — D-9.7-6 FE idempotency: the turn_id we've
  // already sent a cancel_turn frame for. Repeated cancelTurn() calls
  // for the same turn_id are no-ops on the FE side. Backend listener
  // ALSO enforces idempotency, but FE early-return saves a WS frame.
  // Cleared on `cancelled` frame receipt + on resetStreamingTurnState.
  pendingCancelTurnId: string | null;
} = {
  lastSeq: null,
  finalizeStarted: false,
  accumulatedReply: '',
  streamingDeltaText: '',
  inToolCallMode: false,
  toolCallBuffer: '',
  pendingCancelTurnId: null,
};

// Phase 9.6 (2026-05-24) — D-9.6-5 module-level protocol version flag.
// Set by `ready` WS-frame handler from frame.protocol_version. Reset to
// false on every `openPersistentStreamingWS.onOpen` (compat with rolling-
// restart: new frontend + old backend → flag stays false → deltas ignored
// = pre-v19 behavior). Phase 9.6 R1 brief §1.2 / OPEN-1 lock.
let protocolVersionV19_1: boolean = false;
// Phase 9.7 (2026-05-24) — D-9.7 cancel-turn capability flag. 'v19.2' is
// a SUPERSET of 'v19.1' (both flags true on v19.2). Reactivity via the
// status='transcribing' store-field re-render — same pattern as v19_1
// per R1.0 C-2 + Phase 9.6 R3 LIVE precedent.
let protocolVersionV19_2: boolean = false;

function resetStreamingTurnState(): void {
  currentStreamingTurnState.lastSeq = null;
  currentStreamingTurnState.finalizeStarted = false;
  currentStreamingTurnState.accumulatedReply = '';
  // Phase 9.6 — clear delta-accumulator + state-machine fields.
  currentStreamingTurnState.streamingDeltaText = '';
  currentStreamingTurnState.inToolCallMode = false;
  currentStreamingTurnState.toolCallBuffer = '';
  // Phase 9.7 — clear cancel-idempotency tracker.
  currentStreamingTurnState.pendingCancelTurnId = null;
}

// Phase 9.6 (2026-05-24) — D-9.6-2 tool-call-XML filter constants.
const TOOL_CALL_OPEN = '<tool_call>';
const TOOL_CALL_CLOSE = '</tool_call>';
const STREAMING_REPLY_MAX_LENGTH_CHARS = 5000;

/**
 * Phase 9.6 D-9.6-2 — tool-call-XML state-machine filter for delta-accumulator.
 *
 * Reads + writes `currentStreamingTurnState.inToolCallMode` + `.toolCallBuffer`.
 *
 * R1.0 C-2 INVARIANT (regression-guarded by T4b): EVERY exit-tool-mode path
 * MUST clear `toolCallBuffer = ''` AND `inToolCallMode = false`. Buffer-not-
 * cleared causes spurious re-entry on the next delta.
 *
 * 5 paths total:
 *   CASE A no-close      → return '' (still inside tool-call buffer)
 *   CASE A close-found   → clear flag + buffer, return '' (post-close dropped)
 *   CASE B no-open       → return delta (pure prose)
 *   CASE B open-no-close → set flag + buffer from open, return preTool
 *   CASE B open-and-close → return preTool (NEVER enters tool-mode; post-close dropped)
 *
 * Post-close prose dropped per O-9.6-2 lock. T3b regression-guards this.
 */
function _applyToolCallFilter(delta: string): string {
  // CASE A — currently inside a tool_call (open was seen in a previous delta).
  if (currentStreamingTurnState.inToolCallMode) {
    currentStreamingTurnState.toolCallBuffer += delta;
    const closeIdx = currentStreamingTurnState.toolCallBuffer.indexOf(TOOL_CALL_CLOSE);
    if (closeIdx === -1) {
      // Still inside tool-call; drop everything in this delta + keep buffering.
      return '';
    }
    // Found close. Anything AFTER the close in buffer is post-close prose
    // (rare; locked DROPPED per O-9.6-2 — see T3b assertion).
    currentStreamingTurnState.inToolCallMode = false;
    currentStreamingTurnState.toolCallBuffer = '';  // INVARIANT: clear on exit
    return '';
  }

  // CASE B — not in tool-mode. Look for OPEN tag in the delta.
  const openIdx = delta.indexOf(TOOL_CALL_OPEN);
  if (openIdx === -1) {
    return delta;  // pure prose, no XML present
  }

  // Pre-open prose renders normally.
  const preTool = delta.slice(0, openIdx);
  const fromOpen = delta.slice(openIdx);
  const closeIdxInRemainder = fromOpen.indexOf(TOOL_CALL_CLOSE);

  if (closeIdxInRemainder === -1) {
    // Open found, no close in this delta — enter tool-mode + buffer everything
    // from `<tool_call>` onwards.
    currentStreamingTurnState.inToolCallMode = true;
    currentStreamingTurnState.toolCallBuffer = fromOpen;
    return preTool;
  }

  // Open AND close both in this delta. Drop chars between (inclusive of tags).
  // Post-close prose dropped per O-9.6-2 — see T3b. INVARIANT: buffer stays
  // clean, mode stays false (never enters tool-mode in this branch).
  return preTool;
}

// Phase α — module-level mutable dict accumulating the current turn's
// timing fields across the async chain (startRecording → onmessage →
// dispatch → speak → onended). MUST be reset to {} at the top of
// startRecording() (R1.CONCERN-2) so a rapid second mic-click doesn't
// inherit stale partial fields from the prior turn.
let currentTurnTiming: Partial<ClientTurnTiming> = {};

/** WS-A.5 (1.164.0) — per-tab playing-audio registry.
 *
 * Tracks the HTMLAudioElement currently playing for each voice tab so
 * setActiveTab's `pauseAudioOnLoseFocus` branch can pause the right ref
 * when the user leaves a tab. Multi-tab implies multi-concurrent-audio:
 * the prior `outputAudio` module variable is reused at speak() line as
 * the iOS-gesture-primed element, but the actually-playing audio (which
 * may be a fresh `new Audio()` when outputAudio is null) is tracked here
 * keyed by speakingTabId. Entries are registered when speak() begins
 * playback, and cleared on audio.onended OR the play() catch path.
 *
 * Reasoning for using a Module-level Map (not store state): the playing
 * HTMLAudioElement is detached from the DOM (`new Audio()` does NOT
 * append), so it cannot live in Zustand state without serialization
 * problems. Same discipline as existing module-level `outputAudio`,
 * `activeRecorder`, `activeStream`. */
const _playingAudioByTab: Map<TabId, HTMLAudioElement> = new Map();

/** Phase α — 8-hex-char correlation ID. Collision probability is
 * negligible at session scale (~10⁻¹⁰ for 30 turns). */
function genTurnId(): string {
  return Math.random().toString(16).slice(2, 10).padEnd(8, '0');
}

/**
 * 1.137.0 — iOS Safari audio-output unlock.
 *
 * iOS Safari blocks `new Audio(...).play()` outside a user-gesture context.
 * The dispatcher reply path calls speak() asynchronously from a WebSocket
 * message handler — by then the original mic-click gesture is gone, so
 * playback fails silently. macOS Safari is more lenient (works there).
 *
 * Fix: during the mic-click gesture, prime a reusable HTMLAudioElement by
 * playing a 1-frame silent WAV. Once any audio element has been .play()'d
 * inside a gesture, that element stays "unlocked" for the session — future
 * .play() calls with new src don't require a gesture. speak() then reuses
 * this primed element.
 *
 * If the user never clicks the mic (e.g., types text + clicks Speak), the
 * Speak click IS itself a gesture, so the fallback `new Audio(...)` path
 * works fine.
 */
let outputAudio: HTMLAudioElement | null = null;
// Tiny 8 kHz mono 1-sample silent WAV (44-byte header + 0 data bytes).
const SILENT_WAV_DATA_URL =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

function primeOutputAudioUnlock(): void {
  // Must be called synchronously from a user-gesture event handler. Safe
  // to call repeatedly — first successful .play() unlocks the element.
  //
  // 1.145.0 — no-op on non-iOS. The silent-WAV unlock satisfies iOS Safari's
  // autonomous-TTS gesture requirement; other browsers don't gate audio
  // playback on this priming, and Firefox's decoder rejects the empty WAV
  // data chunk with NS_ERROR_DOM_MEDIA_METADATA_ERR (cosmetic; zero
  // functional impact). Skipping the prime on non-iOS silences the noise
  // without changing behavior.
  if (!isAppleMobile()) return;
  if (outputAudio) return;
  try {
    const el = new Audio(SILENT_WAV_DATA_URL);
    el.muted = true;
    const result = el.play();
    if (!(result instanceof Promise)) {
      // jsdom (tests) and very old browsers return undefined from .play().
      // Don't cache the element — speak() will fall back to a fresh Audio
      // per call. Real browsers (incl. iOS Safari) always return a Promise.
      return;
    }
    outputAudio = el;
    result.catch(() => {
      // .play() rejected (autoplay blocked, no gesture, etc.) — drop the
      // element so the next gesture creates a fresh one.
      outputAudio = null;
    });
  } catch {
    /* synchronous throw — Audio constructor rejected; nothing to do */
  }
}

// 1.131.0 iOS mic hotfix — runtime-selected MediaRecorder mimeType.
// Set in startRecording() when the recorder is constructed; sent in the
// WS config message so backend can label the whisperx upload accurately.
// Default 'audio/webm' covers the (rare) no-isTypeSupported fallback path.
let activeRecorderMimeType: string = 'audio/webm';

/**
 * 1.131.0 iOS mic hotfix — pick a MediaRecorder mimeType supported by the
 * current browser. Pre-fix the WS recorder used a hardcoded
 * 'audio/webm;codecs=opus' which iOS Safari does NOT support; the
 * MediaRecorder constructor threw NotSupportedError synchronously and the
 * mic broke silently on every iPhone/iPad.
 *
 * Preference order:
 *   1. audio/webm;codecs=opus — Chrome/Firefox; smallest files, best codec.
 *   2. audio/webm — broader webm; older Chromium variants.
 *   3. audio/mp4;codecs=mp4a.40.2 — iOS Safari with explicit AAC profile.
 *   4. audio/mp4 — iOS Safari default; produces fMP4 that ffmpeg decodes.
 *   5. '' — empty string; caller omits the mimeType option entirely so
 *      the browser picks its default. Last resort for very old browsers
 *      that lack MediaRecorder.isTypeSupported.
 *
 * Returns the chosen mimeType, or '' if no candidate is supported.
 */
export function pickRecorderMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ];
  if (
    typeof MediaRecorder === 'undefined' ||
    typeof MediaRecorder.isTypeSupported !== 'function'
  ) {
    return '';
  }
  for (const mt of candidates) {
    if (MediaRecorder.isTypeSupported(mt)) return mt;
  }
  return '';
}

/**
 * 1.145.0 — canonical iOS-Safari detection used to gate iOS-only quirks.
 *
 * Returns true for:
 *   - iPhone / iPad / iPod via UA substring (pre-iOS-13 UA shape).
 *   - iPadOS 13+ in desktop-UA mode (since iOS 13, default iPad Safari UA
 *     reports `"Macintosh; Intel Mac OS X"` with NO iP* substring; bare
 *     UA regex misses every modern iPad. The MacIntel + maxTouchPoints>1
 *     pair distinguishes iPad-as-Mac from a real Mac — real Macs report
 *     maxTouchPoints === 0; iPads emulating Mac UA still have touch).
 *
 * Returns false for: Mac, Windows, Linux desktop browsers (Firefox, Chrome,
 * Edge, Safari-desktop) — WITH an accepted false-positive on macOS Sonoma+
 * with Touch ID accessibility (`maxTouchPoints > 0` on real Mac). The
 * false-positive is harmless: it causes a cosmetic Firefox-decoder warning,
 * the same behavior as pre-γ on that device. Pinned by test #5 in
 * voiceStore.iosgate.test.ts.
 *
 * Used by: primeOutputAudioUnlock (gating), pickMediaDevicesUnavailableHint,
 * isLikelyIosPermissionDenied, isLockdownLikelyBlocked.
 */
export function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iP(hone|ad|od)/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' &&
      typeof navigator.maxTouchPoints === 'number' &&
      navigator.maxTouchPoints > 1)
  );
}

/**
 * 1.132.0 — Lockdown Mode awareness in the mediaDevices-unavailable hint.
 *
 * Pre-1.132.0 the hint pointed iOS users at `Settings → Safari → Camera &
 * Microphone`, but on Lockdown Mode that path is wrong: the site never
 * appears in the Camera & Microphone permission list until Lockdown is
 * EXCLUDED for it. Lockdown Mode disables WebKit's WebRTC surface
 * entirely (`navigator.mediaDevices === undefined`).
 *
 * Heuristic: secure context + iOS Safari UA + mediaDevices undefined
 * ≈ Lockdown Mode (no other plausible cause on a current iOS Safari).
 *
 * Apple support: https://support.apple.com/en-us/105120
 * Empirical: https://www.sevarg.net/2022/07/20/ios16-lockdown-mode-browser-analysis/
 */
export function pickMediaDevicesUnavailableHint(): string {
  const appleMobile = isAppleMobile();
  const isSecure = typeof window !== 'undefined' && window.isSecureContext === true;

  if (appleMobile && isSecure) {
    return (
      'iOS Lockdown Mode is the most likely cause — it hides navigator.mediaDevices on every non-excluded site. ' +
      'Fix on iPhone/iPad: in Safari, tap the page-settings icon at the LEFT of the URL bar (on iOS 17 and earlier this is the "Aa" button) → Website Settings → turn OFF "Lockdown Mode" for this site → reload → tap the mic again and grant the permission prompt. ' +
      'Alternative path: Settings → Privacy & Security → Lockdown Mode → Configure Web Browsing → Excluded Safari Websites → add this site. ' +
      'NOTE: the Settings → Safari → Camera & Microphone toggle will NOT show this site until Lockdown is excluded for it. ' +
      'If the page is loaded inside a third-party app/WebView (not Safari proper), the mic API is blocked entirely — open the link in Safari directly.'
    );
  }
  if (appleMobile) {
    return (
      'iOS Safari: confirm the page is loaded over HTTPS (not HTTP) — navigator.mediaDevices requires a secure context. ' +
      'If you are inside a third-party app/WebView, open the link in Safari proper. ' +
      'Once on HTTPS, Settings → Safari → Camera & Microphone allows mic access for this site.'
    );
  }
  return (
    'Microphone API not available. Confirm the page is loaded over HTTPS and that your browser supports getUserMedia (Chrome/Firefox/Edge/Safari all do on HTTPS).'
  );
}

/**
 * 1.134.0 — proactive Lockdown Mode detection for mount-time UX.
 *
 * Same heuristic as pickMediaDevicesUnavailableHint above, but exported
 * as a boolean for callers (VoicePanel) that want to surface the
 * structured MicLockdownCard on first render without forcing the user
 * to click the mic first.
 *
 * Gate sequence (all must pass to return true):
 *   1. navigator.mediaDevices.getUserMedia is unavailable — required.
 *      Otherwise we'd false-positive on a normally-functioning iOS site.
 *   2. iOS Safari UA (iPhone/iPad/iPod OR iPadOS-in-desktop-UA-mode).
 *   3. Secure context (HTTPS). Insecure context is a different root
 *      cause (mediaDevices unavailable because of HTTPS, not Lockdown).
 *
 * False-negative on real-Mac running Lockdown Mode: a Mac in Lockdown
 * also disables WebRTC, but this heuristic excludes it (real Macs have
 * maxTouchPoints=0). Acceptable — Mac users are vastly less likely to
 * be on Lockdown Mode, and the click-time hint still surfaces correctly
 * via pickMediaDevicesUnavailableHint's non-Apple branch.
 */
/**
 * 1.135.0 — iOS-aware detection for NotAllowedError state.
 *
 * Used by VoicePanel to decide whether to render MicPermissionDeniedCard
 * (the iOS-26-verified Settings → Apps → Safari → Settings for Websites →
 * Microphone path) instead of the raw error string. Non-iOS users keep the
 * raw string because their browsers tend to surface clearer messaging on
 * permission denial.
 */
export function isLikelyIosPermissionDenied(errorMsg: string | null | undefined): boolean {
  if (!errorMsg) return false;
  if (!errorMsg.includes('NotAllowedError')) return false;
  return isAppleMobile();
}

export function isLockdownLikelyBlocked(): boolean {
  // R1 C1 — `typeof navigator === 'undefined'` guard STAYS even though
  // isAppleMobile() also performs this check internally, because the
  // navigator.mediaDevices access below runs BEFORE isAppleMobile() and
  // would throw ReferenceError on undefined-navigator (SSR / jsdom edges).
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  ) {
    return false;
  }
  const appleMobile = isAppleMobile();
  const isSecure = window.isSecureContext === true;
  return appleMobile && isSecure;
}

// VAD tuning. RMS threshold is normalized 0..1 (8-bit PCM divided by 128).
// Typical speech RMS sits at 0.05-0.20; ambient mic noise at 0.005-0.015.
// 0.02 sits in the gap. Adjust per environment if false-positives/-negatives
// surface in the field.
const SILENCE_RMS_THRESHOLD = 0.02;
const SILENCE_TIMEOUT_MS = 5000;
const VAD_POLL_INTERVAL_MS = 100;

/**
 * PWS (2026-05-17) — per-turn resource teardown.
 * Tears down the per-turn recorder + mic stream + VAD watchdog + audio level
 * meter. Does NOT touch the persistent WebSocket — that's the whole point of
 * PWS: the WS outlives any single turn. Called from ws.onmessage's transcript
 * handler and per-turn error paths.
 *
 * Pre-PWS this was called `teardownSession()` and it ALSO closed activeWS;
 * that path is now `teardownPersistentWS()`.
 */
function teardownTurnResources(): void {
  if (silencePollHandle) {
    clearInterval(silencePollHandle);
    silencePollHandle = null;
  }
  if (activeAudioContext) {
    activeAudioContext.close().catch(() => { /* already closed */ });
    activeAudioContext = null;
  }
  activeAnalyser = null;
  // 2026-05-07: drop level meter back to silence so the UI bar empties.
  useVoiceStore.setState({ audioLevel: 0 });
  try { activeRecorder?.stop(); } catch { /* already stopped */ }
  activeStream?.getTracks().forEach((t) => t.stop());
  activeRecorder = null;
  activeStream = null;
}

/**
 * δ.8 — idempotent per-turn teardown (R2 C8 fix).
 *
 * Wired to `VoiceTurnSocket.onClose` callback so it runs on EVERY close
 * path (normal end_of_turn 1000, abnormal 1006, protocol-violation
 * 1002, etc.) — DESIGN §5.2 R2.3 defends against iOS Safari's per-page
 * AudioContext cap (~6). Without idempotency + null-guards, two
 * sequential close events (onError → onClose) would call
 * `AudioContext.close()` twice and the second throws DOMException.
 *
 * Clears module state FIRST so concurrent re-entry sees nulls and exits
 * via the early-return guard. Does NOT touch the VAD `activeAudioContext`
 * (R3.2 — only `activeTurnAudioContext` is the playback context).
 */
function teardownTurn(): void {
  // δ″ worldview R1 Concern #1 — include turnLevelPollHandle in idempotency guard.
  if (!activeTurnAudioContext && !activeTurnSocket && !activeTurnAudioQueue && !turnLevelPollHandle) {
    return;
  }
  // δ″ F2 cleanup — clear the streaming-mode mic-level poll BEFORE closing the
  // AudioContext (analyser is wired to it; clearing avoids one tick where the
  // poll fires against a torn-down context).
  if (turnLevelPollHandle) {
    clearInterval(turnLevelPollHandle);
    turnLevelPollHandle = null;
  }
  const ctx = activeTurnAudioContext;
  const sock = activeTurnSocket;
  const queue = activeTurnAudioQueue;
  activeTurnAudioContext = null;
  activeTurnSocket = null;
  activeTurnAudioQueue = null;
  try {
    queue?.close?.();
  } catch {
    /* already closed */
  }
  try {
    sock?.close?.();
  } catch {
    /* already closed */
  }
  try {
    const closePromise = ctx?.close?.();
    if (closePromise && typeof closePromise.then === 'function') {
      closePromise.catch(() => {
        /* already closed */
      });
    }
  } catch {
    /* sync throw on already-closed */
  }
}

/**
 * δ″ F1 — extracted out of startTurn's end_of_turn handler so the rejection
 * path can be unit-tested with a faked queue (Plan R1 Concern N1 / Path C).
 *
 * Owns the markSentenceComplete(lastSeq) + drainAndWait() sequence for the
 * final sentence of a streaming turn. On success, opts.onSuccess() runs.
 * On rejection from EITHER markSentenceComplete OR drainAndWait, opts.onError(err)
 * runs. The caller is responsible for teardown via these callbacks — Rule #19:
 * no silent rejection leak.
 */
export async function finalizeTurn(
  queue: VoiceAudioQueue,
  lastSeq: number | null,
  opts: { onSuccess: () => void; onError: (err: unknown) => void },
): Promise<void> {
  try {
    if (lastSeq !== null) {
      await queue.markSentenceComplete(lastSeq);
    }
    await queue.drainAndWait();
    opts.onSuccess();
  } catch (err) {
    opts.onError(err);
  }
}

/**
 * δ⁗ (2026-05-17) — per-turn streaming cleanup that PRESERVES the persistent
 * WS + AudioContext + VoiceAudioQueue across turns. Called on end_of_turn
 * (success + rejection paths).
 *
 * Mirrors classic's `teardownTurnResources()` at line 702 but for streaming
 * state. Distinct from `teardownTurn()` (full shutdown — used for mode
 * switch, WS death, page unload).
 *
 * Resets: turnLevelPollHandle (F2 analyser), activeRecorder + activeStream
 * (mic), audioLevel store field, VoiceAudioQueue per-turn coordinator state
 * via resetForNextTurn(), currentStreamingTurnState.
 *
 * Preserves: activeTurnSocket, activeTurnAudioContext, activeTurnAudioQueue
 * (instance kept alive — its Maps are reset by resetForNextTurn).
 */
function teardownStreamingTurnResources(): void {
  if (turnLevelPollHandle) {
    clearInterval(turnLevelPollHandle);
    turnLevelPollHandle = null;
  }
  try { activeRecorder?.stop(); } catch { /* already stopped */ }
  activeStream?.getTracks().forEach((t) => t.stop());
  activeRecorder = null;
  activeStream = null;
  useVoiceStore.setState({ audioLevel: 0 });
  // Reset queue's per-turn coordinator state (Maps + nextSeq + lastScheduledSource
  // + _drainResolve). Does NOT close the queue or AudioContext.
  activeTurnAudioQueue?.resetForNextTurn();
  // Per-turn streaming state — clears the closure-extracted fields.
  resetStreamingTurnState();
}

/**
 * δ⁗ — open the persistent streaming WebSocket. Mirrors `openPersistentWS()`
 * at line 832 for the classic /api/voice/session endpoint, but targets
 * /api/voice/turn and uses VoiceTurnSocket (not raw WebSocket).
 *
 * MUST run inside a user-gesture (preserves iOS gesture-context for the
 * AudioContext construction below). Caller (startTurn) holds the gesture.
 *
 * Idempotent: if activeTurnSocket is non-null AND not CLOSED, it's a no-op.
 * Creates activeTurnAudioContext ONCE per session — reused across turns.
 *
 * The onText handler delegates to `handleStreamingFrame` (module-level)
 * which mutates `currentStreamingTurnState` + module-level activeTurn* refs.
 * The onClose handler implements the W escape-hatch + auto-reconnect with
 * exponential backoff (matches classic PWS).
 */
function openPersistentStreamingWS(): void {
  if (typeof window === 'undefined') return;
  if (activeTurnSocket && activeTurnSocket.readyState !== WebSocket.CLOSED) return;
  if (streamingReconnectHandle) {
    clearTimeout(streamingReconnectHandle);
    streamingReconnectHandle = null;
  }
  if (typeof AudioContext === 'undefined') {
    useVoiceStore.setState({ status: 'error', error: 'AudioContext unavailable' });
    return;
  }
  if (!activeTurnAudioContext) {
    activeTurnAudioContext = new AudioContext();
  }
  // δ⁗ post-G7 — capture resolve/reject in LOCAL closure so this socket's
  // onOpen/onClose can only settle this socket's Promise, never a later one.
  let localResolve: (() => void) | null = null;
  let localReject: ((err: Error) => void) | null = null;
  streamingWSOpenPromise = new Promise<void>((res, rej) => {
    localResolve = res;
    localReject = rej;
  });
  // Suppress unhandled-rejection warning if no one awaits this Promise
  // (e.g., during a fast teardown). Awaiters still see rejections via await.
  streamingWSOpenPromise.catch(() => { /* handled by awaiters */ });
  const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${wsScheme}//${window.location.host}/api/voice/turn`;
  const socket: VoiceTurnSocket = new VoiceTurnSocket({
    url: wsUrl,
    onOpen: () => {
      // D1-1 — auth frame FIRST. activeTurnSocket is assigned (below) before
      // this async onOpen fires; same pattern as the keepalive send. The
      // open-promise resolved here gates the config send, so auth always
      // precedes config.
      const authFrame = voiceAuthFrame();
      if (authFrame) activeTurnSocket?.send(authFrame);
      streamingReconnectAttempt = 0;
      // Non-sticky banner: a successful (re)open is a clean slate — clear ANY
      // prior error banner on this tab (an auth rejection, stt_failed, or
      // pipeline error from a previous connection epoch is stale once we are
      // reconnected). Unconditional by design; pairs with the `unauthorized`
      // non-sticky handling in handleStreamingFrame's case 'error'. 2026-06-25.
      _setActiveTabFields({ error: null });
      // Phase 9.6 (2026-05-24) — D-9.6-5 reset protocol_version flag on every
      // fresh WS connection. The `ready` frame handler (case 'ready') sets it
      // back to true if backend emits protocol_version='v19.1'. Without this
      // reset, a reconnect to an OLDER backend after talking to a NEWER one
      // would keep the flag true and the SPA would try to accumulate deltas
      // from a backend that doesn't carry the expected shape.
      protocolVersionV19_1 = false;
      // Phase 9.7 (2026-05-24) — same reset for the v19.2 cancel-capability flag.
      protocolVersionV19_2 = false;
      // δ⁗ post-G7 — resolve local capture; survives module-level overwrite
      // by a subsequent openPersistentStreamingWS call.
      if (localResolve) {
        const r = localResolve;
        localResolve = null;
        localReject = null;
        r();
      }
      if (streamingKeepaliveHandle) clearInterval(streamingKeepaliveHandle);
      streamingKeepaliveHandle = setInterval(() => {
        if (activeTurnSocket?.readyState === WebSocket.OPEN) {
          try {
            activeTurnSocket.send({ type: 'keepalive' });
          } catch {
            /* next onclose handler will trigger reconnect */
          }
        }
      }, PWS_KEEPALIVE_INTERVAL_MS);
    },
    onText: (frame) => handleStreamingFrame(frame),
    onBinary: (data) => {
      if (activeTurnAudioQueue) void activeTurnAudioQueue.enqueue(data);
    },
    onError: (ev) => {
      // R2 C8 — do NOT call teardown from here; the subsequent onclose does.
      console.warn('[voiceStore] streaming ws.onerror:', ev);
    },
    onClose: (code) => {
      if (streamingKeepaliveHandle) {
        clearInterval(streamingKeepaliveHandle);
        streamingKeepaliveHandle = null;
      }
      activeTurnSocket = null;
      // δ⁗ post-G7 — reject LOCAL capture so a stale onclose can never
      // erroneously reject a fresh socket's Promise (the previous module-level
      // pattern had this exact bug: opening a 2nd socket overwrote the
      // module-level reject; the 1st socket's onclose then rejected the 2nd
      // socket's Promise).
      if (localReject) {
        const rej = localReject;
        localResolve = null;
        localReject = null;
        rej(new Error(`streaming ws closed before open (code=${code})`));
        // δ⁗ post-G7 Sonnet R1 Bug C fix — MUST return after rejecting the
        // local Promise. Browser WS close is async-queued; this onClose can
        // fire AFTER catch path's teardownStreamingSession AND user retry.
        // Falling through hits the status check below, which sees socket B's
        // 'connecting' state and erroneously sets error + teardown, killing
        // the legitimate retry turn. The Promise rejection IS the entirety
        // of this old socket's outstanding responsibility — anything beyond
        // applies to the new socket B and is wrong.
        return;
      }
      // δ″ F1 — if finalize owns teardown, log + return (no error toast).
      if (currentStreamingTurnState.finalizeStarted) {
        console.warn(`[voiceStore] streaming ws closed normally (code=${code}); finalize owns teardown`);
        return;
      }
      const s = useVoiceStore.getState().status;
      if (s === 'connecting' || s === 'recording' || s === 'transcribing') {
        useVoiceStore.setState({
          status: 'error',
          error: `streaming ws closed unexpectedly (code=${code})`,
        });
        teardownStreamingTurnResources();
        return; // no auto-reconnect mid-turn
      }
      // Auth rejection (1008 policy violation). 2026-06-29 — fail CLOSED only
      // when there is genuinely no token (logout / expired session): retrying
      // would loop against the gate with a dead token, and the auth layer
      // (AuthProvider validity-gate + api.ts handle401StaleToken → /login) owns
      // recovery. But when a valid token IS present, the 1008 was the module-
      // init boot race (WS opened before the OIDC token hydrated) — reconnect
      // so the reopen sends the now-available auth frame, mirroring the classic
      // WS which auto-reconnects unconditionally. See
      // streamingPolicyCloseShouldReconnect + voiceStore.authReject.test.ts.
      if (code === WS_CLOSE_POLICY_VIOLATION && !streamingPolicyCloseShouldReconnect()) {
        console.warn('[voiceStore] streaming ws auth-rejected (1008), no token — not reconnecting; auth layer owns redirect');
        return;
      }
      if (code === WS_CLOSE_POLICY_VIOLATION) {
        console.warn('[voiceStore] streaming ws auth-rejected (1008) but token present — transient boot race; reconnecting');
        // fall through to the idle auto-reconnect backoff below.
      }
      // Idle close — auto-reconnect with exponential backoff (matches classic).
      const delay = PWS_RECONNECT_BACKOFF_MS[
        Math.min(streamingReconnectAttempt, PWS_RECONNECT_BACKOFF_MS.length - 1)
      ];
      streamingReconnectAttempt += 1;
      streamingReconnectHandle = setTimeout(() => {
        openPersistentStreamingWS();
      }, delay);
    },
  });
  activeTurnSocket = socket;
  // Create the audio queue once per session.
  if (!activeTurnAudioQueue) {
    // W3 (2026-05-25) — D-W3-8 LOCKED. Pass settings.audioVolume so the
    // queue's gainNode is initialized at the user's persisted volume.
    // Source is the active tab's persisted settings (fallback to top-level
    // mirror for first-load).
    const tabIdForVolume = useVoiceStore.getState().activeTabId;
    const tabForVolume = tabIdForVolume ? useVoiceStore.getState().byTabId[tabIdForVolume] : null;
    const initialVolume = (tabForVolume?.settings.audioVolume
      ?? useVoiceStore.getState().settings.audioVolume
      ?? DEFAULT_SETTINGS.audioVolume);
    activeTurnAudioQueue = new VoiceAudioQueue(
      activeTurnAudioContext,
      // δ⁗ post-G8 — ACK callback is a NO-OP under PWS. ACKs were for the
      // old per-chunk backpressure (MAX_INFLIGHT_CHUNKS=3). Since BE 1.146.0
      // raised the gate to 1000 (effectively disabling per-chunk gating per
      // δ′ per-sentence ACK semantics), ACKs are pointless. Worse, they
      // become PROTOCOL VIOLATIONS under PWS: ACKs buffered during pipeline
      // emission for turn N can land at the outer-loop's first-frame slot
      // (where server expects config/keepalive/close_session) for turn N+1
      // → server emits protocol_error + closes WS → user sees toast +
      // each turn opens a new WS (defeating PWS persistence).
      () => { /* no-op — see comment above */ },
      initialVolume,
    );
    activeTurnAudioQueue.resetForNextTurn();
  }
}

/**
 * δ⁗ — extracted module-level handler for streaming WS text frames.
 * Was inline inside startTurn's VoiceTurnSocket onText callback (1.151.0).
 * Lifted out because under PWS the WS callbacks are bound once at WS open
 * and must serve many turns; closure variables from startTurn() can't work.
 *
 * Reads + mutates `currentStreamingTurnState` + module-level activeTurn* refs.
 * The `ready` case calls `activeRecorder.start(100)` — Plan R1 Concern C6:
 * startTurn() ensures `activeRecorder` is set BEFORE the config frame is sent,
 * so this is non-null when `ready` arrives.
 */
function handleStreamingFrame(frame: Record<string, unknown>): void {
  const type = frame.type as string;
  switch (type) {
    // WS-A Φ2 — synthetic frame name used by VS.22; sets per-tab status to
    // 'recording' WITHOUT the recorder.start side-effect (recorder is owned
    // by 'ready'). Production server emits 'ready' for the real path; test
    // fixture uses 'recording_started' to assert status-write routing alone.
    case 'recording_started': {
      _setActiveTabFields({ status: 'recording' });
      break;
    }
    case 'ready': {
      // Phase 9.6 (2026-05-24) — D-9.6-5 protocol_version detection. Missing
      // field = pre-v19 backend → compat mode (deltas ignored, no overlay).
      // Present "v19.1" = enable streaming-delta accumulator + XML filter +
      // tool_progress reset + conditional commit path.
      // Phase 9.7 (2026-05-24) — v19.2 SUPERSEDES v19.1. Sets BOTH flags true.
      // Adds cancel-turn capability (stop button visible) + cancelled-frame
      // handling. Pre-9.7 frontends seeing v19.2 fall back to v19.1 paths
      // (no cancel button); pre-9.6 frontends seeing v19.2 ignore the field.
      const protoVersion = (frame as { protocol_version?: string }).protocol_version;
      protocolVersionV19_1 = protoVersion === 'v19.1' || protoVersion === 'v19.2';
      protocolVersionV19_2 = protoVersion === 'v19.2';
      try {
        if (activeRecorder && activeRecorder.state !== 'recording') {
          activeRecorder.start(100);
        }
        _setActiveTabFields({ status: 'recording' });
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e);
        _setActiveTabFields({ status: 'error', error: `recorder.start failed: ${errMsg}` });
        teardownStreamingTurnResources();
      }
      break;
    }
    case 'keepalive_ack':
      // No state change — liveness ping ack.
      break;
    case 'transcript': {
      const text = (frame.text as string) || '';
      const echoedTurnId = (frame as { turn_id?: string }).turn_id;
      const transcriptText = text.trim();
      const activeTabId = useVoiceStore.getState().activeTabId;
      const userTurn: VoiceTurn[] = transcriptText
        ? [{ role: 'user' as const, text: transcriptText, timestamp: Date.now() }]
        : [];
      if (activeTabId) {
        const tab = useVoiceStore.getState().byTabId[activeTabId];
        if (tab) {
          _setTabFields(activeTabId, {
            transcript: text,
            status: 'transcribing',
            currentTurnId: echoedTurnId || tab.currentTurnId,
            history: [...tab.history, ...userTurn].slice(-HISTORY_MAX_TURNS),
          });
        }
      } else {
        // Pre-hydrate fast path — write to top-level mirror only.
        useVoiceStore.setState((s) => ({
          transcript: text,
          status: 'transcribing',
          currentTurnId: echoedTurnId || s.currentTurnId,
          history: [...s.history, ...userTurn].slice(-HISTORY_MAX_TURNS),
        } as Partial<VoiceState>));
      }
      break;
    }
    case 'delta': {
      // Phase 9.6 (2026-05-24) — D-9.6-3 delta-accumulator + D-9.6-2 XML filter.
      // Pre-9.6 this case was: `// Visible-reply streaming is captured via
      // chunk_meta.text accumulation.` (committed history STILL sources from
      // chunk_meta per D-9.6-4; this overlay is transient + parallel).
      if (!protocolVersionV19_1) break;  // D-9.6-5 compat: pre-v19 backends ignored
      const deltaText = (frame as { text?: string }).text;
      if (typeof deltaText !== 'string' || deltaText.length === 0) break;
      const filtered = _applyToolCallFilter(deltaText);
      if (filtered.length === 0) break;
      currentStreamingTurnState.streamingDeltaText += filtered;
      // Defensive cap — runaway guard. Real voice replies <500 chars typically.
      if (currentStreamingTurnState.streamingDeltaText.length > STREAMING_REPLY_MAX_LENGTH_CHARS) {
        currentStreamingTurnState.streamingDeltaText =
          currentStreamingTurnState.streamingDeltaText.slice(-STREAMING_REPLY_MAX_LENGTH_CHARS);
      }
      const activeTabIdForDelta = useVoiceStore.getState().activeTabId;
      if (activeTabIdForDelta) {
        _setTabFields(activeTabIdForDelta, {
          streamingReplyText: currentStreamingTurnState.streamingDeltaText,
        });
      }
      break;
    }
    case 'chunk_meta': {
      const seq = frame.seq as number | undefined;
      if (typeof seq !== 'number') break;
      const sentenceText = frame.text as string | undefined;
      if (typeof sentenceText === 'string' && sentenceText.length > 0) {
        currentStreamingTurnState.accumulatedReply += sentenceText;
      }
      // δ′ R1 C1 — chunk_meta(seq=N) flushes sentence N-1.
      if (
        currentStreamingTurnState.lastSeq !== null
        && seq > 0
        && activeTurnAudioQueue
      ) {
        void activeTurnAudioQueue.markSentenceComplete(currentStreamingTurnState.lastSeq);
      }
      currentStreamingTurnState.lastSeq = seq;
      break;
    }
    case 'tool_call':
      break;
    case 'tool_progress': {
      // Phase 9.3 (2026-05-24) — append a tool_progress entry to the
      // bounded per-tab strip. CONC-9.3-1 extensible schema: silently
      // drop malformed frames (unknown status, missing tool, etc.).
      // Single-getState destructure per R1.1 C3 (avoid race window).
      const tool = (frame as { tool?: unknown }).tool;
      const step = (frame as { step?: unknown }).step;
      const status = (frame as { status?: unknown }).status;
      const error = (frame as { error?: string | null }).error ?? null;
      if (
        typeof tool !== 'string'
        || typeof step !== 'number'
        || (status !== 'starting' && status !== 'completed' && status !== 'failed')
      ) {
        // CONC-9.3-1: schema guard. Silently drop — extensibility means
        // unknown/malformed shapes must not crash the handler.
        break;
      }
      const entry: ToolProgressEntry = {
        tool,
        step,
        status,
        error,
        timestamp: Date.now(),
      };
      const { activeTabId, byTabId } = useVoiceStore.getState();
      // Phase 9.6 (2026-05-24) — D-9.6-3 load-bearing reset. When a tool is
      // about to fire (starting), the deltas accumulated in
      // streamingDeltaText were PRE-TOOL narration (e.g., "Looking that
      // up..."). The user shouldn't see those chars committed; the
      // committed history comes from chunk_meta at the TERMINAL step
      // (Option C re-feed). Clear the overlay so the next step's deltas
      // (or terminal reply) start fresh.
      if (status === 'starting' && protocolVersionV19_1) {
        currentStreamingTurnState.streamingDeltaText = '';
        currentStreamingTurnState.inToolCallMode = false;
        currentStreamingTurnState.toolCallBuffer = '';
        if (activeTabId) {
          _setTabFields(activeTabId, { streamingReplyText: '' });
        }
      }
      if (activeTabId) {
        const prev = byTabId[activeTabId]?.currentToolProgress ?? [];
        // Bounded to 10 most recent entries — see v17 §5.2.
        _setTabFields(activeTabId, {
          currentToolProgress: [...prev, entry].slice(-10),
        });
      }
      break;
    }
    case 'tool_result': {
      // Phase 9.8 (2026-05-24) — D-9.8 LOCKED. Single-fire frame per tool
      // invocation. Distinct from tool_progress (which fires multiple times
      // per tool). On valid frame: push a VoiceTurn{role:'tool',tool:{...}}
      // to history via _appendTabHistory.
      //
      // D-9.8-8 LOCKED — turn_id guard: drop frames whose turn_id doesn't
      // match the current turn (defensive against late-arrival frames from
      // a cancelled previous turn).
      //
      // Schema-guard pattern from Phase 9.3 (CONC-9.3-1): malformed frames
      // are silently dropped — never crash the handler on unknown shape.
      const tool = (frame as { tool?: unknown }).tool;
      if (typeof tool !== 'object' || tool === null) break;
      const t = tool as Record<string, unknown>;
      if (typeof t.name !== 'string') break;
      const frameTurnId = (frame as { turn_id?: unknown }).turn_id;
      // turn_id guard (D-9.8-8): if a current turn id is tracked on the
      // active tab, the frame must match. Read per-tab currentTurnId (not
      // top-level) — frame-tab scoping pattern from transcript handler at
      // L1888-1897. If no current turn (test-injection path doesn't always
      // set one), accept the frame.
      const stateForTurnGuard = useVoiceStore.getState();
      const activeTabForGuard = stateForTurnGuard.activeTabId
        ? stateForTurnGuard.byTabId[stateForTurnGuard.activeTabId]
        : null;
      const activeTabTurnId = activeTabForGuard?.currentTurnId ?? null;
      if (
        activeTabTurnId !== null
        && typeof frameTurnId === 'string'
        && frameTurnId !== activeTabTurnId
      ) {
        break;
      }
      const name = t.name;
      const resultSummary = typeof t.result_summary === 'string' ? t.result_summary : '';
      const error = typeof t.error === 'string' ? t.error : null;
      const argsSummary = typeof t.args_summary === 'string' ? t.args_summary : undefined;
      const durationMs = typeof t.duration_ms === 'number' ? t.duration_ms : null;
      const step = typeof t.step === 'number' ? t.step : undefined;
      // text field = chip label — populated defensively (R1.0 OPEN-C LOCKED).
      let chipLabel: string;
      if (error) {
        chipLabel = `${name} → error`;
      } else if (resultSummary) {
        chipLabel = `${name} → ${resultSummary}`;
      } else {
        chipLabel = `${name} ran`;
      }
      const toolTurn: VoiceTurn = {
        role: 'tool',
        text: chipLabel,
        timestamp: Date.now(),
        tool: {
          name,
          args_summary: argsSummary,
          result_summary: resultSummary || undefined,
          error,
          duration_ms: durationMs,
          step,
        },
      };
      const activeTabIdForToolResult = useVoiceStore.getState().activeTabId;
      if (activeTabIdForToolResult) {
        _appendTabHistory(activeTabIdForToolResult, toolTurn);
      } else {
        useVoiceStore.setState((s) => ({
          history: [...s.history, toolTurn].slice(-HISTORY_MAX_TURNS),
        } as Partial<VoiceState>));
      }
      break;
    }
    case 'prefix_routed': {
      // 1.162.2 — backend intercepted the voice turn before LLM ran.
      // SPA pushes synthetic bridge turn + (optionally) fires CustomEvent
      // routing the stripped text to the active Pi chat tab. Per R3 Δ23:
      // user turn was ALREADY pushed by the `transcript` case above at
      // L1184-1198. Do NOT push it again here.
      const stripped = (frame.stripped as string) || '';
      const activeTabId = useVoiceStore.getState().activeTabId;
      if (!activeTabId) break;
      if (stripped.length === 0) {
        const fb: VoiceTurn = {
          role: 'bridge',
          text: "Pi chat prefix detected but no message text. Say 'pi chat, [your request]'",
          timestamp: Date.now(),
        };
        _appendTabHistory(activeTabId, fb);
        _setTabFields(activeTabId, { status: 'idle' });
      } else {
        window.dispatchEvent(
          new CustomEvent('vibe:send-to-pi-chat', { detail: { text: stripped } }),
        );
        const fb: VoiceTurn = {
          role: 'bridge',
          text: '[forwarded to Pi chat]',
          timestamp: Date.now(),
        };
        _appendTabHistory(activeTabId, fb);
        _setTabFields(activeTabId, { status: 'idle' });
      }
      // Leave the streaming state machine clean for the NEXT turn — no
      // LLM/TTS ran, but the per-turn streaming resources were allocated
      // by `transcript`-frame processing earlier in this turn.
      teardownStreamingTurnResources();
      break;
    }
    case 'cancelled': {
      // Phase 9.7 (2026-05-24) — D-9.7 cancelled-frame handler. Backend emits
      // this after the cancel_event fires in the pipeline + drain completes.
      // D-9.7-2: tool calls 0..N-1 STAY APPLIED — append a history affordance
      // showing what fired before cancel landed. D-9.7-4: WS stays open;
      // status returns to 'idle' for next turn.
      if (!protocolVersionV19_2) break;  // compat: pre-9.7 backends never send this
      const activeTabIdForCancel = useVoiceStore.getState().activeTabId;
      // Append a cancelled marker to history BEFORE teardown so the user
      // sees what fired before cancel landed.
      // Worldview D-9.7-2 + R1.0 OPEN-G handling: lastToolFired gates the
      // step-count branch (when no tool fired, completedSteps is irrelevant
      // and we show "[cancelled — no tools fired]" instead).
      const completedSteps = (frame as { completedSteps?: number }).completedSteps ?? -1;
      const lastToolFired = (frame as { lastToolFired?: string | null }).lastToolFired ?? null;
      const cancelledTurn: VoiceTurn = {
        role: 'bridge',
        text: lastToolFired
          ? `[cancelled — ${completedSteps + 1} step(s) completed; last: ${lastToolFired}]`
          : `[cancelled — no tools fired]`,
        timestamp: Date.now(),
      };
      if (activeTabIdForCancel) {
        _appendTabHistory(activeTabIdForCancel, cancelledTurn);
        _setTabFields(activeTabIdForCancel, {
          status: 'idle',
          streamingReplyText: '',
          currentToolProgress: null,
        });
      }
      // R2 C-1 fix: call teardownStreamingTurnResources() to ALSO clear
      // turnLevelPollHandle + accumulatedReply + lastSeq + finalizeStarted
      // (was leaking to next turn). Without this, end_of_turn on turn N+1
      // reads stale accumulatedReply from turn N. teardownStreamingTurnResources
      // also runs activeTurnAudioQueue.resetForNextTurn() — but the cancelled
      // path REQUIRES the more aggressive abort() (which preserves AudioContext
      // AND signals in-flight drainAndWait), so call abort() FIRST.
      if (activeTurnAudioQueue) {
        activeTurnAudioQueue.abort();
      }
      teardownStreamingTurnResources();
      break;
    }
    case 'end_of_turn': {
      currentStreamingTurnState.finalizeStarted = true;
      // δ″ F3 — bridge reply push (guarded for tool-call-only path).
      const activeTabIdForEot = useVoiceStore.getState().activeTabId;
      // Phase 9.6 (2026-05-24) — D-9.6-6: HOIST `action` destructure from
      // post-commit (was at L2006-2007) to BEFORE the commit block so the
      // commit decision can read it. v1 NOT-WIRED lock: the streamingReplyText
      // fallback commit when accumulatedReply is empty AND action_taken null
      // is DEFERRED (worldview §4.2 D-9.6-6 acceptable degraded UX — user
      // sees "text appears then disappears" on malformed-XML-suppressed
      // turns; v18 silence-on-malformed-XML lock preserved). action is
      // read here but only USED by the existing tool-dispatch block below.
      // R2 C-4: `?? null` tightens type from `T | null | undefined` to `T | null`.
      const action = ((frame as { action_taken?: DispatcherActionTaken | null })
        .action_taken) ?? null;
      if (currentStreamingTurnState.accumulatedReply.trim().length > 0) {
        const bridgeTurn: VoiceTurn = {
          role: 'bridge',
          text: currentStreamingTurnState.accumulatedReply,
          timestamp: Date.now(),
        };
        if (activeTabIdForEot) {
          _appendTabHistory(activeTabIdForEot, bridgeTurn);
        } else {
          useVoiceStore.setState((s) => ({
            history: [...s.history, bridgeTurn].slice(-HISTORY_MAX_TURNS),
          } as Partial<VoiceState>));
        }
      }
      // 1.162.1 — handle action_taken in the streaming WS end_of_turn frame.
      // Pre-1.162.1 this was ignored; only the HTTP /voice/dispatch path
      // routed action_taken. Now both paths dispatch identically: streaming
      // (this case) + classic HTTP (dispatch() action at L2339-2370).
      // Phase 9.6: `action` destructure HOISTED above (was here at L2006-2007).
      if (action && !action.error) {
        if (action.tool === 'spawn_terminal'
            && ((action.result?.spawned_names as string[] | undefined)?.length ?? 0) > 0) {
          void usePaneStore.getState().refresh();
        } else if (action.tool === 'navigate') {
          const view = (action.result as { view?: string } | undefined)?.view;
          if (view === 'designer' || view === 'terminals') {
            useUIStore.getState().setViewMode(view as ViewMode);
          }
        } else if (action.tool === 'send_to_pi_chat') {
          const text = (action.result as { text?: string } | undefined)?.text;
          if (typeof text === 'string' && text.length > 0) {
            window.dispatchEvent(
              new CustomEvent('vibe:send-to-pi-chat', { detail: { text } }),
            );
          }
        } else if (action.tool === 'spa_action') {
          // Phase 9.0 Surface B — see HTTP path below for the matching
          // dispatcher. 2026-06-27: both paths now delegate to the shared
          // applyVoiceSpaAction helper (switch_view/focus_panel + the new
          // preview_file/build/deploy/undeploy IDE verbs).
          const result = action.result as
            | { action?: string; params?: Record<string, unknown> }
            | undefined;
          applyVoiceSpaAction(result?.action, result?.params ?? {});
        }
      }
      // 1.164.3 — route status:'idle' to the ORIGIN tab (activeTabIdForEot,
      // captured at L1702). finalizeTurn is async (drainAndWait + markSentence-
      // Complete). If the user switches tabs while drain is pending, a late
      // _setActiveTabFields would idle the WRONG tab and leave the origin tab
      // stuck on 'transcribing' (VS-ET1 RED test reproduces this).
      // If the origin tab was closed before finalize resolved, _setTabFields
      // is a no-op (tab missing from byTabId) — correct: the currently-active
      // tab's status is left undisturbed.
      const writeIdleToOriginTab = (): void => {
        // Phase 9.3 (CONC-9.3-3): clear currentToolProgress on every
        // end_of_turn finalize path (no-queue sync, onSuccess, onError).
        // Injected into the shared function per R1.1 lock so all 3 paths
        // are covered by one edit.
        // Phase 9.6 (2026-05-24) — R2 C-2 fix: also clear streamingReplyText
        // on every end_of_turn finalize. teardownStreamingTurnResources
        // already clears the module-level streamingDeltaText accumulator,
        // but per-tab streamingReplyText was leaking turn-N text into turn
        // N+1's status='transcribing' window (visible flicker until the
        // first delta of turn N+1 overwrites). T7 extended to assert this.
        if (activeTabIdForEot) {
          _setTabFields(activeTabIdForEot, { status: 'idle', currentToolProgress: null, streamingReplyText: '' });
        } else {
          // Pre-hydrate fallback (no per-tab state to write to).
          _setActiveTabFields({ status: 'idle', currentToolProgress: null, streamingReplyText: '' });
        }
      };
      if (!activeTurnAudioQueue) {
        // No queue — nothing to drain. Just tear down per-turn resources.
        teardownStreamingTurnResources();
        writeIdleToOriginTab();
        break;
      }
      void finalizeTurn(activeTurnAudioQueue, currentStreamingTurnState.lastSeq, {
        onSuccess: () => {
          teardownStreamingTurnResources();
          writeIdleToOriginTab();
        },
        onError: (err) => {
          console.error('[voiceStore] finalize() rejected:', err);
          teardownStreamingTurnResources();
          writeIdleToOriginTab();
        },
      });
      break;
    }
    case 'error': {
      // δ″.3 — surface server error codes to user (no_speech, no_audio, etc.).
      const fatal = frame.fatal === true;
      const code = (frame.code as string) || 'pipeline_error';
      const msg = (frame.message as string) || code;
      if (!fatal && code === 'backpressure_ack_timeout') {
        console.warn(`[voiceStore] non-fatal pipeline error: ${code}: ${msg}`);
        break;
      }
      // 1.164.3 — capture origin tab at case entry (defensive code-symmetry
      // with end_of_turn). The error case body is synchronous, so the race
      // window is narrower than end_of_turn's async finalize, but the same
      // pattern eliminates future regressions. Closed-tab silent-drop is
      // correct (see end_of_turn writeIdleToOriginTab rationale above).
      const activeTabIdForError = useVoiceStore.getState().activeTabId;
      // 2026-06-25 — auth-frame rejection (expired/absent token; backend
      // routes/voice.py:287 closes 1008 right after this frame) is NON-STICKY:
      // the auth layer owns recovery (the parallel REST 401 drives api.ts
      // handle401StaleToken → /login, and the AuthProvider validity-gate stops
      // the app ever mounting with a dead token). A sticky red voice banner
      // outlived the condition and confused users whose 8h session lapsed —
      // carry NO banner text for this code (still tear the turn down below).
      const isAuthReject = code === 'unauthorized';
      if (isAuthReject) {
        console.warn('[voiceStore] streaming ws auth rejected (unauthorized); auth layer owns redirect to /login');
      }
      const errorPatch: Partial<VoiceTabState> = {
        status: 'idle',
        error: isAuthReject ? null : `${code}: ${msg}`,
      };
      if (activeTabIdForError) {
        _setTabFields(activeTabIdForError, errorPatch);
      } else {
        _setActiveTabFields(errorPatch);
      }
      // 1.158.0 (worldview §2.6) — if finalizeStarted=true, EITHER (a)
      // end_of_turn arrived first and set the flag at L1061 BEFORE this
      // error frame (finalize is in flight — its onError/onSuccess at
      // L1082/L1087 will handle teardown), OR (b) a prior error frame
      // already set the flag AND called teardown below (no finalize in
      // flight, but teardown already happened). Both cases: skip teardown
      // here. Prevents concurrent-teardown race that resets pendingByseq
      // mid-finalize (FA6 secondary mechanism).
      if (currentStreamingTurnState.finalizeStarted) {
        console.warn(`[voiceStore] error frame arrived with finalizeStarted=true; skipping teardown (already handled)`);
        break;
      }
      // δ⁗ — under PWS, transient server errors (no_speech, no_audio, stt_*)
      // continue the outer loop on the server side. Client tears down PER-TURN
      // resources but keeps WS open for next click.
      teardownStreamingTurnResources();
      // Mark finalizeStarted so a near-simultaneous onClose (from a TERMINAL
      // server error that also closes) is suppressed.
      currentStreamingTurnState.finalizeStarted = true;
      break;
    }
    default:
      console.warn('[voiceStore] unknown text frame type:', type);
  }
}

/**
 * δ⁗ — exposed mode-switch teardown hook for VoicePanel's onChange. Closes
 * the persistent streaming WS + AudioContext + AudioQueue so the server-side
 * coroutine doesn't sit idle on the persistent WS until IDLE_TIMEOUT_SECONDS.
 */
function teardownStreamingSession(): void {
  if (streamingReconnectHandle) {
    clearTimeout(streamingReconnectHandle);
    streamingReconnectHandle = null;
  }
  if (streamingKeepaliveHandle) {
    clearInterval(streamingKeepaliveHandle);
    streamingKeepaliveHandle = null;
  }
  // δ⁗ post-G7 Sonnet R1 Concern 2 — null the open-Promise so a subsequent
  // startTurn that races ahead of the next openPersistentStreamingWS doesn't
  // accidentally await a stale rejected Promise (which would resolve-rejected
  // immediately and short-circuit the new turn).
  streamingWSOpenPromise = null;
  teardownTurn(); // full shutdown — closes activeTurnSocket/Context/Queue.
}

/**
 * PWS — full WS lifecycle teardown. Closes the persistent WebSocket, clears
 * keepalive + reconnect handles. Called only from fatal-error paths or
 * explicit shutdown (NOT per-turn transcript / error paths — those use
 * `teardownTurnResources()` which preserves the WS).
 */
function teardownPersistentWS(): void {
  if (pwsKeepaliveHandle) {
    clearInterval(pwsKeepaliveHandle);
    pwsKeepaliveHandle = null;
  }
  if (pwsReconnectHandle) {
    clearTimeout(pwsReconnectHandle);
    pwsReconnectHandle = null;
  }
  try { activeWS?.close(); } catch { /* already closed */ }
  activeWS = null;
}

/**
 * Back-compat shim. Pre-PWS callers used `teardownSession()` which closed
 * EVERYTHING. PWS splits that into turn-resources vs WS. Call sites:
 *   - ws.onmessage transcript path → teardownTurnResources (keeps WS alive)
 *   - ws.onmessage error path     → teardownTurnResources
 *   - ws.onerror                  → teardownTurnResources (WS will fire onclose itself)
 *   - MediaRecorder construction failure → teardownTurnResources
 * If any code path needs the OLD behavior, call both: teardownTurnResources()
 * + teardownPersistentWS(). The legacy name remains for grep-discoverability
 * but is now an alias for teardownTurnResources (the per-turn case).
 */
function teardownSession(): void {
  teardownTurnResources();
}

function buildWsUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/api/voice/session`;
}

/**
 * D1-1 (2026-06-09 audit) — send the mandatory first-frame auth payload on a
 * freshly-opened voice WS, BEFORE config/keepalive. No-op when no token is
 * stored (local dev / AUTH_ENABLED=false). Must be the first `.send` in every
 * onopen so the backend gate (voice.py _authenticate_ws) sees it first.
 */
function sendVoiceAuthFrame(ws: WebSocket): void {
  const frame = voiceAuthFrame();
  if (frame) {
    try {
      ws.send(JSON.stringify(frame));
    } catch {
      // send failed (rare race); onclose/reconnect handles recovery.
    }
  }
}

/**
 * PWS (2026-05-17) — open the persistent voice WebSocket.
 *
 * Called once at module init (after the store is defined, see bottom of file)
 * and again from ws.onclose's auto-reconnect path if the WS dies. Idempotent:
 * if activeWS is already non-null AND not CLOSED, it's a no-op.
 *
 * Handler bindings (the per-WS lifecycle handlers) are attached here. ws.onmessage
 * dispatches by frame type:
 *   - ready          → no state change (per-turn ack of config)
 *   - keepalive_ack  → no state change (proof of WS aliveness)
 *   - transcript     → set transcript + dispatch to LLM (existing post-φ flow,
 *                      reads turn_id from currentTurnTiming module-level state)
 *   - error          → set status='error' + teardownTurnResources (preserves WS)
 *
 * On unexpected close (code !== 1000) AND status === 'idle', schedules a
 * reconnect via exponential backoff. On close mid-recording, the existing
 * W (2026-05-16) escape-hatch sets status='error' (so user can click-to-retry).
 */
function openPersistentWS(): void {
  if (typeof window === 'undefined') return; // SSR safety
  if (activeWS && activeWS.readyState !== WebSocket.CLOSED) {
    // Already OPEN or CONNECTING. Idempotent.
    return;
  }
  // Clear any pending reconnect; we're opening now.
  if (pwsReconnectHandle) {
    clearTimeout(pwsReconnectHandle);
    pwsReconnectHandle = null;
  }
  const ws = new WebSocket(buildWsUrl());
  activeWS = ws;

  ws.onopen = () => {
    // D1-1 — auth frame FIRST, before keepalive or any turn config.
    sendVoiceAuthFrame(ws);
    pwsInitialized = true;
    pwsReconnectAttempt = 0;
    // Start keepalive interval. Backend echoes {type:"keepalive_ack"} per
    // PWS protocol (voice.py multi-turn loop, 2026-05-17).
    if (pwsKeepaliveHandle) clearInterval(pwsKeepaliveHandle);
    pwsKeepaliveHandle = setInterval(() => {
      if (activeWS?.readyState === WebSocket.OPEN) {
        try {
          activeWS.send(JSON.stringify({ type: 'keepalive' }));
        } catch {
          // send failed (rare); next onclose handler will trigger reconnect
        }
      }
    }, PWS_KEEPALIVE_INTERVAL_MS);
  };

  ws.onmessage = (ev) => {
    let payload: VoiceServerEvent | { type: 'keepalive_ack' };
    try {
      payload = JSON.parse(ev.data) as VoiceServerEvent | { type: 'keepalive_ack' };
    } catch {
      return;
    }
    if ((payload as { type: string }).type === 'keepalive_ack') {
      // Liveness ping; no state change.
      return;
    }
    // Delegate to the per-turn message handler. Inline here because hoisting
    // the existing per-turn ws.onmessage logic verbatim would be a multi-
    // hundred-line move; this thin wrapper preserves the existing per-turn
    // flow without duplicating the body.
    handlePerTurnWsMessage(payload as VoiceServerEvent);
  };

  ws.onerror = () => {
    // Per W (2026-05-16) escape-hatch: surface error only when a turn is
    // active. Idle WS errors are silent; the next reconnect attempt will
    // log if it fails.
    const s = useVoiceStore.getState().status;
    if (s === 'connecting' || s === 'recording' || s === 'transcribing') {
      useVoiceStore.setState({ status: 'error', error: 'voice ws error' });
      teardownTurnResources();
    }
  };

  ws.onclose = (ev) => {
    if (pwsKeepaliveHandle) {
      clearInterval(pwsKeepaliveHandle);
      pwsKeepaliveHandle = null;
    }
    activeWS = null;
    const s = useVoiceStore.getState().status;
    if (s === 'connecting' || s === 'recording' || s === 'transcribing') {
      // Mid-recording close — existing W escape-hatch. User can click-to-retry.
      useVoiceStore.setState({
        status: 'error',
        error: `ws closed unexpectedly (code=${ev.code})`,
      });
      teardownTurnResources();
      // Do NOT auto-reconnect mid-recording; recovery is via user click which
      // will trigger a new openPersistentWS via startRecording's fallback path.
      return;
    }
    // Idle WS death — auto-reconnect with exponential backoff.
    const idx = Math.min(pwsReconnectAttempt, PWS_RECONNECT_BACKOFF_MS.length - 1);
    const delay = PWS_RECONNECT_BACKOFF_MS[idx];
    pwsReconnectAttempt += 1;
    pwsReconnectHandle = setTimeout(() => {
      openPersistentWS();
    }, delay);
  };
}

/**
 * PWS — per-turn ws.onmessage logic, extracted out of startRecording so the
 * persistent WS can dispatch transcript / error frames across multiple turns
 * without re-binding the handler each turn. Reads `currentTurnTiming.turn_id`
 * from module-level state (set by startRecording at function-entry).
 *
 * This is the existing ws.onmessage body lifted from the legacy per-turn-WS
 * path; substantively unchanged except it now lives outside startRecording.
 */
function handlePerTurnWsMessage(payload: VoiceServerEvent): void {
  if (payload.type === 'transcript') {
    // Phase α — capture transcript-received timestamp.
    currentTurnTiming.t_transcript_rcvd = performance.now();
    const echoedTurnId = (payload as unknown as { turn_id?: string }).turn_id;
    const transcriptText = (payload.text || '').trim();
    // WS-A Φ2 + D-A12 — capture activeTabId BEFORE any async work so the
    // dispatch chain writes back to the ORIGINATING tab, not whatever tab
    // is active when the microtask resolves.
    const dispatchingTabId = useVoiceStore.getState().activeTabId;
    if (echoedTurnId) {
      currentTurnTiming.turn_id = echoedTurnId;
      _setActiveTabFields({ currentTurnId: echoedTurnId });
    }
    const userTurn: VoiceTurn[] = transcriptText
      ? [{ role: 'user' as const, text: transcriptText, timestamp: Date.now() }]
      : [];
    if (dispatchingTabId) {
      const tab = useVoiceStore.getState().byTabId[dispatchingTabId];
      if (tab) {
        _setTabFields(dispatchingTabId, {
          transcript: payload.text,
          status: 'idle',
          history: [...tab.history, ...userTurn].slice(-HISTORY_MAX_TURNS),
        });
      }
    } else {
      // Pre-hydrate fast path: write to top-level mirror only.
      useVoiceStore.setState((s) => ({
        transcript: payload.text,
        status: 'idle',
        history: [...s.history, ...userTurn].slice(-HISTORY_MAX_TURNS),
      } as Partial<VoiceState>));
    }
    teardownTurnResources();
    if (transcriptText) {
      // Read settings from the DISPATCHING tab for Pi-prefix + forward-trigger
      // detection. D-A10 voice/speed/engine lock is owned by dispatch() itself
      // (which captures lockedVoice/Speed/Engine at its own entry to avoid the
      // double-capture-with-different-state edge case).
      const dispatchingTab = dispatchingTabId
        ? useVoiceStore.getState().byTabId[dispatchingTabId]
        : null;
      const settingsNow = dispatchingTab?.settings ?? useVoiceStore.getState().settings;
      // Forward-trigger detection. If transcript contains the trigger phrase,
      // forward configured scope from PRIOR history (excluding the just-pushed
      // user turn).
      if (checkPiForwardTrigger(transcriptText, settingsNow.piForwardTriggerPhrase)) {
        const histAll = dispatchingTab
          ? useVoiceStore.getState().byTabId[dispatchingTabId!]?.history ?? []
          : useVoiceStore.getState().history;
        const histPrior = histAll.length > 0 ? histAll.slice(0, -1) : [];
        // Phase 9.8 — filter tool rows out of Pi-forward payload. Tool rows
        // are LLM-context affordances (cross-turn memory), NOT user-visible
        // content the user wants to forward to Pi chat.
        const histPriorForForward = histPrior.filter(
          (t): t is VoiceTurn & { role: 'user' | 'bridge' } => t.role !== 'tool',
        );
        const forwardPayload = buildForwardPayload(settingsNow.piForwardScope, histPriorForForward);
        if (forwardPayload.length > 0) {
          window.dispatchEvent(
            new CustomEvent('vibe:send-to-pi-chat', { detail: { text: forwardPayload } }),
          );
          const fb: VoiceTurn = {
            role: 'bridge',
            text: '[forwarded to Pi chat]',
            timestamp: Date.now(),
          };
          if (dispatchingTabId) _appendTabHistory(dispatchingTabId, fb);
          else useVoiceStore.setState((s) => ({
            history: [...s.history, fb].slice(-HISTORY_MAX_TURNS),
          } as Partial<VoiceState>));
        } else {
          const fb: VoiceTurn = {
            role: 'bridge',
            text: '[nothing to forward — no prior bridge reply in scope]',
            timestamp: Date.now(),
          };
          if (dispatchingTabId) _appendTabHistory(dispatchingTabId, fb);
          else useVoiceStore.setState((s) => ({
            history: [...s.history, fb].slice(-HISTORY_MAX_TURNS),
          } as Partial<VoiceState>));
        }
        return;
      }
      const piRoute = checkAndRoutePiPrefix(transcriptText, settingsNow.piPrefixPhrase);
      if (piRoute.routed) {
        if (piRoute.strippedText.length > 0) {
          window.dispatchEvent(
            new CustomEvent('vibe:send-to-pi-chat', { detail: { text: piRoute.strippedText } }),
          );
          const fb: VoiceTurn = {
            role: 'bridge',
            text: '[forwarded to Pi chat]',
            timestamp: Date.now(),
          };
          if (dispatchingTabId) _appendTabHistory(dispatchingTabId, fb);
          else useVoiceStore.setState((s) => ({
            history: [...s.history, fb].slice(-HISTORY_MAX_TURNS),
          } as Partial<VoiceState>));
        } else {
          const fb: VoiceTurn = {
            role: 'bridge',
            text: "Pi chat prefix detected but no message text. Say 'pi chat, [your request]'",
            timestamp: Date.now(),
          };
          if (dispatchingTabId) _appendTabHistory(dispatchingTabId, fb);
          else useVoiceStore.setState((s) => ({
            history: [...s.history, fb].slice(-HISTORY_MAX_TURNS),
          } as Partial<VoiceState>));
        }
        return;
      }
      // D-A10 + D-A12 — dispatch() now self-contains the bridge-turn push +
      // speak() chain (captures dispatchingTabId + locks voice/speed/engine
      // at its own entry). Fire-and-forget; do NOT chain a .then() here or
      // the bridge turn would be double-pushed.
      void useVoiceStore.getState().dispatch(transcriptText);
    }
  } else if (payload.type === 'error') {
    const code = (payload as { code?: string }).code || 'unknown';
    if (code === 'unauthorized') {
      // Auth-frame rejection (expired/absent token; backend routes/voice.py:287
      // closes 1008 right after this frame). NON-STICKY: the auth layer owns
      // recovery — the parallel REST 401 drives api.ts handle401StaleToken →
      // /login, and the AuthProvider validity-gate stops the app ever mounting
      // with a dead token. A sticky red voice banner outlived the condition
      // (never auto-cleared) and confused users whose 8h session simply lapsed.
      // Log + reset to idle WITHOUT setting the banner. 2026-06-25.
      console.warn('[voiceStore] streaming ws auth rejected (unauthorized); auth layer owns redirect to /login');
      _setActiveTabFields({ status: 'idle', error: null });
      teardownTurnResources();
    } else {
      const msg = (payload as { message?: string }).message
        ? `${code}: ${(payload as { message: string }).message}`
        : code;
      _setActiveTabFields({ status: 'error', error: msg });
      teardownTurnResources();
    }
  }
  // ready / keepalive_ack: no state change.
}

// PWS — test-only window getter for Playwright + integration tests to inspect
// activeWS lifecycle. NOT for production observability. Wrapped in a function
// so consumers always get the CURRENT value, not a stale closure.
if (typeof window !== 'undefined') {
  (window as unknown as { __activeWS: () => WebSocket | null }).__activeWS = () => activeWS;
}

export const useVoiceStore = create<VoiceState>((set, _get) => ({
  // WS-A Φ2 — byTabId is canonical; top-level fields are mirrors maintained
  // via _setActiveTabFields / _setTabFields for back-compat with 60+ legacy
  // consumers. Pre-hydrate init: byTabId is empty + activeTabId is null;
  // top-level mirrors carry the legacy single-store values (existing tests
  // beforeEach pattern).
  byTabId: {},
  activeTabId: null,

  status: 'idle',
  transcript: '',
  error: null,
  lastDispatch: null,
  history: [],
  audioLevel: 0,
  settings: getStoredSettings(),
  voices: [...VOICE_PRESETS],
  engines: [],
  models: [],
  lastTurnTiming: null,
  currentTurnId: null,
  currentTurnStart: null,
  currentToolProgress: null,  // Phase 9.3
  streamingReplyText: '',     // Phase 9.6 D-9.6-3 transient overlay mirror

  // WS-A Φ2 — hydrate per-tenant per-tab v2 settings from localStorage.
  // First-load migration from legacy SETTINGS_KEY into tab-0 settings.
  // Legacy key delete deferred to first successful v2 write (D-A2).
  //
  // 1.164.2 — every tab is "hot-loaded": its greeting text is appended to
  // history during hydrate via populateGreetingHistory (defined above hydrate's
  // host object). User-visible effect: switching to any tab on cold load shows
  // the configured greeting immediately, no need to click the mic first.
  // Audio playback remains gated to first activation per tab via the existing
  // _greetingFiredByTab Set inside fireInitialGreetingIfReady.
  async hydrate(tenantId, tabsForTenant, activeTabIdHint) {
    _activeTenantId = tenantId;
    const v2 = getStoredSettingsV2(tenantId);
    if (v2 && Object.keys(v2).length > 0) {
      const rawByTabId: Record<TabId, VoiceTabState> = {};
      for (const [tabId, settings] of Object.entries(v2)) {
        rawByTabId[tabId] = freshTabState(settings);
      }
      // 1.167.0 — restore persisted per-tab history from
      // `vibe_voice_history_v2_${tenantId}`. Tabs without a persisted entry
      // stay at freshTabState's history=[] (populateGreetingHistory below
      // then seeds the greeting for them). Orphan cleanup: tabs persisted
      // in history but no longer in v2 settings get dropped (will be
      // collected by the next setStoredHistoryV2 write via the subscriber's
      // diff path, which writes only the tabs currently in byTabId).
      const storedHistory = getStoredHistoryV2(tenantId);
      let orphanFound = false;
      for (const [tabId, turns] of Object.entries(storedHistory)) {
        if (rawByTabId[tabId]) {
          rawByTabId[tabId] = { ...rawByTabId[tabId], history: turns };
        } else {
          orphanFound = true;
        }
      }
      // Eagerly clean orphans so cross-session closes don't leave plaintext
      // transcripts behind until the next history-changing setState.
      if (orphanFound) {
        const cleaned: Record<TabId, VoiceTurn[]> = {};
        for (const [tabId, turns] of Object.entries(storedHistory)) {
          if (rawByTabId[tabId]) cleaned[tabId] = turns;
        }
        setStoredHistoryV2(tenantId, cleaned);
      }
      // 1.164.2 — pre-populate greeting text into each tab's history.
      const byTabId = populateGreetingHistory(rawByTabId);
      // 1.166.2 — honor persisted activeTabId from voiceTabsStore. The
      // caller (VoicePanel) passes voiceTabsStore.activeTabId after its
      // own hydrate so the user's last-active tab is the active tab AT
      // hydrate-time, not after a post-hydrate bridge resync. Pre-1.166.2
      // defaulted to tabsForTenant[0] which races the greeting fire:
      // a user click during the post-hydrate async window would fire
      // fireInitialGreetingIfReady('first-interaction') against Tab 1's
      // settings even when the persisted active tab was Tab N.
      const persistedActive =
        activeTabIdHint && byTabId[activeTabIdHint]
          ? activeTabIdHint
          : (tabsForTenant?.[0] ?? Object.keys(byTabId)[0]);
      set({ byTabId, activeTabId: persistedActive ?? null });
      if (persistedActive && byTabId[persistedActive]) {
        // Mirror active-tab settings to top level for legacy consumers.
        set({ settings: byTabId[persistedActive].settings });
        // 1.166.3 — also persist to legacy `vibe_voice_settings` so the
        // next reload's pre-hydrate fast path (getStoredSettings at module
        // init) sees this tab's settings, not whichever tab was last edited.
        setStoredSettings(byTabId[persistedActive].settings);
      }
      return;
    }
    // Legacy migration path. Read legacy SETTINGS_KEY; on present, seed
    // tab-0 with those settings. Mark legacy-delete-pending so a subsequent
    // setStoredSettingsV2 wipes the legacy key (D-A2).
    let legacy: VoiceSettings | null = null;
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (raw) legacy = coerceSettings(JSON.parse(raw));
    } catch { /* non-fatal */ }
    const firstTabId = tabsForTenant?.[0] ?? 'tab-0';
    const initialSettings = legacy ?? { ...DEFAULT_SETTINGS, voice: getStoredVoice() };
    const rawByTabId: Record<TabId, VoiceTabState> = {
      [firstTabId]: freshTabState(initialSettings),
    };
    // Pre-create any additional tabs the caller knows about (from voiceTabsStore).
    for (const tid of (tabsForTenant ?? []).slice(1)) {
      rawByTabId[tid] = freshTabState({ ...initialSettings });
    }
    if (legacy) _legacyDeletePending[tenantId] = true;
    // 1.164.2 — pre-populate greeting text into each tab's history (legacy path).
    const byTabId = populateGreetingHistory(rawByTabId);
    set({ byTabId, activeTabId: firstTabId, settings: initialSettings });
  },

  // WS-A Φ2 + WS-A.5 — swap active voice tab.
  // - No-op when same tab or unknown tabId.
  // - VAD AudioContext + streaming finalize teardown ALWAYS run (resource-
  //   safety floor — these leak AudioContext/MediaStreamTracks otherwise).
  // - WS-A.5 (1.164.0): three OUTGOING-tab toggles gate the teardown branches
  //   that affect user-observable behavior:
  //     * pauseAudioOnLoseFocus  → pause + rewind that tab's currently-playing
  //                                  audio (looked up in _playingAudioByTab)
  //     * stopRecorderOnLoseFocus → stop activeRecorder + release MediaStream
  //                                  tracks WHEN streaming finalize is in
  //                                  flight (the non-helper-managed path)
  //     * resetStatusOnLoseFocus  → flip outgoing tab back to 'idle'
  //   All three default false: tabs are independent surfaces; the audio,
  //   recording, and status of the leaving tab are preserved by default.
  //   This matches the user's 2026-05-19 directive: "i dont want the tabs
  //   to stop speaking if i aswitch — thats the reason we want multiple tabs."
  // - Top-level mirrors copied from new active tab.
  // - Greeting fire-once-per-tab is handled by voicePanel's useEffect.
  setActiveTab(tenantId, tabId) {
    const state = useVoiceStore.getState();
    if (state.activeTabId === tabId) return;
    // WS-A Φ6 R3 C-1 fix — auto-create byTabId entry when missing. This
    // happens when voiceTabsStore.newTab creates a uuid tab and the
    // VoicePanel bridge useEffect calls setActiveTab(new-uuid) before
    // voiceStore.byTabId knows about it. Inherit settings from the prior
    // active tab so the new tab carries the user's last-used voice/speed/
    // engine. Pre-hydrate fallback: top-level mirror settings.
    if (!state.byTabId[tabId]) {
      const inheritFrom = state.activeTabId
        ? state.byTabId[state.activeTabId]?.settings
        : null;
      const seedSettings = inheritFrom ?? state.settings;
      useVoiceStore.setState((s) => ({
        byTabId: { ...s.byTabId, [tabId]: freshTabState({ ...seedSettings }) },
      }));
    }

    // WS-A.5 — capture OUTGOING tab + settings BEFORE swap. The toggles on
    // the leaving tab govern its own teardown (per user directive).
    const outgoingTabId = state.activeTabId;
    const outgoingSettings = outgoingTabId ? state.byTabId[outgoingTabId]?.settings : null;

    // Classic-mode VAD cleanup — UNCONDITIONAL (resource-safety floor; leaks
    // AudioContext otherwise per R1 C1).
    if (activeAudioContext) {
      try { void activeAudioContext.close(); } catch { /* already closed — non-fatal */ }
      activeAudioContext = null;
      activeAnalyser = null;
    }
    if (silencePollHandle) {
      clearInterval(silencePollHandle);
      silencePollHandle = null;
    }
    // Streaming mid-turn: defer to the existing helper UNCONDITIONALLY when
    // finalize is not in flight (resource-safety floor — helper releases
    // recorder + MediaStreamTracks per R1 C1). The else-branch below gates
    // ONLY the manual finalize-in-flight recorder.stop() path under
    // stopRecorderOnLoseFocus.
    if (!currentStreamingTurnState.finalizeStarted) {
      teardownStreamingTurnResources();
    } else if (activeRecorder && outgoingSettings?.stopRecorderOnLoseFocus) {
      // WS-A.5 R1 C1 — gate only the finalize-in-flight manual recorder stop
      // branch. Default off: recorder + tracks persist on the leaving tab.
      try { activeRecorder.stop(); } catch { /* already stopped — non-fatal */ }
      activeRecorder = null;
      if (activeStream) {
        activeStream.getTracks().forEach((t) => t.stop());
        activeStream = null;
      }
    }
    // WS-A.5 — pauseAudioOnLoseFocus: pause + rewind the OUTGOING tab's
    // currently-playing audio looked up in _playingAudioByTab. The audio
    // ref is NEVER recreated here (matches the prior D-A8 invariant).
    if (outgoingSettings?.pauseAudioOnLoseFocus && outgoingTabId) {
      const a = _playingAudioByTab.get(outgoingTabId);
      if (a && !a.paused) {
        try {
          a.pause();
          a.currentTime = 0;
        } catch { /* non-fatal — audio may already be detached */ }
      }
    }
    // WS-A.5 — resetStatusOnLoseFocus: flip outgoing tab back to idle +
    // clear transient state (transcript, error, currentTurnId, currentTurnStart).
    if (outgoingSettings?.resetStatusOnLoseFocus && outgoingTabId) {
      _setTabFields(outgoingTabId, {
        status: 'idle',
        transcript: '',
        error: null,
        currentTurnId: null,
        currentTurnStart: null,
      });
    }

    // WS-A Φ6 R3 C-1 fix — re-read state after the possible auto-create
    // setState above so newTab points at the freshly-seeded entry.
    const newTab = useVoiceStore.getState().byTabId[tabId];
    if (!newTab) return; // defensive — should be unreachable after auto-create.
    set({
      activeTabId: tabId,
      // Mirror per-tab fields to top level for legacy consumers.
      status: newTab.status,
      transcript: newTab.transcript,
      error: newTab.error,
      lastDispatch: newTab.lastDispatch,
      history: newTab.history,
      settings: newTab.settings,
      currentTurnId: newTab.currentTurnId,
      currentTurnStart: newTab.currentTurnStart,
      lastTurnTiming: newTab.lastTurnTiming,
    });
    // 1.166.3 — persist the new active tab's settings to the legacy
    // `vibe_voice_settings` localStorage key. Pre-1.166.3, only
    // updateSettings wrote to that key, so the persisted mirror tracked
    // the LAST EDITED tab, not the LAST ACTIVE tab. On reload, if
    // fireInitialGreetingIfReady('first-interaction') fired BEFORE hydrate
    // completed, speak() read state.settings (the stale legacy mirror) and
    // played the wrong tab's voice. With this write, the legacy mirror
    // tracks active-tab transitions and stays coherent with the persisted
    // active tab across reloads.
    setStoredSettings(newTab.settings);
    // Persist hint — caller (voiceTabsStore) holds the canonical tabs list.
    void tenantId;
    // WS-A Φ4 + D-A6 — fire greeting on tab activation. `reason='first-interaction'`
    // is the right enum value because the tab-switch is itself a user-triggered
    // event (matches the existing `pointerdown`/`keydown` listener path).
    // _greetingFiredByTab Set ensures fire-once-per-tab (D-A6 v2.1).
    //
    // KNOWN iOS LIMITATION (pre-existing, not introduced by Φ4): greeting
    // speak() does `await fetch('/api/voice/tts')` BEFORE `outputAudio.play()`.
    // The await breaks iOS Safari's gesture-context chain — so if the tab
    // being activated has never had startRecording() called (which primes
    // outputAudio inside the click gesture via primeOutputAudioUnlock at
    // voiceStore.ts:1841), the greeting audio fails silently on iOS Safari
    // (NotAllowedError caught by speak() → status='error' → resetTo 'idle').
    // Greeting fires correctly on desktop browsers + on iOS once the user
    // has clicked the mic at least once. Fire-and-forget; greeting failures
    // are non-fatal and visible only via console.warn.
    void fireInitialGreetingIfReady('first-interaction');
  },

  // 1.167.0 — remove a tab's byTabId entry. The subscriber below will
  // catch the byTabId reference change and persist the cleaned history
  // map (dropped tab's history goes with it). No-op if tabId is unknown.
  // Caller (voiceTabsStore.closeTab) handles activeTabId reassignment.
  dropTab(tabId) {
    useVoiceStore.setState((s) => {
      if (!s.byTabId[tabId]) return s;
      const next = { ...s.byTabId };
      delete next[tabId];
      return { byTabId: next } as Partial<VoiceState>;
    });
  },

  // WS-A Φ2 test-only hook — inject synthetic classic WS frame (VS.21).
  __test_classic_onmessage_handle: (payload) => handlePerTurnWsMessage(payload),
  // WS-A Φ2 test-only hook — inject synthetic streaming frame (VS.22).
  __test_streaming_frame_handle: (frame) => handleStreamingFrame(frame),

  async loadVoices() {
    // 2026-05-07: engine-aware. Query the SELECTED engine's voice list via
    // /api/voice/voices?engine=<id>. Backend resolves to the engine's
    // base URL (kokoro / qwen-fast) and returns a unified
    // {voices: [{id, gender, description}, ...]} shape regardless of which
    // engine produced it.
    const engineId = _get().settings.engine || DEFAULT_ENGINE_ID;
    try {
      const body = await api.get<{ voices: VoicePreset[] }>(
        `/voice/voices?engine=${encodeURIComponent(engineId)}`,
      );
      if (Array.isArray(body.voices) && body.voices.length > 0) {
        set({ voices: body.voices });
        // Validate persisted voice against the live list. Each engine has
        // a distinct voice namespace (Kokoro `af_heart`, VibeVoice
        // `en-Carter_man`, qwen-fast `vivian`); when user switches engines
        // their old stored voice won't be in the new list. Fall back to
        // the engine's default_voice (resolved via the engines list, or
        // ultimately the SPA-level DEFAULT_VOICE = 'af_heart').
        const current = _get().settings.voice;
        const validIds = new Set(body.voices.map((v) => v.id));
        if (!validIds.has(current)) {
          const enginesList = _get().engines;
          const engineSpec = enginesList.find((e) => e.id === engineId);
          const fallback = engineSpec && validIds.has(engineSpec.default_voice)
            ? engineSpec.default_voice
            : (validIds.has(DEFAULT_VOICE) ? DEFAULT_VOICE : body.voices[0].id);
          const next = { ..._get().settings, voice: fallback };
          setStoredSettings(next);
          set({ settings: next });
        }
      }
    } catch {
      // network error or non-2xx — fallback to VOICE_PRESETS already in store
    }
  },

  async loadEngines() {
    try {
      const body = await api.get<{ default: string; engines: EngineSpec[] }>(
        '/voice/engines',
      );
      if (Array.isArray(body.engines) && body.engines.length > 0) {
        set({ engines: body.engines });
        // If persisted engine isn't in the live registry (e.g., backend
        // dropped support for it), reset to the backend-reported default.
        const current = _get().settings.engine;
        const validEngines = new Set(body.engines.map((e) => e.id));
        if (!validEngines.has(current)) {
          await _get().setEngine(body.default || DEFAULT_ENGINE_ID);
        }
      }
    } catch {
      // network error or backend predates multi-engine support — fall back
      // to single-engine behavior (settings.engine = DEFAULT_ENGINE_ID = 'kokoro')
    }
  },

  async loadModels() {
    // 2026-06-28 — voice model picker. Source the tenant router's models from
    // the same endpoint Pi Chat's selector uses (/api/chat-proxy/v1/models,
    // mirrored from the LLM router's /v1/models). Drop unhealthy entries +
    // the synthetic 'auto' row (the UI renders its own "Auto" option). Label
    // falls back to id when `name` is absent. Best-effort: on any failure the
    // picker degrades to just "Auto" (the unchanged default).
    try {
      const body = await api.get<{
        data?: Array<{ id?: string; name?: string; status?: string }>;
      }>('/chat-proxy/v1/models');
      const entries = Array.isArray(body.data) ? body.data : [];
      const models: VoiceModelOption[] = entries
        .filter((e): e is { id: string; name?: string; status?: string } =>
          typeof e.id === 'string' && e.id.length > 0)
        .filter((e) => e.status !== 'unhealthy')
        .filter((e) => e.id !== 'auto')
        .map((e) => ({ id: e.id, label: e.name && e.name.length > 0 ? e.name : e.id }));
      set({ models });
      // 2026-07-08 — dallas Bridge-tab live failure: a persisted per-tab model
      // that has since been RETIRED from the router registry 404'd every voice
      // turn. The backend now falls back to auto-resolution too, but the picker
      // must not keep advertising a dead id — reset any stale persisted model
      // to 'auto'. Guarded on a non-empty list so a transient empty registry
      // response can't wipe user preferences.
      if (models.length > 0) {
        const ids = new Set(models.map((m) => m.id));
        const state = _get();
        let tabsPatched = false;
        for (const [tid, tab] of Object.entries(state.byTabId)) {
          const m = tab.settings.model;
          if (m && m !== 'auto' && !ids.has(m)) {
            _setTabFields(tid, { settings: { ...tab.settings, model: 'auto' } });
            tabsPatched = true;
          }
        }
        // Top-level shim (pre-hydrate / legacy path). If the active tab was
        // patched above, _setTabFields already mirrored it here.
        const top = _get().settings;
        if (top.model && top.model !== 'auto' && !ids.has(top.model)) {
          const next = { ...top, model: 'auto' };
          setStoredSettings(next);
          set({ settings: next });
        }
        if (tabsPatched && _activeTenantId) {
          // Persist the sanitized per-tab settings (same v2 write updateSettings uses).
          const fresh = _get();
          const settingsByTab: Record<TabId, VoiceSettings> = {};
          for (const [tid, tab] of Object.entries(fresh.byTabId)) {
            settingsByTab[tid] = tab.settings;
          }
          setStoredSettingsV2(_activeTenantId, settingsByTab, fresh.activeTabId);
        }
      }
    } catch {
      // network error / non-2xx — leave models unchanged; picker shows "Auto".
    }
  },

  async setEngine(engineId) {
    const state = _get();
    const enginesList = state.engines;
    const engineSpec = enginesList.find((e) => e.id === engineId);
    if (!engineSpec) return;
    const activeTabId = state.activeTabId;
    const currentSettings = activeTabId
      ? state.byTabId[activeTabId]?.settings ?? state.settings
      : state.settings;
    const next = { ...currentSettings, engine: engineId, voice: engineSpec.default_voice };
    setStoredSettings(next);
    if (activeTabId) {
      _setTabFields(activeTabId, { settings: next });
    } else {
      set({ settings: next });
    }
    await _get().loadVoices();
  },

  updateSettings(patch) {
    const state = _get();
    const activeTabId = state.activeTabId;
    const currentSettings = activeTabId
      ? state.byTabId[activeTabId]?.settings ?? state.settings
      : state.settings;
    const next = { ...currentSettings, ...patch };
    setStoredSettings(next);
    // W3 (2026-05-25) — D-W3-6 LOCKED. Real-time gain update for the
    // in-flight turn. When audioVolume changes, propagate to the active
    // VoiceAudioQueue's GainNode WITHOUT waiting for the next turn.
    // Web Audio API direct gain.value writes are click-free at slider-drag
    // rates per OPEN-C LOCKED.
    if (
      typeof patch.audioVolume === 'number'
      && activeTurnAudioQueue !== null
    ) {
      activeTurnAudioQueue.setVolume(patch.audioVolume);
    }
    if (activeTabId) {
      _setTabFields(activeTabId, { settings: next });
      // WS-A Φ2 — persist v2 with active-tab mirror to VOICE_PREF_KEY (D-A9).
      // Build the full byTabId-keyed payload AFTER the write lands.
      const freshState = _get();
      const settingsByTab: Record<TabId, VoiceSettings> = {};
      for (const [tid, tab] of Object.entries(freshState.byTabId)) {
        settingsByTab[tid] = tab.settings;
      }
      // Reviewer C-3 fix — persist on EVERY write while a tenant is active,
      // not just on the first post-migration write. Pre-hydrate (no
      // _activeTenantId) falls back to legacy `setStoredSettings` above.
      // The legacy-delete-pending iteration is also handled here because
      // `setStoredSettingsV2` flushes _legacyDeletePending[tenantId] on first
      // successful write.
      if (_activeTenantId) {
        setStoredSettingsV2(_activeTenantId, settingsByTab, activeTabId);
      } else {
        for (const tid of Object.keys(_legacyDeletePending)) {
          setStoredSettingsV2(tid, settingsByTab, activeTabId);
        }
      }
    } else {
      set({ settings: next });
    }
  },

  clearHistory() {
    const tabId = _get().activeTabId;
    if (tabId) {
      _setTabFields(tabId, { history: [] });
    } else {
      set({ history: [] });
    }
  },

  replaceHistory(history) {
    const tabId = _get().activeTabId;
    if (tabId) {
      _setTabFields(tabId, { history });
    } else {
      set({ history });
    }
  },

  async startRecording(options) {
    // 1.137.0 — iOS Safari audio-output unlock. MUST run synchronously
    // BEFORE any await so we're still inside the user-gesture context.
    // Subsequent autonomous TTS playback (dispatcher → speak chain) on
    // iOS depends on this priming.
    primeOutputAudioUnlock();
    // Phase α — reset prior-turn partial timing data (R1.CONCERN-2) and
    // start a fresh turn. The reset is mandatory: a rapid second mic-click
    // before the previous turn's audio.onended fires would otherwise
    // inherit stale fields (e.g., t_audio_play_called from the prior turn).
    currentTurnTiming = {};
    const turn_id = genTurnId();
    const t_mic_click = performance.now();
    currentTurnTiming.turn_id = turn_id;
    currentTurnTiming.t_mic_click = t_mic_click;
    // PWS (2026-05-17) — capture WS state at the moment of mic-click. If we
    // have a persistent WS already OPEN, this turn skips the construction +
    // handshake and just sends a config frame. On CONNECTING/CLOSED, fall
    // through to the legacy per-turn construction (cold path).
    //
    // GUARD — only treat activeWS as a persistent-WS reference if
    // pwsInitialized is true (i.e., openPersistentWS has fired at least
    // once successfully). Otherwise activeWS may be a leftover per-turn WS
    // from a prior call (legacy cold path) which should NOT be treated as
    // a warm-WS candidate. Also protects test environments where module-init
    // is disabled (PWS_AUTOINIT guard) from accidentally taking the warm path.
    const wsAtClick = pwsInitialized ? activeWS : null;
    const wsStateAtClick: 'OPEN' | 'CONNECTING' | 'CLOSED' =
      wsAtClick?.readyState === WebSocket.OPEN ? 'OPEN'
      : wsAtClick?.readyState === WebSocket.CONNECTING ? 'CONNECTING'
      : 'CLOSED';
    currentTurnTiming.t_ws_state_at_click = wsStateAtClick;
    // WS-A Φ6 R3 C-2 fix — route per-tab status/transcript/error/currentTurn*
    // writes through _setActiveTabFields so byTabId[activeTabId] stays in sync
    // with the top-level mirror. Pre-fix this raw set() left byTabId.status
    // stuck at the prior value while only the mirror updated.
    _setActiveTabFields({ status: 'idle', transcript: '', error: null, currentTurnId: turn_id, currentTurnStart: t_mic_click });
    const disableVAD = options?.disableVAD ?? false;

    // 1. mic permission
    //
    // 1.122.0 — pre-flight check for `navigator.mediaDevices`. iOS Safari
    // (verified live 2026-05-13 on iPad) leaves `navigator.mediaDevices`
    // undefined under several conditions: in-WebView wrappers, mic blocked
    // in Settings → Safari → Camera & Microphone, non-secure context, OR
    // certain PWA modes. Calling `.getUserMedia` on undefined throws
    // `TypeError: undefined is not an object 'navigator.mediaDevices.
    // getUserMedia'` — the existing try/catch surfaces it but the message
    // doesn't tell the user what to fix. Detect upfront and surface a
    // platform-specific recovery hint instead.
    //
    // 1.123.0 — iPad detection fix. Since iOS 13 (2019), iPad's default
    // Safari UA reports `"Macintosh; Intel Mac OS X"` with NO "iPad"
    // substring (the "Request Desktop Site" default). The 1.122.0 regex
    // /iP(hone|ad|od)/i missed every modern iPad. Canonical iPadOS-in-
    // desktop-mode detection: `navigator.platform === 'MacIntel' &&
    // navigator.maxTouchPoints > 1` — real Macs have maxTouchPoints=0
    // (no touch screen) while iPads emulating Mac UA still have touch
    // points exposed.
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      // 1.132.0 — Lockdown-Mode-aware hint. See pickMediaDevicesUnavailableHint().
      // WS-A Φ6 R3 C-2 fix — route per-tab error/status via _setActiveTabFields.
      _setActiveTabFields({
        status: 'error',
        error: `mic: navigator.mediaDevices is unavailable. ${pickMediaDevicesUnavailableHint()}`,
      });
      return;
    }
    try {
      activeStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      _setActiveTabFields({ status: 'error', error: `mic: ${msg}` });
      return;
    }
    // φ (2026-05-16) — capture mic-permission-resolved timestamp. See
    // ClientTurnTiming.t_get_user_media_resolved for why this matters.
    currentTurnTiming.t_get_user_media_resolved = performance.now();

    // 2026-05-07 + W (2026-05-16): flip UI to 'connecting' (yellow) as soon as
    // the mic is captured. The 2026-05-07 fix set status='recording' here to
    // avoid the "user clicks again because no visual change" problem, BUT it
    // told a small lie — recorder.start() doesn't run until ws.onopen below,
    // so the user thinks they're recording and starts talking, but their
    // first 0-15s of speech is LOST. On the VPS reverse-tunnel path the
    // handshake was observed taking up to 15s. W flips this to 'connecting'
    // (yellow orb, "connecting" label, click-is-no-op) so the user knows to
    // WAIT to speak. ws.onopen below transitions 'connecting' → 'recording'.
    // WS-A Φ6 R3 C-2 fix — route per-tab status write through _setActiveTabFields.
    _setActiveTabFields({ status: 'connecting' });

    // PWS (2026-05-17) — WARM PATH. If we have a persistent WS already OPEN
    // from openPersistentWS() at module init, REUSE it and skip the legacy
    // per-turn `new WebSocket()` block entirely. We send the config frame
    // on the existing WS, then continue to the recorder construction (which
    // happens unconditionally below). Cold path falls through to the legacy
    // `new WebSocket()` block.
    if (wsStateAtClick === 'OPEN' && activeWS) {
      // Warm-path equivalences for Phase α timing fields. No new WebSocket
      // was constructed, so set constructor + open timestamps to t_mic_click
      // so the analyst sees delta=0ms attributed to those stages (correct;
      // no work was done there).
      currentTurnTiming.t_ws_constructor_called = t_mic_click;
      currentTurnTiming.t_ws_open = t_mic_click;
      const language = useVoiceStore.getState().settings.language || 'auto';
      activeWS.send(JSON.stringify({
        type: 'config',
        language,
        audio_mime_type: activeRecorderMimeType,
        turn_id,
      }));
      currentTurnTiming.t_config_sent = performance.now();
      // Fall through to the MediaRecorder construction block below; that
      // block calls activeRecorder.start(250) + set('recording') itself
      // when MediaRecorder is constructed successfully.
      // Skip the ws.onopen / ws.onmessage / ws.onerror / ws.onclose handler
      // block since the persistent WS's handlers (set at openPersistentWS)
      // are already in place and handle all message types including
      // transcript / error for THIS turn.
      // Note: pre-PWS, the recorder.start() was inside ws.onopen. PWS-warm
      // moves it OUT of the (skipped) ws.onopen and into the recorder
      // construction block — see "PWS-WARM RECORDER START" anchor below.
    } else {
      // COLD PATH — legacy per-turn WS construction. Fallback for when
      // openPersistentWS hasn't fired yet OR the persistent WS died.
      activeRecorderMimeType = 'audio/webm'; // overwritten by pickRecorderMimeType below
    }

    // 2. WS open + send config on onopen (COLD-PATH ONLY).
    if (wsStateAtClick !== 'OPEN' || !activeWS) {
    const ws = new WebSocket(buildWsUrl());
    // φ (2026-05-16) — capture WS-constructor-returned timestamp. The
    // constructor itself returns synchronously; the handshake happens after.
    // Together with t_get_user_media_resolved + t_ws_open, this lets us
    // attribute the 17.76s stalls to (getUserMedia | WS-construct | WS-handshake).
    currentTurnTiming.t_ws_constructor_called = performance.now();
    activeWS = ws;

    ws.onopen = () => {
      // D1-1 — auth frame FIRST, before the config send below.
      sendVoiceAuthFrame(ws);
      // Phase α — capture WS-open timestamp (one of the 12 DESIGN §11 α
      // points).
      currentTurnTiming.t_ws_open = performance.now();
      // Z.6 — pass user's preferred recognition language from settings.
      // 'auto' = whisperx auto-detect (default). Multi-language users
      // can pin via the settings modal.
      const language = useVoiceStore.getState().settings.language || 'auto';
      // 1.131.0 iOS mic hotfix: ship the chosen mimeType so backend can
      // label the whisperx upload accurately (Safari emits MP4, Chrome
      // emits WebM/Opus).
      // Phase α — ship turn_id in the config frame so the server-side
      // voice.stt.timing log line uses the same correlation ID as the
      // client log. Server will echo it back in the transcript frame
      // (or generate one if absent).
      ws.send(JSON.stringify({
        type: 'config',
        language,
        audio_mime_type: activeRecorderMimeType,
        turn_id,
      }));
      // Phase α — R1.C2 fix: capture t_config_sent immediately after ws.send.
      currentTurnTiming.t_config_sent = performance.now();
      // 2026-05-06: start the recorder ONLY after the WS is open + the
      // config message has been sent. Pre-fix the recorder.start() ran
      // synchronously below — on the VPS path (browser → VPS nginx →
      // reverse SSH tunnel → T440 nginx → vibe-backend), the WS handshake
      // routinely takes >250ms, so the FIRST `dataavailable` chunk fires
      // while ws.readyState === CONNECTING and the `if (ws.readyState
      // === WebSocket.OPEN)` guard in ondataavailable below silently
      // dropped it. That first chunk carries the EBML/webm magic header
      // (1A 45 DF A3 ...) — without it whisperx's ffmpeg saw a body-only
      // webm and refused to decode ("Format matroska,webm detected only
      // with low score of 1, misdetection possible!" → HTTP 500). Moving
      // recorder.start() in here guarantees every chunk lands on an
      // already-open socket.
      if (activeRecorder && activeRecorder.state === 'inactive') {
        activeRecorder.start(250);
        _setActiveTabFields({ status: 'recording' });
      }
    };

    ws.onmessage = (ev) => {
      let payload: VoiceServerEvent;
      try { payload = JSON.parse(ev.data) as VoiceServerEvent; } catch { return; }
      if (payload.type === 'transcript') {
        currentTurnTiming.t_transcript_rcvd = performance.now();
        const serverTurnId = (payload as { turn_id?: string }).turn_id;
        if (serverTurnId && typeof serverTurnId === 'string') {
          currentTurnTiming.turn_id = serverTurnId;
          _setActiveTabFields({ currentTurnId: serverTurnId });
        }
        const transcriptText = (payload.text || '').trim();
        // WS-A Φ2 — capture activeTabId at transcript arrival; bridge writes
        // go to this tab via dispatch()'s D-A12 capture (dispatch() reads
        // activeTabId at its own entry, which is sync with this transcript
        // handler).
        const userTurn: VoiceTurn[] = transcriptText
          ? [{ role: 'user' as const, text: transcriptText, timestamp: Date.now() }]
          : [];
        const activeTabIdNow = useVoiceStore.getState().activeTabId;
        if (activeTabIdNow) {
          const tab = useVoiceStore.getState().byTabId[activeTabIdNow];
          if (tab) {
            _setTabFields(activeTabIdNow, {
              transcript: payload.text,
              status: 'idle',
              history: [...tab.history, ...userTurn].slice(-HISTORY_MAX_TURNS),
            });
          }
        } else {
          set((s) => ({
            transcript: payload.text,
            status: 'idle',
            history: [...s.history, ...userTurn].slice(-HISTORY_MAX_TURNS),
          }));
        }
        teardownSession();
        if (transcriptText) {
          // WS-A Φ2 + D-A10 + D-A12 — dispatch() self-chains bridge-push +
          // speak() with locked voice/speed/engine. Do NOT chain .then()
          // here or the bridge turn double-pushes.
          void useVoiceStore.getState().dispatch(transcriptText);
        }
      } else if (payload.type === 'error') {
        const code = payload.code || 'unknown';
        if (code === 'unauthorized') {
          // Auth rejection — NON-STICKY, mirrors handleStreamingFrame. Auth
          // layer owns the /login redirect; don't set a persistent voice
          // banner. (Legacy non-streaming path; kept consistent.) 2026-06-25.
          console.warn('[voiceStore] classic ws auth rejected (unauthorized); auth layer owns redirect');
          _setActiveTabFields({ status: 'idle', error: null });
          teardownSession();
        } else {
          const msg = payload.message ? `${code}: ${payload.message}` : code;
          // TODO 1.164.3+: apply captured-tab-id pattern (see end_of_turn fix at
          // L1746-1760). Legacy non-streaming WS path; current multi-tab user
          // flow exercises the streaming path, not this one.
          _setActiveTabFields({ status: 'error', error: msg });
          teardownSession();
        }
      }
      // ready is just an ack — no state change.
    };

    ws.onerror = () => {
      // TODO 1.164.3+: apply captured-tab-id pattern. Legacy non-streaming WS.
      _setActiveTabFields({ status: 'error', error: 'voice ws error' });
      teardownSession();
    };

    ws.onclose = (ev) => {
      const finalStatus = useVoiceStore.getState().status;
      if (finalStatus === 'connecting' || finalStatus === 'recording' || finalStatus === 'transcribing') {
        // TODO 1.164.3+: apply captured-tab-id pattern. Legacy non-streaming WS.
        _setActiveTabFields({ status: 'error', error: `ws closed unexpectedly (code=${ev.code})` });
      }
      activeWS = null;
    };
    }  // close cold-path if-block opened above (PWS 2026-05-17)
    // PWS — after cold path falls through OR warm path skipped the WS-create
    // block, activeWS is guaranteed non-null (either freshly-constructed by
    // cold path or persistent from openPersistentWS). MediaRecorder + audio
    // handlers below use `activeWS` directly (bound to the closure var `ws`
    // for backward compat with the legacy code paths inside this function).
    const ws: WebSocket = activeWS!;

    // 3. MediaRecorder — 1.131.0 iOS mic hotfix: pick a supported mimeType
    // at runtime + wrap construction in try/catch. Pre-fix the hardcoded
    // 'audio/webm;codecs=opus' threw NotSupportedError synchronously on
    // iOS Safari and the throw escaped the only nearby try block (which
    // wraps getUserMedia only), breaking the mic silently on iPhone/iPad.
    const recorderMimeType = pickRecorderMimeType();
    activeRecorderMimeType = recorderMimeType || 'audio/webm';
    let recorder: MediaRecorder;
    try {
      recorder = recorderMimeType
        ? new MediaRecorder(activeStream, { mimeType: recorderMimeType })
        : new MediaRecorder(activeStream);
    } catch (err) {
      const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      // WS-A Φ6 R3 re-review C-A fix — route per-tab status/error via helper.
      _setActiveTabFields({
        status: 'error',
        error: `mic: MediaRecorder construction failed (${msg}). Tried mimeType="${recorderMimeType || '(default)'}".`,
      });
      teardownSession();
      return;
    }
    activeRecorder = recorder;

    // PWS-WARM RECORDER START. On the warm path the WS is already OPEN, so
    // recorder.start() can fire immediately. On the cold path, recorder.start
    // was called from inside ws.onopen (which fires asynchronously); we
    // already started it from there. Either way, the guard checks current
    // state to avoid double-start.
    if (wsStateAtClick === 'OPEN' && activeRecorder.state === 'inactive') {
      activeRecorder.start(250);
      _setActiveTabFields({ status: 'recording' });
    }

    recorder.ondataavailable = (ev) => {
      if (ws.readyState === WebSocket.OPEN && ev.data.size > 0) {
        // Phase α — capture FIRST audio chunk's send-attempt timestamp.
        // Subsequent chunks don't update the field. We use the JS-side
        // "we tried to send" time (not "browser actually sent it") which
        // is the closest observable instant.
        if (currentTurnTiming.t_first_audio_chunk_sent === undefined) {
          currentTurnTiming.t_first_audio_chunk_sent = performance.now();
        }
        ev.data.arrayBuffer().then((buf) => {
          if (ws.readyState === WebSocket.OPEN) ws.send(buf);
        });
      }
    };

    // 2026-05-06: recorder.start() is now called from ws.onopen above to
    // avoid the race where the first chunk (containing the webm/EBML
    // header) fires before the WS has opened and gets silently dropped
    // by the `if (ws.readyState === WebSocket.OPEN)` guard in
    // ondataavailable. If the WS already happens to be OPEN here (e.g.,
    // hot-reload tests where the connection is instantaneous), start
    // now; otherwise ws.onopen handles it.
    if (ws.readyState === WebSocket.OPEN && recorder.state === 'inactive') {
      recorder.start(250);
      _setActiveTabFields({ status: 'recording' });
    }

    // VAD silence-watchdog: read RMS amplitude from the same stream every
    // VAD_POLL_INTERVAL_MS; if 5s pass without any sample above
    // SILENCE_RMS_THRESHOLD, trigger stopRecording. AudioContext is gated
    // behind a typeof check so jsdom (which doesn't ship AudioContext) just
    // skips the watchdog and the click-to-stop path remains the only way out.
    // F2 — push-to-talk path passes disableVAD=true; the user holds the
    // button and explicitly releases to stop, no silence cutoff.
    if (!disableVAD && typeof AudioContext !== 'undefined') {
      try {
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(activeStream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        activeAudioContext = audioContext;
        activeAnalyser = analyser;

        const buf = new Uint8Array(analyser.fftSize);
        let lastSoundAt = Date.now();

        silencePollHandle = setInterval(() => {
          if (!activeAnalyser) return;
          activeAnalyser.getByteTimeDomainData(buf);
          // RMS around the 128 zero-line of 8-bit PCM, normalized to [0,1].
          let sumSquares = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / buf.length);
          // 2026-05-07: publish level for the live mic-meter UI. RMS for
          // typical voice is in the 0.02..0.30 range; we don't clamp here
          // — VoicePanel scales to a reasonable display range.
          set({ audioLevel: rms });
          if (rms > SILENCE_RMS_THRESHOLD) {
            lastSoundAt = Date.now();
          } else if (Date.now() - lastSoundAt > SILENCE_TIMEOUT_MS) {
            // Hand off to stopRecording — same path the user click takes.
            useVoiceStore.getState().stopRecording();
          }
        }, VAD_POLL_INTERVAL_MS);
      } catch {
        // AudioContext init failed; skip VAD. Manual click-to-stop still works.
        activeAudioContext = null;
        activeAnalyser = null;
      }
    }
  },

  stopRecording() {
    if (useVoiceStore.getState().status !== 'recording') return;
    // WS-A Φ2.4 R2 C1 fix — route per-tab status write through _setActiveTabFields
    // so byTabId[activeTabId].status doesn't stay stuck at 'recording'.
    _setActiveTabFields({ status: 'transcribing' });

    // Stop MediaRecorder; final dataavailable will fire before onstop.
    activeRecorder?.stop();

    // Brief delay so the final chunk lands on the WS before stop frame.
    setTimeout(() => {
      try {
        if (activeWS && activeWS.readyState === WebSocket.OPEN) {
          activeWS.send(JSON.stringify({ type: 'stop' }));
          // Phase α — capture stop-frame-sent timestamp. This is the
          // load-bearing OLD-PATH origin for ttfa_ms (time-to-first-audio).
          currentTurnTiming.t_stop_sent = performance.now();
        }
      } catch { /* WS already gone */ }
    }, 200);
  },

  // ===== δ.8.1 — voice-streaming-pipeline integration =====
  //
  // Per DESIGN §5.2 + plan §4 δ.8 + R2 C8 idempotent + R2 C10 log-not-silence
  // + R1 C5 fire-and-forget resume + R3.2 naming discipline.
  //
  // startTurn() parallels startRecording but uses /api/voice/turn WS. The
  // server drives the full STT → LLM stream → Kokoro stream → audio_chunk
  // pipeline. SPA receives frames + plays via voiceAudioQueue.
  async startTurn(_options) {
    // R1 C5 gesture-sync prelude — MUST run synchronously BEFORE any await.
    primeOutputAudioUnlock();
    // WS-A Φ2.4 R2 C2 fix — capture activeTabId SYNC at startTurn entry so
    // a tab switch during the (potentially 8s) WS-open await can't poison
    // the config-frame settings/history reads below. Same D-A10/D-A12
    // discipline as dispatch()'s dispatchingTabId.
    const startingTabId = useVoiceStore.getState().activeTabId;
    if (typeof window === 'undefined' || typeof AudioContext === 'undefined') {
      // WS-A Φ6 R3 C-2 fix — route per-tab status/error through helper.
      _setActiveTabFields({ status: 'error', error: 'AudioContext unavailable' });
      return;
    }
    // δ⁗ R1 Concern #3 frame-ordering gate — must NOT be mid-turn (status
    // 'connecting'/'recording'/'transcribing') before starting a new turn.
    // 'idle' OR 'error' are both fine: 'idle' = clean state; 'error' =
    // post-failure retry. Per handleClick at VoicePanel.tsx:344, the retry
    // button enters startTurn from status='error'. δ⁗ post-G7 fix.
    const currentStatus = useVoiceStore.getState().status;
    if (currentStatus !== 'idle' && currentStatus !== 'error') {
      console.warn(`[voiceStore] startTurn called while status=${currentStatus}; ignoring`);
      return;
    }
    // Clear any stale error from a prior failed attempt so retry starts clean.
    if (currentStatus === 'error') {
      // WS-A Φ6 R3 C-2 fix — route per-tab error clear through helper.
      _setActiveTabFields({ error: null });
    }
    // δ⁗ — open persistent streaming WS (creates AudioContext + Queue lazily).
    // Idempotent: warm path (2nd+ turn) is a no-op.
    openPersistentStreamingWS();
    // F5 — ensure AudioContext is 'running' before audio bytes arrive (was
    // silent suspended on Firefox subsequent turns pre-fix).
    if (activeTurnAudioContext && activeTurnAudioContext.state !== 'running') {
      try {
        await activeTurnAudioContext.resume();
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        teardownStreamingSession();
        _setActiveTabFields({ status: 'error', error: `AudioContext.resume() rejected: ${errMsg}` });
        return;
      }
    }
    // Per-turn state reset (Plan R1 Concern C7: warm path's queue.resetForNextTurn
    // already happened at end-of-turn-N via teardownStreamingTurnResources).
    resetStreamingTurnState();

    const turn_id = genTurnId();
    // WS-A Φ6 R3 C-2 fix — route per-tab state writes via helper so byTabId stays in sync.
    _setActiveTabFields({
      status: 'connecting',
      transcript: '',
      error: null,
      currentTurnId: turn_id,
      currentTurnStart: performance.now(),
    });

    // Acquire mic
    let stream: MediaStream;
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        const hint = pickMediaDevicesUnavailableHint();
        teardownStreamingTurnResources();
        _setActiveTabFields({ status: 'error', error: hint });
        return;
      }
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      teardownStreamingTurnResources();
      _setActiveTabFields({
        status: 'error',
        error: isLikelyIosPermissionDenied(errMsg)
          ? `Microphone permission denied: ${errMsg}`
          : errMsg,
      });
      return;
    }
    activeStream = stream;

    // δ″ F2 + F6 — streaming-mode mic-level meter + auto-stop after silence.
    const turnCtx = activeTurnAudioContext;
    if (turnCtx) {
      try {
        const turnSource = turnCtx.createMediaStreamSource(stream);
        const turnAnalyser = turnCtx.createAnalyser();
        turnAnalyser.fftSize = 1024;
        turnSource.connect(turnAnalyser);
        const buf = new Uint8Array(turnAnalyser.fftSize);
        const silenceSec = useVoiceStore.getState().settings.streamingSilenceSec;
        const silenceTimeoutMs = silenceSec > 0 ? silenceSec * 1000 : 0;
        let lastSoundAt = Date.now();
        let everHeardSound = false;
        turnLevelPollHandle = setInterval(() => {
          turnAnalyser.getByteTimeDomainData(buf);
          let sumSquares = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / buf.length);
          set({ audioLevel: rms });
          if (rms > SILENCE_RMS_THRESHOLD) {
            lastSoundAt = Date.now();
            everHeardSound = true;
          } else if (
            silenceTimeoutMs > 0
            && everHeardSound
            && Date.now() - lastSoundAt > silenceTimeoutMs
            && useVoiceStore.getState().status === 'recording'
          ) {
            if (turnLevelPollHandle) {
              clearInterval(turnLevelPollHandle);
              turnLevelPollHandle = null;
            }
            useVoiceStore.getState().stopTurn();
          }
        }, VAD_POLL_INTERVAL_MS);
      } catch (err) {
        console.warn('[voiceStore] streaming analyser construction failed; mic-meter disabled for this turn:', err);
      }
    }

    // MediaRecorder — MUST be constructed before sendConfig (Plan R1 Concern C5
    // warm-path ordering fix: on 2nd+ turn the WS is already open, so sendConfig
    // fires immediately, server returns 'ready' fast, handleStreamingFrame's
    // ready case calls activeRecorder.start(100) — activeRecorder must be set).
    let recorder: MediaRecorder;
    const mimeType = pickRecorderMimeType();
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      teardownStreamingTurnResources();
      _setActiveTabFields({ status: 'error', error: `MediaRecorder construction failed: ${errMsg}` });
      return;
    }
    activeRecorder = recorder;
    activeRecorderMimeType = mimeType || 'audio/webm';

    // MediaRecorder bytes → WS.
    recorder.ondataavailable = async (ev) => {
      if (
        ev.data
        && ev.data.size > 0
        && activeTurnSocket
        && activeTurnSocket.readyState === WebSocket.OPEN
      ) {
        const buf = await ev.data.arrayBuffer();
        activeTurnSocket.send(buf);
      }
    };

    // δ⁗ post-G6 fix — await WS open via Promise (race-free; resolves
    // immediately if already open, blocks if still CONNECTING). Replaces the
    // pendingConfigSend pattern that had a startTurn-vs-onOpen ordering race.
    if (activeTurnSocket && activeTurnSocket.readyState !== WebSocket.OPEN && streamingWSOpenPromise) {
      try {
        // 1.164.2 — 20s budget (was 8s). Constants exported above for vitest
        // pinning. Rationale: voiceStore.ts:37-43 documents 15s observed VPS
        // tunnel handshake latency; 20s clears it with headroom while still
        // failing loud if the handshake truly hangs.
        await Promise.race([
          streamingWSOpenPromise,
          new Promise<void>((_, rej) =>
            setTimeout(() => rej(new Error(STREAMING_WS_OPEN_TIMEOUT_ERROR)), STREAMING_WS_OPEN_TIMEOUT_MS),
          ),
        ]);
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        // δ⁗ post-G7 fix — FULL teardown so retry creates a fresh WS + Promise.
        // Pre-fix: teardownStreamingTurnResources kept the dead/hung socket alive,
        // so retry reused stale activeTurnSocket + pending streamingWSOpenPromise,
        // hitting the same 8s timeout forever (user's reported "still broken on
        // retry" failure mode).
        teardownStreamingSession();
        _setActiveTabFields({ status: 'error', error: `streaming ws: ${errMsg}` });
        return;
      }
    }
    // By here, WS is OPEN (or never existed → bail).
    if (!activeTurnSocket || activeTurnSocket.readyState !== WebSocket.OPEN) {
      teardownStreamingTurnResources();
      _setActiveTabFields({ status: 'error', error: 'streaming ws not open after wait' });
      return;
    }
    // 1.161.0 — include history + agent name in the streaming WS config frame
    // (parallel to the HTTP /voice/dispatch path). Streaming mode hits this
    // path; HTTP dispatch hits the other. Both must carry the same context.
    // WS-A Φ2.4 R2 C2 fix — read settings + history from the STARTING tab
    // (captured pre-await) via byTabId; falls back to top-level mirror when
    // pre-hydrate (startingTabId is null).
    const startingState = useVoiceStore.getState();
    const startingTab = startingTabId ? startingState.byTabId[startingTabId] : null;
    const settingsForConfig = startingTab?.settings ?? startingState.settings;
    const fullHistoryForConfig = startingTab?.history ?? startingState.history;
    // No mid-turn slice-off here because the streaming-mode pipeline receives
    // the transcript via STT *after* the config frame — at this point the
    // current user turn has NOT been pushed to history yet (the per-turn
    // transcript handler does that at voiceStore.ts:~1356). Send all of
    // history capped by setting.
    const historyCap = settingsForConfig.dispatcherHistoryTurns;
    const historyForConfig = historyCap > 0
      ? fullHistoryForConfig.slice(-historyCap).map((t) => ({ role: t.role, text: t.text }))
      : [];
    activeTurnSocket.send({
      type: 'config',
      language: 'auto',
      audio_mime_type: mimeType || 'audio/webm',
      engine: settingsForConfig.engine,
      voice: settingsForConfig.voice,
      speed: settingsForConfig.speed,
      turn_id,
      history: historyForConfig,
      voice_agent_name: settingsForConfig.voiceAgentName,
      // 2026-06-28 — per-voice-tab LLM model picker (streaming-path mirror of
      // the /voice/dispatch body). 'auto' → backend resolves as before.
      model: settingsForConfig.model,
      // 1.421.0 — unrestricted-conversation toggle (streaming-path mirror).
      // Absent/false → backend keeps today's prompt.
      unrestricted_content: settingsForConfig.unrestrictedContent,
      // 1.162.2 — voice→Pi prefix route. Backend detects in voice_turn
      // handler AFTER STT, BEFORE LLM pipeline. Empty string disables.
      pi_prefix_phrase: settingsForConfig.piPrefixPhrase,
      // 1.187.0 Phase 7.3 — streaming-path mirror of /voice/dispatch payload
      // field. WS minimal-config sends at L2708 + L2756 intentionally omit
      // this field — backend `config.get("enable_web_search")` → None →
      // normalized True (same backward-compat pattern as voice_agent_name).
      enable_web_search: useChatPrefsStore.getState().useWebSearch,
      // 2026-07-01 — on-screen snapshot so the voice agent can SEE the active
      // module's data (spaContextStore). Backend injects it into the dispatcher
      // system prompt via _format_spa_context_block. null when nothing published.
      spa_context: getSpaContext(),
    });
  },

  stopTurn() {
    const s = useVoiceStore.getState().status;
    if (s !== 'recording') return;
    // WS-A Φ2.4 R2 C1 fix — route per-tab status write through _setActiveTabFields.
    _setActiveTabFields({ status: 'transcribing' });
    try {
      activeRecorder?.stop();
    } catch {
      /* recorder already stopped */
    }
    // Brief delay so the final chunk lands on the WS before stop frame.
    setTimeout(() => {
      try {
        if (
          activeTurnSocket &&
          (activeTurnSocket as { readyState?: number }).readyState === WebSocket.OPEN
        ) {
          activeTurnSocket.send({ type: 'stop' });
        }
      } catch {
        /* socket already gone */
      }
    }, 200);
  },

  cancelTurn() {
    // Phase 9.7 (2026-05-24) — D-9.7 / D-9.7-6 mid-turn cancel. Send
    // cancel_turn frame for the in-flight turn_id. Backend listener +
    // pipeline check + cancelled-frame emit handle the rest. WS stays
    // open per D-9.7-4. FE idempotency: pendingCancelTurnId guards
    // repeated sends for the same turn_id (backend also idempotent).
    const currentTurnId = useVoiceStore.getState().currentTurnId;
    if (!currentTurnId) return;
    if (currentStreamingTurnState.pendingCancelTurnId === currentTurnId) return;
    if (
      !activeTurnSocket ||
      (activeTurnSocket as { readyState?: number }).readyState !== WebSocket.OPEN
    ) {
      return;
    }
    currentStreamingTurnState.pendingCancelTurnId = currentTurnId;
    try {
      activeTurnSocket.sendCancelTurn(currentTurnId);
    } catch {
      /* socket already gone — cancel will not arrive; backend turn completes naturally */
    }
  },

  stopSpeaking() {
    // 2026-06-07 — user-requested STOP-while-speaking. The Phase 9.7 Stop
    // button only handles `transcribing` status (LLM in-pipeline cancel
    // via cancel_turn frame). Once status flips to `speaking` (TTS audio
    // playing on the client), the player had NO affordance to halt it
    // until either the clip ended OR they switched tabs (and only if
    // pauseAudioOnLoseFocus was opted in). This violates the voice-must-
    // be-pausable rule (feedback_voice_must_be_pausable_stoppable memory
    // anchor 2026-06-07).
    //
    // Implementation mirrors the pauseAudioOnLoseFocus path at L2879-2887:
    // audio.pause() + currentTime=0 + delete from the per-tab map. Status
    // flips to 'idle'. Idempotent — if no audio is registered, no-op.
    const tabId = useVoiceStore.getState().activeTabId;
    if (!tabId) return;
    const audio = _playingAudioByTab.get(tabId as TabId);
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {
        // Non-fatal — audio may already be detached. Log so future
        // debugging of "stop didn't work" has a signal; the status
        // transition still completes below regardless of pause() outcome.
        // Reviewer-flagged 2026-06-07 (handoff-13 enshittification audit):
        // prior code used bare `catch {}` which suppressed the signal.
        console.warn('[voiceStore] stopSpeaking: audio.pause() threw on detached element; continuing to idle', e);
      }
      _playingAudioByTab.delete(tabId as TabId);
    }
    _setTabFields(tabId, { status: 'idle' });
    // Mirror to the top-level for legacy consumers (matches setActiveTab
    // pattern at L2904-2909).
    set({ status: 'idle' });
  },

  teardownStreamingSession() {
    teardownStreamingSession();
  },

  async speak(text, voice, speed, engine) {
    // WS-A Φ2 + D-A10 + D-A12 (C-1 reviewer fix) — capture speakingTabId
    // SYNC at speak() entry so audio.onended (which fires seconds later, after
    // the user may have switched tabs) writes status back to the ORIGINATING
    // tab, not whichever tab is active when the audio ends. Same discipline
    // as dispatch()'s dispatchingTabId.
    const speakingTabId = _get().activeTabId;
    const writeSpeakField = (patch: Partial<VoiceTabState>): void => {
      if (speakingTabId) {
        _setTabFields(speakingTabId, patch);
      } else {
        set(patch as Partial<VoiceState>);
      }
    };
    writeSpeakField({ status: 'speaking', error: null });
    // WS-A Φ2 + D-A10 — read engine/voice/speed in priority order:
    //   1. explicit arg from caller (locked at dispatch initiation)
    //   2. SPEAKING TAB's settings (byTabId[speakingTabId])
    //   3. top-level mirror (pre-hydrate fast path)
    //   4. defaults
    const state = _get();
    const stored = speakingTabId
      ? state.byTabId[speakingTabId]?.settings ?? state.settings
      : state.settings;
    const effectiveEngine = engine ?? stored.engine ?? DEFAULT_ENGINE_ID;
    const effectiveVoice = voice ?? stored.voice ?? getStoredVoice();
    const effectiveSpeed = speed ?? stored.speed;
    // Phase α — capture TTS-POST-start timestamp + include turn_id on the body
    // so the server's voice.tts.timing log line joins the rest of this turn's
    // logs by ID.
    currentTurnTiming.t_tts_post_start = performance.now();
    const turn_id_for_tts = _get().currentTurnId ?? undefined;
    let response: Response;
    try {
      // Bare fetch is INTENTIONAL here: api.ts `handleResponse` always calls
      // `response.json()` on success, which would consume the audio Blob and
      // make subsequent `.blob()` calls throw "Body is unusable". TTS returns
      // binary audio/wav; we need raw Response. getAuthHeaders() supplies the
      // same Bearer token api.ts would. (Cleanup 2026-05-04 post-E.21.28.)
      // eslint-disable-next-line no-restricted-syntax
      response = await fetch('/api/voice/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          text,
          engine: effectiveEngine,
          voice: effectiveVoice,
          speed: effectiveSpeed,
          turn_id: turn_id_for_tts,
        }),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      writeSpeakField({ status: 'error', error: `tts: ${msg}` });
      return;
    }
    // Phase α — TTS response received (bytes still buffered in `response`,
    // not yet decoded). This is the wire-arrival timestamp.
    currentTurnTiming.t_tts_rcvd = performance.now();

    if (!response.ok) {
      let code = `tts_${response.status}`;
      let message = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        const detail = body?.detail;
        if (detail && typeof detail === 'object') {
          code = detail.code || code;
          message = detail.message || message;
        }
      } catch { /* non-JSON error body */ }
      writeSpeakField({ status: 'error', error: `${code}: ${message}` });
      return;
    }

    // Play the audio.
    //
    // 1.137.0 iOS Safari fix: prefer the gesture-primed `outputAudio`
    // element (created in startRecording) — iOS allows .play() on an
    // already-unlocked element without a fresh gesture. The autonomous
    // dispatcher → speak chain depends on this. If outputAudio is null
    // (user typed text + clicked Speak with no prior mic click), fall
    // back to a fresh Audio — that path IS itself a gesture, so it
    // works on iOS too.
    const blob = await response.blob();
    // WS-A Φ2 — wrap URL.createObjectURL since jsdom (test environment) does
    // not implement it. fireInitialGreetingIfReady's `resolves.not.toThrow`
    // assertion (VS.31) requires speak() to resolve cleanly under these
    // conditions. Production browsers ALL implement URL.createObjectURL.
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      writeSpeakField({ status: 'error', error: 'audio: URL.createObjectURL unavailable' });
      return;
    }
    const url = URL.createObjectURL(blob);
    const audio = outputAudio ?? new Audio();
    audio.muted = false;
    // 2026-06-07 — apply settings.audioVolume to the non-streaming TTS
    // playback path. PRIOR BUG: the App volume slider in VoiceSettingsModal
    // (W3 D-W3-5) only routed through activeTurnAudioQueue.setVolume()
    // (streaming path at L3066). This legacy non-streaming speak() path
    // (used by initial-greeting + briefing-room ReadAloudControl invocations)
    // skipped the volume gate entirely — slider movements had no effect on
    // playback level. HTMLMediaElement.volume is [0, 1.0], so the [0, 1.5]
    // setting range is clamped to 1.0 in this path; >1.0 gain requires Web
    // Audio (the streaming queue's path). For mute (slider at 0), we still
    // assign 0 — preferable to leaving audio.volume defaulting to 1.0.
    audio.volume = Math.max(0, Math.min(1.0, stored.audioVolume ?? 1.0));
    audio.src = url;
    // WS-A.5 — register this audio in the per-tab playback map so
    // setActiveTab's pauseAudioOnLoseFocus branch can find the right ref
    // when the user switches away from speakingTabId. The Map entry is
    // deleted in onended + the play() catch paths below.
    if (speakingTabId) {
      _playingAudioByTab.set(speakingTabId, audio);
    }
    audio.onended = () => {
      URL.revokeObjectURL(url);
      if (speakingTabId) _playingAudioByTab.delete(speakingTabId);
      // Phase α — emit per-turn client timing summary BEFORE flipping status
      // back to idle, then reset module-level state. The console.info line
      // is the join-point for the browser-devtools side of the analysis;
      // the matching server-side log lines join by turn_id.
      currentTurnTiming.t_audio_ended = performance.now();
      const t = currentTurnTiming;
      const t_mic = t.t_mic_click;
      const t_stop = t.t_stop_sent;
      const t_play = t.t_audio_play_called;
      const t_end = t.t_audio_ended;
      if (
        t.turn_id !== undefined &&
        t_mic !== undefined && t.t_ws_open !== undefined &&
        t.t_config_sent !== undefined && t.t_first_audio_chunk_sent !== undefined &&
        t_stop !== undefined && t.t_transcript_rcvd !== undefined &&
        t.t_dispatch_post_start !== undefined && t.t_dispatch_rcvd !== undefined &&
        t.t_tts_post_start !== undefined && t.t_tts_rcvd !== undefined &&
        t_play !== undefined && t_end !== undefined
      ) {
        const full: ClientTurnTiming = {
          turn_id: t.turn_id,
          t_mic_click: t_mic,
          t_get_user_media_resolved: t.t_get_user_media_resolved ?? 0,
          t_ws_constructor_called: t.t_ws_constructor_called ?? 0,
          t_ws_state_at_click: t.t_ws_state_at_click ?? 'CLOSED',
          t_ws_open: t.t_ws_open,
          t_config_sent: t.t_config_sent,
          t_first_audio_chunk_sent: t.t_first_audio_chunk_sent,
          t_stop_sent: t_stop,
          t_transcript_rcvd: t.t_transcript_rcvd,
          t_dispatch_post_start: t.t_dispatch_post_start,
          t_dispatch_rcvd: t.t_dispatch_rcvd,
          t_tts_post_start: t.t_tts_post_start,
          t_tts_rcvd: t.t_tts_rcvd,
          t_audio_play_called: t_play,
          t_audio_ended: t_end,
          ttfa_ms: Math.round(t_play - t_stop),
          total_ms: Math.round(t_end - t_mic),
          user_agent: typeof navigator !== 'undefined'
            ? navigator.userAgent.slice(0, 80)
            : '',
        };
        // eslint-disable-next-line no-console
        console.info('[voice.turn.timing.client]', JSON.stringify(full));
        writeSpeakField({ lastTurnTiming: full });
      }
      currentTurnTiming = {};
      writeSpeakField({ status: 'idle', currentTurnId: null, currentTurnStart: null });
    };
    try {
      // Phase α — capture audio.play()-called timestamp (OLD-PATH TTFA origin
      // partner; ttfa_ms = t_audio_play_called - t_stop_sent).
      currentTurnTiming.t_audio_play_called = performance.now();
      const playResult = audio.play();
      if (playResult instanceof Promise) {
        await playResult.catch((err) => {
          writeSpeakField({ status: 'error', error: `audio play: ${err}` });
          URL.revokeObjectURL(url);
          if (speakingTabId) _playingAudioByTab.delete(speakingTabId);
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      writeSpeakField({ status: 'error', error: `audio: ${msg}` });
      URL.revokeObjectURL(url);
      if (speakingTabId) _playingAudioByTab.delete(speakingTabId);
    }
  },

  async dispatch(transcript) {
    // WS-A Φ2 + D-A10 + D-A12 — capture activeTabId + lock voice/speed/engine
    // BEFORE any async work so the .then() chain writes back to the originating
    // tab and speak() uses the originating tab's voice/speed/engine, NOT
    // whichever tab is active when the microtask resolves.
    const stateAtInit = _get();
    const dispatchingTabId = stateAtInit.activeTabId;
    const dispatchingTab = dispatchingTabId ? stateAtInit.byTabId[dispatchingTabId] : null;
    const settingsNow = dispatchingTab?.settings ?? stateAtInit.settings;
    const lockedVoice = settingsNow.voice;
    const lockedSpeed = settingsNow.speed;
    const lockedEngine = settingsNow.engine;

    _setActiveTabFields({ error: null });
    currentTurnTiming.t_dispatch_post_start = performance.now();
    const turn_id_for_dispatch = _get().currentTurnId ?? undefined;
    const fullHistory = dispatchingTab?.history ?? _get().history;
    const priorHistory = fullHistory.length > 0 ? fullHistory.slice(0, -1) : [];
    const cap = settingsNow.dispatcherHistoryTurns;
    const historyToSend = cap > 0 ? priorHistory.slice(-cap) : [];
    const historyPayload = historyToSend.map((t) => ({ role: t.role, text: t.text }));
    let result: DispatcherResponse;
    try {
      const piChatHistory = collectPiChatHistory(settingsNow.piChatHistoryTurns);
      result = await api.post<DispatcherResponse>('/voice/dispatch', {
        transcript,
        turn_id: turn_id_for_dispatch,
        history: historyPayload,
        voice_agent_name: settingsNow.voiceAgentName,
        pi_chat_history: piChatHistory,
        // 2026-06-28 — per-voice-tab LLM model picker. 'auto' (default) →
        // backend _select_model() falls back to _resolve_active_model().
        model: settingsNow.model,
        // 1.421.0 — unrestricted-conversation toggle. False → today's prompt;
        // True → backend appends the OPEN CONVERSATION block.
        unrestricted_content: settingsNow.unrestrictedContent,
        // 1.187.0 Phase 7.3 — per-tenant useWebSearch toggle. None at backend
        // → normalized True (backward-compat); explicit boolean threads
        // through to _build_system_prompt + _build_native_tool_payload to
        // gate the web_search AgentTool advertising.
        enable_web_search: useChatPrefsStore.getState().useWebSearch,
        // 2026-07-01 — on-screen snapshot of the active module (spaContextStore)
        // so the voice agent can SEE the data instead of hallucinating. Backend
        // appends _format_spa_context_block to the system prompt. null = nothing.
        spa_context: getSpaContext(),
      });
      currentTurnTiming.t_dispatch_rcvd = performance.now();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          err.data && typeof err.data === 'object'
            ? (err.data as { detail?: { code?: string; message?: string } }).detail
            : undefined;
        const code = detail?.code || `dispatch_${err.status}`;
        const message = detail?.message || `HTTP ${err.status}`;
        // D-A12 — error writes to dispatching tab, not whichever tab is active now.
        if (dispatchingTabId) {
          _setTabFields(dispatchingTabId, { status: 'error', error: `${code}: ${message}` });
        } else {
          set({ status: 'error', error: `${code}: ${message}` });
        }
      } else {
        const msg = err instanceof Error ? err.message : String(err);
        if (dispatchingTabId) {
          _setTabFields(dispatchingTabId, { status: 'error', error: `dispatch: ${msg}` });
        } else {
          set({ status: 'error', error: `dispatch: ${msg}` });
        }
      }
      return null;
    }

    // D-A12 — lastDispatch writes to originating tab.
    if (dispatchingTabId) {
      _setTabFields(dispatchingTabId, { lastDispatch: result });
    } else {
      set({ lastDispatch: result });
    }

    const action = result.action_taken;
    if (action?.tool === 'spawn_terminal' &&
        ((action.result?.spawned_names as string[] | undefined)?.length ?? 0) > 0) {
      void usePaneStore.getState().refresh();
    }
    if (action?.tool === 'navigate' && !action.error) {
      const view = (action.result as { view?: string } | undefined)?.view;
      if (view === 'designer' || view === 'terminals') {
        useUIStore.getState().setViewMode(view as ViewMode);
      }
    }
    if (action?.tool === 'send_to_pi_chat' && !action.error) {
      const text = (action.result as { text?: string } | undefined)?.text;
      if (typeof text === 'string' && text.length > 0) {
        window.dispatchEvent(
          new CustomEvent('vibe:send-to-pi-chat', { detail: { text } }),
        );
      }
    }
    // Phase 9.0 Surface B — generic spa_action dispatch. Backend validates
    // (action, params) shape + value allowlist; here we route to the right
    // store/event. New view 'knowledge' joins the existing designer/terminals
    // set (per ViewMode type at uiStore.ts:32). focus_panel fires a
    // CustomEvent that target components listen for in mount-time effects.
    if (action?.tool === 'spa_action' && !action.error) {
      const result = action.result as
        | { action?: string; params?: Record<string, unknown> }
        | undefined;
      // 2026-06-27 — shared dispatcher (switch_view/focus_panel + the new
      // preview_file/build/deploy/undeploy IDE verbs). Same handler as the WS path.
      applyVoiceSpaAction(result?.action, result?.params ?? {});
    }

    // WS-A Φ2 + D-A10 + D-A12 — push bridge reply to ORIGINATING tab AND call
    // speak() with locked voice/speed/engine from dispatch init. This makes
    // dispatch() self-contained for the bridge-turn + speak chain (was split
    // between dispatch() returning + handlePerTurnWsMessage chaining).
    const reply = result.reply_text?.trim();
    if (reply) {
      const bridgeTurn: VoiceTurn = { role: 'bridge', text: reply, timestamp: Date.now() };
      if (dispatchingTabId) {
        _appendTabHistory(dispatchingTabId, bridgeTurn);
      } else {
        set((s) => ({ history: [...s.history, bridgeTurn].slice(-HISTORY_MAX_TURNS) }));
      }
      void _get().speak(reply, lockedVoice, lockedSpeed, lockedEngine);
    }

    return result;
  },
}));

// 1.167.0 — persistent voice chat history subscriber. Centralizes write
// path so we don't have to touch each of the ~12 history-append sites
// (some bypass _appendTabHistory and inline [...history, x].slice cap).
// Plain Zustand `create` (no subscribeWithSelector middleware in this
// project), so listener receives (state, prevState) directly.
//
// Gating:
//   - Skip if _activeTenantId is null (pre-hydrate; nothing to persist
//     under yet, and we'd leak a null-keyed localStorage entry).
//   - Skip if byTabId reference unchanged (no setState touched it).
//   - Skip if no per-tab history reference changed AND no tab was dropped.
//     Note: settings-only edits DO create a new byTabId outer reference
//     (updateSettings → _setTabFields spreads byTabId), so the outer
//     check above does NOT bail on those — the per-tab-history-ref
//     check is what catches them. _setTabFields builds the next tab via
//     `{ ...tab, ...patch }` which preserves the history reference
//     identity when patch doesn't include history, so the inner diff
//     correctly returns no-change on settings-only edits.
useVoiceStore.subscribe((state, prev) => {
  if (!_activeTenantId) return;
  if (state.byTabId === prev.byTabId) return;
  let changed = false;
  for (const id of Object.keys(state.byTabId)) {
    if (state.byTabId[id]?.history !== prev.byTabId?.[id]?.history) {
      changed = true;
      break;
    }
  }
  if (!changed) {
    for (const id of Object.keys(prev.byTabId ?? {})) {
      if (!state.byTabId[id]) { changed = true; break; }
    }
  }
  if (!changed) return;
  const payload: Record<TabId, VoiceTurn[]> = {};
  for (const [tabId, tab] of Object.entries(state.byTabId)) {
    payload[tabId] = tab.history;
  }
  setStoredHistoryV2(_activeTenantId, payload);
});

// PWS (2026-05-17) — module-init trigger. Open the persistent WS as soon as
// this module loads. In a browser context this happens at App mount time
// (the first import of useVoiceStore from a React component). The cost of
// the WS handshake (~500ms in Chromium, 3-20s in user's Firefox) is paid
// at SPA load time where users tolerate latency, not at mic-click time.
//
// Vibe-designer uses Mode 1 OIDC PKCE (SPA owns auth). The voice WS endpoint
// has no auth gate at the route layer (per CLAUDE.md). Opening pre-auth is
// safe; the user only sends a config frame after the login flow completes.
//
// The openPersistentWS function is idempotent (no-op if WS is already OPEN
// or CONNECTING) so subsequent imports / hot-reloads don't double-open.
//
// TEST-MODE GUARD — vitest tests install their OWN MockWebSocket per-test
// in beforeEach. The module-init trigger would open a WS using whatever
// globalThis.WebSocket was at module-load time (which may be jsdom's
// missing WebSocket OR a leftover mock from a prior test file). Either way
// the test's own WebSocket assertions get polluted. Skip auto-init in tests.
const PWS_AUTOINIT = typeof window !== 'undefined'
  && (import.meta as { env?: { MODE?: string } }).env?.MODE !== 'test';
if (PWS_AUTOINIT) {
  // Defer one microtask to let the module finish initializing before the
  // WS handshake starts — purely cosmetic for the React boot sequence.
  queueMicrotask(() => { openPersistentWS(); });
}

// 1.160.0 — initial-greeting orchestration. The greeting fires once per page-
// load when settings.initialGreetingEnabled=true. Timing gate: module-init for
// 'before-interaction' (desktop only; iOS autoplay blocks) or first pointerdown/
// keydown for 'after-interaction' (iOS-safe default). speak() resolves after
// audio.play() STARTS (not after onended) and swallows errors internally to
// status='error' — never throws. The orchestrator reads status post-speak to
// detect failure and resets to 'idle' so a greeting failure does not surface
// as a page-load error toast. autoArm fires only on success.
// WS-A Φ2 + D-A6 — per-tab greeting fire-once. Was `let _greetingFiredThisSession = false`;
// now a Set<TabId> so each tab fires once on its own first-interaction (the
// activeTabId at fire time). Pre-hydrate / no-active-tab uses sentinel key
// '__no_tab__' as the canonical "session-global" fallback.
const _greetingFiredByTab: Set<TabId> = new Set();
const _NO_TAB_KEY = '__no_tab__';

async function fireInitialGreetingIfReady(
  reason: 'module-init' | 'first-interaction',
): Promise<void> {
  const state = useVoiceStore.getState();
  // WS-A Φ2 — read active-tab settings (byTabId[active]); fall back to top-level
  // mirror (which is itself active-tab when populated). Pre-hydrate path uses
  // the top-level mirror.
  const activeTabId = state.activeTabId;
  const greetingKey = activeTabId ?? _NO_TAB_KEY;
  if (_greetingFiredByTab.has(greetingKey)) return;
  const s = activeTabId
    ? state.byTabId[activeTabId]?.settings ?? state.settings
    : state.settings;
  const status = activeTabId
    ? state.byTabId[activeTabId]?.status ?? state.status
    : state.status;
  if (!s.initialGreetingEnabled) return;
  if (status !== 'idle') return;
  if (s.initialGreetingTiming === 'before-interaction' && reason !== 'module-init') return;
  if (s.initialGreetingTiming === 'after-interaction' && reason !== 'first-interaction') return;
  const text = (s.initialGreetingText || '').trim();
  if (text.length === 0) return;
  // 2026-06-29 — boot-race guard. The greeting is an authenticated TTS call
  // (speak() → POST /api/voice/tts with the Bearer token). On a fresh load the
  // user's first tap can fire this BEFORE the OIDC callback has written the
  // token to localStorage, producing a 401 (observed live as request #7 on
  // vibe.dallas) and — because the fire-once flag below was set before the
  // await — the greeting then never spoke again all session. Skip WITHOUT
  // consuming the fire-once flag so the persistent pointerdown/keydown listener
  // (or any later trigger) re-runs this once the token is present.
  if (getStoredToken() === null) return;
  _greetingFiredByTab.add(greetingKey);
  // 1.164.2 — history is pre-populated by populateGreetingHistory inside
  // hydrate() now. This function only handles the AUDIO side: speak the
  // greeting for the first activation per tab. Removing the prior history-
  // append paths (active-tab branch and pre-hydrate else branch) avoids
  // the double-text-append that hydrate's new behavior would otherwise
  // produce. The audio gate (status === 'idle' check above) is preserved.
  await useVoiceStore.getState().speak(text);
  if (useVoiceStore.getState().status === 'error') {
    console.warn('[voiceStore] initial greeting audio failed; resetting status to idle');
    _setActiveTabFields({ status: 'idle', error: null });
    return;
  }
  if (s.initialGreetingAutoArmMic) {
    try {
      await useVoiceStore.getState().startRecording();
    } catch {
      // recorder may fail (mic permission, etc.) — leave status as-is.
    }
  }
}

// 1.160.0 — streaming-WS warm-init. Mirror of classic PWS_AUTOINIT above:
// auto-open /api/voice/turn at module init so first-click pays no handshake
// cost. Cold path (per-turn construction inside startRecording / startTurn)
// stays for failure recovery; user just doesn't see it in normal use.
//
// Also: greeting trigger registration. Persistent (not {once:true}) listeners
// so that enabling greeting in settings AFTER a first interaction still works
// — the _greetingFiredThisSession flag is the sole idempotency gate.
//
// Test-mode skip rationale identical to classic (see comment above).
const PWS_STREAMING_AUTOINIT = typeof window !== 'undefined'
  && (import.meta as { env?: { MODE?: string } }).env?.MODE !== 'test';
if (PWS_STREAMING_AUTOINIT) {
  queueMicrotask(() => {
    openPersistentStreamingWS();
    void fireInitialGreetingIfReady('module-init');
  });
  const greetingHandler = (): void => { void fireInitialGreetingIfReady('first-interaction'); };
  window.addEventListener('pointerdown', greetingHandler, { capture: true });
  window.addEventListener('keydown', greetingHandler, { capture: true });
  // V-FE-13 (Δ12) — Playwright debug hook. Reads module-private activeTurnSocket
  // (same accessor as __deltaTestHooks.getActiveTurnSocket) but window-visible
  // so page.evaluate() can poll readyState without reaching into test-only hooks.
  // Phase 9.6 (2026-05-24) — R2 C-1 Playwright hook. Injects a synthetic
  // streaming WS frame into the store's handleStreamingFrame from
  // page.evaluate() context. Production paths never call this from the SPA.
  // Scoped to non-test builds (PWS_STREAMING_AUTOINIT gate above) — vitest
  // uses __deltaTestHooks.streamingFrame instead.
  (window as Window & {
    __voicePhase96Test?: {
      injectStreamingFrame: (frame: Record<string, unknown>) => void;
    };
  }).__voicePhase96Test = {
    injectStreamingFrame: (frame: Record<string, unknown>): void => {
      handleStreamingFrame(frame);
    },
  };
  (window as Window & {
    __pwsState?: {
      getStreamingWSReadyState: () => number;
      getActiveRecorderState: () => string;
      getPlayingAudioPausedForTab: (tabId: string) => boolean | null;
    };
  }).__pwsState = {
    getStreamingWSReadyState: () => activeTurnSocket?.readyState ?? WebSocket.CLOSED,
    // WS-A.5 — MCP-Playwright probe for AC.5.5 verification.
    getActiveRecorderState: () => activeRecorder?.state ?? 'inactive',
    // WS-A.5 — MCP-Playwright probe for AC.5.3/AC.5.4 (audio paused state).
    // Returns null when no audio is registered for that tab (vs false for
    // playing, true for paused).
    getPlayingAudioPausedForTab: (tabId: string): boolean | null => {
      const el = _playingAudioByTab.get(tabId as TabId);
      return el ? el.paused : null;
    },
  };
}

// PWS — test-only export so vitest can manually trigger openPersistentWS
// AFTER installing the per-test MockWebSocket. Production callers MUST
// NOT use this; use the module-init auto-trigger.
// WS-A Φ5 — adds outputAudio inspectors so iOS-gesture-context smoke tests
// can verify D-A8 (pause-not-recreate across tab switch) without touching
// module-private state. The setter is for test fixtures only; production
// callers MUST set outputAudio exclusively via primeOutputAudioUnlock().
export const __pwsTestHooks = {
  openPersistentWS,
  teardownPersistentWS,
  // Read-only inspectors for assertions.
  getActiveWS: () => activeWS,
  getPwsInitialized: () => pwsInitialized,
  getOutputAudio: (): HTMLAudioElement | null => outputAudio,
  setOutputAudio: (el: HTMLAudioElement | null): void => { outputAudio = el; },
};

// δ.8 — test-only hooks for the voice-streaming-pipeline module state +
// teardownTurn. Production code does NOT use these.
export const __deltaTestHooks = {
  teardownTurn,
  resetTurnState: (): void => {
    activeTurnAudioContext = null;
    activeTurnSocket = null;
    activeTurnAudioQueue = null;
    // δ″ Plan R1 Concern C2 — clear new poll handle so tests don't pollute.
    if (turnLevelPollHandle) {
      clearInterval(turnLevelPollHandle);
      turnLevelPollHandle = null;
    }
    // 1.164.3 R2 — also reset currentStreamingTurnState so the next test's
    // setActiveTab() call doesn't see a leaked finalizeStarted=true that
    // would skip teardownStreamingTurnResources for the wrong reason.
    resetStreamingTurnState();
  },
  getActiveTurnAudioContext: (): AudioContext | null => activeTurnAudioContext,
  setActiveTurnAudioContext: (ctx: AudioContext | null): void => {
    activeTurnAudioContext = ctx;
  },
  getActiveTurnSocket: (): VoiceTurnSocket | null => activeTurnSocket,
  setActiveTurnSocket: (sock: VoiceTurnSocket | null): void => {
    activeTurnSocket = sock;
  },
  getActiveTurnAudioQueue: (): VoiceAudioQueue | null => activeTurnAudioQueue,
  setActiveTurnAudioQueue: (q: VoiceAudioQueue | null): void => {
    activeTurnAudioQueue = q;
  },
  // δ″ F2 — verification getter for the new poll handle (test-only).
  getTurnLevelPollHandle: (): ReturnType<typeof setInterval> | null => turnLevelPollHandle,
  // Read-only inspector for the VAD AudioContext — tests use this to
  // verify teardownTurn does NOT touch it (R3.2 naming discipline).
  getActiveAudioContextForTest: (): AudioContext | null => activeAudioContext,
  setActiveAudioContextForTest: (ctx: AudioContext | null): void => {
    activeAudioContext = ctx;
  },
  // WS-A.5 — vitest fixture inspectors for activeRecorder + per-tab audio map.
  // Mirrors the getActiveAudioContextForTest pattern; production code MUST NOT
  // use these. Required for LF.1..LF.8 truth-table tests (R1 C2 + C3 fixes).
  getActiveRecorder: (): MediaRecorder | null => activeRecorder,
  setActiveRecorder: (r: MediaRecorder | null): void => { activeRecorder = r; },
  getPlayingAudioByTab: (tabId: TabId): HTMLAudioElement | undefined =>
    _playingAudioByTab.get(tabId),
  setPlayingAudioByTab: (tabId: TabId, el: HTMLAudioElement | null): void => {
    if (el) _playingAudioByTab.set(tabId, el);
    else _playingAudioByTab.delete(tabId);
  },
  clearPlayingAudioByTab: (): void => { _playingAudioByTab.clear(); },
  // Phase 9.6 (2026-05-24) — test hooks for streaming-delta state machine.
  // Production MUST NOT use these. Required by
  // voiceStore.streaming-deltas.test.ts T1-T7 + T3b + T4b.
  // `__test_streaming_frame_handle` already exists on the store at L2734;
  // tests can use either path (this re-export OR useVoiceStore.getState()).
  streamingFrame: (frame: Record<string, unknown>): void =>
    handleStreamingFrame(frame),
  getCurrentStreamingDeltaText: (): string => currentStreamingTurnState.streamingDeltaText,
  getInToolCallMode: (): boolean => currentStreamingTurnState.inToolCallMode,
  getToolCallBuffer: (): string => currentStreamingTurnState.toolCallBuffer,
  getProtocolVersionV19_1: (): boolean => protocolVersionV19_1,
  resetProtocolVersionV19_1: (): void => { protocolVersionV19_1 = false; },
  // Phase 9.7 (2026-05-24) — same getter/reset pattern for the v19.2 flag.
  getProtocolVersionV19_2: (): boolean => protocolVersionV19_2,
  resetProtocolVersionV19_2: (): void => { protocolVersionV19_2 = false; },
  setupForActiveTab: (): void => {
    // Always re-create a fresh tab — avoids state leak between tests.
    const tabId = 'test-tab-0';
    useVoiceStore.setState({
      activeTabId: tabId,
      byTabId: { [tabId]: freshTabState() },
      streamingReplyText: '',  // also clear the top-level mirror
    });
  },
};

// 1.160.0 + WS-A Φ2 — test-only hooks for initial-greeting orchestration.
// hasFiredForTab is the new per-tab membership probe per R1.C C1. Mirror of
// __deltaTestHooks pattern. Production code MUST NOT use these.
export const __greetingTestHooks = {
  resetGreetingFired: (): void => { _greetingFiredByTab.clear(); },
  getGreetingFired: (): boolean => _greetingFiredByTab.size > 0,
  hasFiredForTab: (tabId: TabId): boolean => _greetingFiredByTab.has(tabId),
  fireInitialGreetingIfReady,
};
